import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { apiFetch } from '../../utils/apiConfig';
import { openInViewerWindow } from '../../utils/viewerWindow';
import { getAuthHeaders } from '../../utils/authUtils';
import PDFPreviewDialog from './PDFPreviewDialog';
import PDFPreviewDialogStructured from './PDFPreviewDialogStructured';
import PriorReportTextView from './PriorReportTextView';
import ReportEditorLayout from './editor/ReportEditorLayout';
import PatientDataCard from './editor/PatientDataCard';
import PriorResultsCard from './editor/PriorResultsCard';
import AISummaryCard from './editor/AISummaryCard';
import StudyInfoCard from './editor/StudyInfoCard';
import ReportStepper from './editor/ReportStepper';
import usePriorStudies from './editor/usePriorStudies';
import useReportSidebarPopup from './editor/useReportSidebarPopup';
import MultiDatabaseFieldAccordion from './editor/MultiDatabaseFieldAccordion';
import { buildTemplateSteps } from '../../utils/reportEditorUtils';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,  
  InputAdornment,
  ButtonGroup,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  Chip,
  Radio,
  RadioGroup,
  Tooltip,
  Popper,
  Menu,
  MenuList,
  ClickAwayListener,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Male,
  Female,
  TransgenderOutlined,
  PhotoLibrary
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../utils/apiConfig';
import { formatDateWithAgeSync, formatDateSync } from '../../utils/dateTimeFormat';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CommentIcon from '@mui/icons-material/Comment';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChatIcon from '@mui/icons-material/Chat';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import PaletteIcon from '@mui/icons-material/Palette';
import { Fade } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { CirclePicker, SketchPicker } from 'react-color';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import BrowserUpdatedOutlinedIcon from '@mui/icons-material/BrowserUpdatedOutlined';
import TemplateSelectDialog from './TemplateSelectDialog';
import SnippetSelector from './SnippetSelector';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';
import showPatientHistoryIcon from '../../statics/showPatientHistory-48x48.png';
import signatureIcon from '../../statics/signature.svg';
import { useSnackbar } from '../CentralSnackbar';
import { usePatientContext } from '../../contexts/PatientContext';
import sessionService from '../../utils/sessionService';

const REPORT_EDITOR_PAGE_CONTENT_ZOOM = 0.8;

const ReportEditor = () => {
  const { examId, reportId: reportIdFromUrl } = useParams(); // reportId für /report/combined/:reportId
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();
  const { currentContext, setContext, clearContext } = usePatientContext();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // ============================================================================
  // ZUSAMMENGEFASSTE BEFUNDE (MULTI-EXAM MODE)
  // ============================================================================
  const searchParams = new URLSearchParams(location.search);
  const examIdsParam = searchParams.get('examIds');
  const reportIdQueryParam = searchParams.get('reportId'); // Für Query-Parameter Variante
  // Unterstütze sowohl URL-Parameter als auch Query-Parameter
  const reportIdParam = reportIdFromUrl || reportIdQueryParam;
  // WICHTIG: Explizit zu Boolean konvertieren, damit isMultiExam true/false ist, nicht die ID!
  const isMultiExamNew = !!(location.pathname === '/report/combined' && examIdsParam && !reportIdParam);
  const isMultiExamExisting = !!(location.pathname.startsWith('/report/combined') && reportIdParam);
  const isMultiExam = isMultiExamNew || isMultiExamExisting;
  const multiExamIds = isMultiExamNew ? examIdsParam.split(',').map(id => parseInt(id, 10)) : [];
  
  // Multi-Exam spezifische States
  const [combinedExams, setCombinedExams] = useState([]); // Alle Exams für zusammengefassten Befund
  const [combinedReportId, setCombinedReportId] = useState(null); // Report ID des kombinierten Befunds
  const [needsCombinedTemplateLoad, setNeedsCombinedTemplateLoad] = useState(false); // Flag für Template-Laden bei kombinierten Befunden
  
  // Prüfe ob Addendum-Modus aktiv ist (initial aus URL, kann später dynamisch gesetzt werden)
  const [isAddendumMode, setIsAddendumMode] = useState(
    new URLSearchParams(location.search).get('addendum') === 'true'
  );
  const [loading, setLoading] = useState(true);
  const [examData, setExamData] = useState(null);
  const [studyDate, setStudyDate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [reportContent, setReportContent] = useState('');
  const [showNumpad, setShowNumpad] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [viewerConfig, setViewerConfig] = useState({
    web: { base_url: '', test_study_uid: '' },
    client: { base_url: '', test_study_uid: '' }
  });
  
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
  const [templateBrowserType, setTemplateBrowserType] = useState('F');
  const [matchingTemplates, setMatchingTemplates] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [examExaminationId, setExamExaminationId] = useState(null);
  const [activeTemplateType, setActiveTemplateType] = useState('F');
  const [initialTemplateLoaded, setInitialTemplateLoaded] = useState(false);
  
  // Addendum State
  const [allAddenda, setAllAddenda] = useState([]); // Alle Addenda (inkl. approved)
  const [addendumContent, setAddendumContent] = useState(''); // Content des aktuell editierbaren Addendums
  const [cancelAddendumDialogOpen, setCancelAddendumDialogOpen] = useState(false);
  const allowNavigationRef = useRef(false); // Flag: Navigation wurde durch eigenen Dialog bestätigt
  
  // States für normale Kommentarfunktion
  const [comments, setComments] = useState([]);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  // States für Inline-Kommentare
  const [inlineComments, setInlineComments] = useState([]);
  const [selectedText, setSelectedText] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [inlineCommentDialog, setInlineCommentDialog] = useState({
    open: false,
    text: '',
    position: null
  });
  const [newInlineComment, setNewInlineComment] = useState('');
  const [commentTooltip, setCommentTooltip] = useState({
    open: false,
    comment: null,
    anchorEl: null
  });
  
  // Ref für automatischen Fokus auf Inline Comment TextField
  const inlineCommentInputRef = useRef(null);
  
  const [sendMenuOpen, setSendMenuOpen] = useState(false);
  const [sendMenuAnchor, setSendMenuAnchor] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:25000';
  
  // Rich Text Formatting States
  const [fontSize, setFontSize] = useState(12); // Real: 12, Display: 14
  const [fontColor, setFontColor] = useState('#000000');
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [savedSelection, setSavedSelection] = useState(null);
  const [colorPickerMode, setColorPickerMode] = useState('palette'); // 'palette' oder 'spectrum'
  const [tempColor, setTempColor] = useState('#000000'); // Temporäre Farbe für Spectrum Mode
  
  // States für Voruntersuchung Bestätigungsdialog
  const [priorStudyDialog, setPriorStudyDialog] = useState({
    open: false,
    study: null
  });
  
  // Numpad-Funktionalität States
  const [focusedFindingIndex, setFocusedFindingIndex] = useState(0);
  const [dropdownStates, setDropdownStates] = useState({});
  const dropdownStatesRef = useRef({});
  const findingRefs = useRef([]);
  
  // Snippet-Funktionalität States
  const [snippetDialogOpen, setSnippetDialogOpen] = useState(false);
  const [snippetCursorPosition, setSnippetCursorPosition] = useState(null);
  const [snippetInsertionPoint, setSnippetInsertionPoint] = useState(null);
  
  // Provisional Release Dialog States
  const [provisionalReleaseDialogOpen, setProvisionalReleaseDialogOpen] = useState(false);
  const [provisionalPassword, setProvisionalPassword] = useState('');
  const [provisionalPasswordError, setProvisionalPasswordError] = useState('');
  const [provisionalActionType, setProvisionalActionType] = useState('send_and_print');
  
  // AI Summary State
  const [aiSummaryAnchor, setAiSummaryAnchor] = useState(null);
  const [aiSummaryMenuAnchor, setAiSummaryMenuAnchor] = useState(null);
  const [aiUserPrompts, setAiUserPrompts] = useState([]);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [selectedAiPrompt, setSelectedAiPrompt] = useState(null);

  // Verify Dialog States
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyPasswordError, setVerifyPasswordError] = useState('');
  const [autoSendEnabled, setAutoSendEnabled] = useState(true); // Befund versenden Toggle
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(true); // Befund drucken Toggle
  const [navigateAfterPrint, setNavigateAfterPrint] = useState(false); // Nach Druckdialog zu MyTasks navigieren
  
  // App Config State
  const [appConfig, setAppConfig] = useState({});
  
  // Template Type Switch Warning Dialog States
  const [templateTypeSwitchDialog, setTemplateTypeSwitchDialog] = useState({
    open: false,
    targetType: null
  });

  // PDF Preview Dialog States  
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewStructuredOpen, setPdfPreviewStructuredOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  const [snippetJustInserted, setSnippetJustInserted] = useState(false);
  const [existingReportId, setExistingReportId] = useState(null);
  const [currentNewReport, setCurrentNewReport] = useState(null); // ID des existierenden Reports
  const [reportStatus, setReportStatus] = useState(null); // Status des Reports (NEW, STARTED, PROVISIONALLY, AWAITING_APPROVAL, APPROVED)
  const [lastSavedState, setLastSavedState] = useState(null); // Letzter gespeicherter Zustand für Vergleich
  const [isLoadingReport, setIsLoadingReport] = useState(false); // Verhindert falsche Änderungserkennung während dem Laden
  const [initialLoadedState, setInitialLoadedState] = useState(null); // Ursprünglich geladener Zustand
  const [reportLoadedTimestamp, setReportLoadedTimestamp] = useState(null); // Timestamp wann Report geladen wurde
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  
  // Chat Scroll Ref
  const chatMessagesRef = useRef(null);
  const stepSectionRefs = useRef([]);
  
  const priorStudiesState = usePriorStudies(examData?.patient_id);

  const reportSidebarContext = useMemo(() => {
    if (!examData?.patient_id) return null;
    return {
      examId: examData?.id,
      examIds: isMultiExam ? multiExamIds : undefined,
      reportId: combinedReportId || existingReportId || reportIdParam || undefined,
      patientId: examData.patient_id,
      isCombined: isMultiExam,
      theme: theme.palette.mode,
      locale: i18n.language,
    };
  }, [
    examData,
    isMultiExam,
    multiExamIds,
    combinedReportId,
    existingReportId,
    reportIdParam,
    theme.palette.mode,
    i18n.language,
  ]);

  const {
    sidebarDetached,
    popupOpen,
    toggleDetach,
    layoutSettings,
    updateLayoutSettings,
  } = useReportSidebarPopup({
    enabled: !loading && !!examData?.patient_id,
    reportContext: reportSidebarContext,
    onPopupBlocked: () => {
      showError(
        t('report.error.popupBlocked', 'Popup-Fenster wurde blockiert. Bitte erlauben Sie Popups für diese Seite.'),
        'ERR-POPUP-001'
      );
    },
  });

  const sidebarInPopup = sidebarDetached && popupOpen;

  // Inline Comment Icon Tooltip State
  const [commentIconTooltip, setCommentIconTooltip] = useState({ open: false, anchorEl: null, text: '' });

  // ============================================================================
  // HILFSFUNKTIONEN FÜR ZUSAMMENGEFASSTE BEFUNDE
  // ============================================================================
  
  // Kombiniert unterschiedliche Werte mit ";" oder gibt einzelnen Wert zurück
  const combineUniqueValues = (exams, field) => {
    const values = [...new Set(exams.map(e => e[field]).filter(v => v))];
    return values.join('; ');
  };

  // Formatiert mehrere Daten (getrennt durch ";") korrekt
  const formatMultipleDates = (dateString) => {
    if (!dateString || dateString === '-') return '-';
    
    // Wenn es mehrere Daten gibt (getrennt durch ";"), jedes einzeln formatieren
    const dates = dateString.split(';').map(d => d.trim()).filter(d => d);
    
    if (dates.length === 0) return '-';
    
    const formattedDates = dates.map(d => {
      try {
        return formatDateSync(d);
      } catch {
        return d; // Fallback: Ursprünglicher Wert
      }
    }).filter(d => d && d !== '-' && d !== 'Invalid Date');
    
    // Unique formattierte Daten
    const uniqueFormattedDates = [...new Set(formattedDates)];
    
    return uniqueFormattedDates.length > 0 ? uniqueFormattedDates.join('; ') : '-';
  };

  // Hilfsfunktion für die Sortierung nach Datum
  const sortByDate = (a, b, dateField) => {
    const dateA = new Date(a[dateField]);
    const dateB = new Date(b[dateField]);
    return dateB - dateA; // Neueste zuerst
  };

  const templateSteps = useMemo(
    () => buildTemplateSteps(selectedTemplate, { isMultiExam, combinedExams, t }),
    [selectedTemplate, isMultiExam, combinedExams, t]
  );

  useEffect(() => {
    setActiveStepIndex(0);
  }, [selectedTemplate?.id, selectedTemplate?.template_type]);

  useEffect(() => {
    if (activeStepIndex >= templateSteps.length && templateSteps.length > 0) {
      setActiveStepIndex(0);
    }
    stepSectionRefs.current = stepSectionRefs.current.slice(0, templateSteps.length);
  }, [templateSteps.length, activeStepIndex]);

  // Hilfsfunktion: Findings nach Name gruppieren (wie Preview)
  const groupFindingsByName = (findings) => {
    const grouped = {};
    findings.forEach((finding) => {
      const name = finding.name;
      if (!grouped[name]) {
        grouped[name] = [];
      }
      grouped[name].push(finding);
    });
    return grouped;
  };

  // Hauptfunktion zum Rendern der strukturierten Inhalte (exakter Preview-Flow)
  const renderStructuredContent = (allItems) => {
    if (!Array.isArray(allItems) || allItems.length === 0) return null;

    const elements = [];
    const processedFindingIndices = new Set();

    for (let idx = 0; idx < allItems.length; idx++) {
      const item = allItems[idx];

      if (item.item_type === 'field') {
        elements.push(renderStructuredItem(item, idx, '', 0, resolveFindingIndex(item)));

      } else if (item.item_type === 'group') {
        const grouped = groupFindingsByName(item.findings || []);

        elements.push(
          <Box key={item.id || idx} sx={{ width: '100%', borderLeft: '3px solid', borderColor: 'primary.main', pl: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'light', mb: 1, textDecoration: 'underline' }}>{item.name}</Typography>
            {Object.entries(grouped).map(([findingName, groupFinds]) => {
              if (groupFinds.length === 1 && !groupFinds[0].subtitle) {
                return renderStructuredItem(groupFinds[0], idx + '-' + findingName, '', 1, resolveFindingIndex(groupFinds[0]));
              }
              return (
                <Box key={`${item.id}-${findingName}`} sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'light', mb: 1, textDecoration: 'underline', ml: 4 }}>
                    {findingName}
                  </Typography>
                  {groupFinds.map((f, i) => renderStructuredItem(f, `${idx}-${i}`, '', 2, resolveFindingIndex(f)))}
                </Box>
              );
            })}
          </Box>
        );

      } else if (item.item_type === 'finding' && !processedFindingIndices.has(idx)) {
        const sameName = [item];
        processedFindingIndices.add(idx);

        for (let j = idx + 1; j < allItems.length; j++) {
          const nxt = allItems[j];
          if (nxt.item_type === 'finding' && nxt.name === item.name && !processedFindingIndices.has(j)) {
            sameName.push(nxt);
            processedFindingIndices.add(j);
          } else if (nxt.item_type !== 'finding') {
            break;
          }
        }

        if (sameName.length === 1 && !sameName[0].subtitle) {
          elements.push(renderStructuredItem(sameName[0], idx, '', 1, resolveFindingIndex(sameName[0])));
        } else {
          elements.push(
            <Box key={`finding-${item.name}-${idx}`} sx={{ width: '100%', mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'light', mb: 1, ml: 4 }}>{item.name}</Typography>
              {sameName.map((f, i) => renderStructuredItem(f, `${idx}-${i}`, '', 2, resolveFindingIndex(f)))}
            </Box>
          );
        }
      }
    }

    return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>{elements}</Box>;
  };

  // Lade bestehenden Report via report_id (für kombinierte Befunde)
  const loadExistingReportById = async (reportId) => {
    try {
      console.log('🔍 DEBUG: loadExistingReportById GESTARTET mit reportId:', reportId);
      
      const response = await apiFetch(`/reports/${reportId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const existingReport = await response.json();
        console.log('🔍 DEBUG: Geladener existingReport =', existingReport);
        
        setReportLoadedTimestamp(Date.now());
        setReportStatus(existingReport.status);
        setIsLoadingReport(true);
        
        // Template laden falls vorhanden
        if (existingReport.template_id) {
          try {
            const templateResponse = await apiFetch(`/templates/${existingReport.template_id}`, {
              method: 'GET'
            });
            
            if (templateResponse.ok) {
              const loadedTemplate = await templateResponse.json();
              setSelectedTemplate(loadedTemplate);
              if (loadedTemplate.template_type) {
                setActiveTemplateType(loadedTemplate.template_type);
              }
            }
          } catch (templateError) {
            console.error('Error loading template:', templateError);
          }
        } else if (isMultiExam) {
          // Bei kombinierten Befunden ohne gespeichertes Template: Freitext-Template laden
          console.log('🔗 MULTI-EXAM: Kein gespeichertes Template - setze Flag für Freitext-Template-Laden');
          setNeedsCombinedTemplateLoad(true);
        }
        
        // Report-Daten wiederherstellen
        if (existingReport.report && existingReport.report.template_data) {
          const templateData = existingReport.report.template_data;
          const restoredFormValues = {};
          
          if (Array.isArray(templateData)) {
            templateData.forEach(item => {
              if (item.id && item.value !== undefined) {
                restoredFormValues[item.id] = item.value;
              }
            });
          }
          setFormValues(restoredFormValues);
        }
        
        setIsLoadingReport(false);
      }
    } catch (error) {
      console.error('Error in loadExistingReportById:', error);
      setIsLoadingReport(false);
    }
  };

  // Lade bestehenden Report falls vorhanden
  const loadExistingReport = async (examData, newReportId) => {
    try {
      if (!newReportId) {
        console.log('🔍 DEBUG: Keine newReportId - kein Report vorhanden');
        return;
      }

      
      console.log('🔍 DEBUG: loadExistingReport GESTARTET');
      console.log('🔍 DEBUG: examData.id (new_exams.id) =', examData.id);
      console.log('🔍 DEBUG: newReportId (new_reports.id) =', newReportId);
      console.log('🔍 DEBUG: Fetche reports/by-new-report/' + newReportId);
      
      const response = await apiFetch(`/reports/by-new-report/${newReportId}`, {
        method: 'GET'
      });

      console.log('🔍 DEBUG: Response Status:', response.status);

      if (response.ok) {
        const existingReport = await response.json();
        console.log('🔍 DEBUG: Geladener existingReport =', existingReport);
        console.log('🔍 DEBUG: existingReport.id =', existingReport.id);
        console.log('🔍 DEBUG: existingReport.new_exam_id =', existingReport.new_exam_id);
        console.log('🔍 DEBUG: existingReport.study_description =', existingReport.study_description);
        console.log('🔍 DEBUG: existingReport.modality =', existingReport.modality);
        console.log('🔍 DEBUG: existingReport.patient_name =', existingReport.patient_name);
        
        // ⚠️ KRITISCH: Setze diese Flags SOFORT, um Template-Laden zu verhindern
        setExistingReportId(existingReport.id);
        setReportLoadedTimestamp(Date.now());
        setReportStatus(existingReport.status);
        console.log('🛑 REPORT ERKANNT - Template/Snippet-Laden wird verhindert (existingReportId:', existingReport.id, ', reportLoadedTimestamp:', Date.now(), ')');
        
        // Setze Loading-Flag um falsche Änderungserkennung zu verhindern
        setIsLoadingReport(true);
        console.log('📊 Report Status geladen:', existingReport.status);
        
        // 🔍 PRÜFE: Gibt es Addenda? Lade alle und prüfe ob ein offenes existiert
        if (existingReport.addendum) {
          try {
            const addenda = typeof existingReport.addendum === 'string' 
              ? JSON.parse(existingReport.addendum) 
              : existingReport.addendum;
            
            if (Array.isArray(addenda) && addenda.length > 0) {
              // ✅ Speichere ALLE Addenda (für Anzeige)
              setAllAddenda(addenda);
              
              const lastAddendum = addenda[addenda.length - 1];
              // Prüfe ob das letzte Addendum noch nicht approved ist
              if (!lastAddendum.approved_by) {
                console.log('🔄 Offenes Addendum gefunden - aktiviere Addendum-Modus automatisch', {
                  addendaCount: addenda.length,
                  lastAddendumContent: lastAddendum.content?.substring(0, 50),
                  hasSignatures: lastAddendum.signers?.length > 0
                });
                
                // ✅ Aktiviere Addendum-Modus UND lade Content sofort
                setIsAddendumMode(true);
                const content = lastAddendum.content || '';
                setAddendumContent(content);
                console.log('📝 Offenes Addendum Content geladen:', {
                  addendaCount: addenda.length,
                  contentLength: content.length
                });
              } else {
                console.log('✅ Alle Addenda approved - normaler Modus', {
                  addendaCount: addenda.length,
                  approved_by: lastAddendum.approved_by
                });
              }
            }
          } catch (e) {
            console.error('❌ Fehler beim Prüfen der Addenda:', e);
          }
        }
        
        // Aktualisiere Patientenkontext mit reportId
        console.log('🔧 REPORT EDITOR: Setze reportId im PatientContext (existierender Report)');
        console.log('   existingReport.id:', existingReport.id);
        console.log('   Vorheriger Context:', currentContext);
        setContext(prev => {
          const newContext = { ...prev, reportId: existingReport.id };
          console.log('   Neuer Context:', newContext);
          return newContext;
        });
        
        // Lade Template und Daten aus gespeichertem Report
        await restoreReportFromData(existingReport);
        
            // Prior Studies werden über usePriorStudies geladen
        
      } else if (response.status === 404) {
        setIsLoadingReport(false); // Loading beendet für neue Reports
      } else {
        console.error('❌ Fehler beim Laden des bestehenden Reports:', response.status);
        setIsLoadingReport(false); // Loading beendet auch bei Fehlern
      }
    } catch (error) {
      console.error('❌ Fehler beim Prüfen auf bestehenden Report:', error);
      setIsLoadingReport(false); // Loading beendet auch bei Exceptions
    }
  };

  // Lade aktuelle NewReport für study_instance_uid
  const loadCurrentNewReport = async (examId) => {
    try {
      const response = await apiFetch(`/new-reports/by-exam/${examId}`, {
        method: 'GET'
      });
      
      if (response.ok) {
        const newReport = await response.json();
        setCurrentNewReport(newReport);
        return newReport; // Gebe newReport zurück für direkte Verwendung
      } else if (response.status === 404) {
        // Keine NewReport vorhanden - das ist normal für manche Exams
        setCurrentNewReport(null);
        return null;
      } else {
        console.error('❌ Error loading current NewReport:', response.status);
        return null;
      }
    } catch (err) {
      console.error('❌ Network error loading current NewReport:', err);
      return null;
    }
  };

  // Stelle gespeicherten Report wieder her
  const restoreReportFromData = async (existingReport) => {
    try {

      // 1. Template laden falls vorhanden
      let loadedTemplate = null;
      if (existingReport.template_id) {
        const templateResponse = await apiFetch(`/templates/${existingReport.template_id}`, {
          method: 'GET'
        });
        
        if (templateResponse.ok) {
          loadedTemplate = await templateResponse.json();
          setSelectedTemplate(loadedTemplate);
          // Setze activeTemplateType basierend auf template_type
          if (loadedTemplate.template_type) {
            setActiveTemplateType(loadedTemplate.template_type);
          }
        }
      }

      // 2. Report-Daten wiederherstellen
      if (existingReport.report && existingReport.report.template_data) {
        const templateData = existingReport.report.template_data;
        const restoredFormValues = {};
        const templateType = existingReport.report.metadata?.template_type || 'F';
        
        console.log('🔄 RESTORE - Template Type:', templateType);
        console.log('🔄 RESTORE - Template Data:', templateData);
        
        // Unterscheide zwischen Freitext und strukturierten Templates
        if (templateType === 'F') {
          // 📝 FREITEXT-TEMPLATE: Einfache Wiederherstellung
          if (Array.isArray(templateData)) {
            templateData.forEach(item => {
              if (item.id && item.value !== undefined) {
                restoredFormValues[item.id] = item.value;
              }
            });
          }
        } else {
          // 🏗️ STRUKTURIERTES TEMPLATE: REKURSIVE Wiederherstellung aller Werte (inkl. Groups und Findings)
          const restoreValuesRecursive = (items, parentKey = '') => {
            if (!Array.isArray(items)) return;
            
            items.forEach((item, idx) => {
              const itemType = item.type || item.item_type;
              const itemId = item.id || `item_${idx}`;
              // 🔥 KRITISCH: Verwende die GLEICHE keyBase-Logik wie renderStructuredItem!
              const keyBase = parentKey ? `${parentKey}.${itemId}` : itemId;
              
              if (itemType === 'field' || itemType === 'freetext' || itemType === 'database') {
                // Top-Level Field - stelle Wert wieder her
                if (item.id && item.value !== undefined) {
                  restoredFormValues[item.id] = item.value;
                }
              } else if (itemType === 'group') {
                // Group mit Findings - rekursiv wiederherstellen
                if (Array.isArray(item.findings)) {
                  item.findings.forEach((finding, findingIdx) => {
                    const findingId = finding.id || `finding_${findingIdx}`;
                    const findingKey = `${keyBase}.${findingId}`;
                    
                    console.log(`🔍 DEBUG RESTORE: Stelle Finding-Wert wieder her für Key: "${findingKey}" (findingId: "${findingId}", value: "${finding.value}", finding_type: "${finding.finding_type}")`);
                    
                    // Stelle Finding-Wert wieder her (mit vollem Key für verschachtelte Struktur)
                    if (finding.value !== undefined) {
                      let valueToRestore = finding.value;
                      
                      // 🔥 CHECKBOX-WIEDERHERSTELLUNG: String zu Array konvertieren
                      if (finding.finding_type === 'checkboxes') {
                        if (typeof valueToRestore === 'string' && valueToRestore) {
                          // Komma-getrennte Werte zu Array konvertieren
                          const checkboxArray = valueToRestore.split(',').map(v => v.trim()).filter(v => v);
                          restoredFormValues[findingKey] = checkboxArray;
                          restoredFormValues[findingId] = checkboxArray;
                        } else {
                          restoredFormValues[findingKey] = [];
                          restoredFormValues[findingId] = [];
                        }
                      } else {
                        // 🔥 DROPDOWN-VALIDIERUNG: Prüfe ob Wert in Optionen enthalten ist
                        if (finding.finding_type === 'dropdown' && Array.isArray(finding.options)) {
                          const validOptions = finding.options.filter(opt => opt && opt.trim());
                          if (valueToRestore && !validOptions.includes(valueToRestore)) {
                            console.warn(`⚠️ Dropdown-Wert "${valueToRestore}" nicht in Optionen [${validOptions.join(', ')}] gefunden. Setze auf leer.`);
                            valueToRestore = ''; // Leeren Wert setzen statt ungültigen
                          }
                        }
                        
                        restoredFormValues[findingKey] = valueToRestore;
                        restoredFormValues[findingId] = valueToRestore;
                      }
                    }
                    
                    // 🔥 KOMMENTAR-WERT für yesno_comment Findings wiederherstellen
                    if (finding.comment_value !== undefined && finding.finding_type === 'yesno_comment') {
                      const commentKey = `${findingKey}.comment`;
                      const commentIdKey = `${findingId}.comment`;
                      restoredFormValues[commentKey] = finding.comment_value;
                      restoredFormValues[commentIdKey] = finding.comment_value;
                    }
                  });
                }
              } else if (itemType === 'finding') {
                // Standalone Finding (außerhalb von Group)
                const findingKey = keyBase; // Verwende keyBase direkt
                if (item.value !== undefined) {
                  let valueToRestore = item.value;
                  
                  // 🔥 CHECKBOX-WIEDERHERSTELLUNG: String zu Array konvertieren
                  if (item.finding_type === 'checkboxes') {
                    if (typeof valueToRestore === 'string' && valueToRestore) {
                      // Komma-getrennte Werte zu Array konvertieren
                      const checkboxArray = valueToRestore.split(',').map(v => v.trim()).filter(v => v);
                      restoredFormValues[findingKey] = checkboxArray;
                      restoredFormValues[itemId] = checkboxArray;
                    } else {
                      restoredFormValues[findingKey] = [];
                      restoredFormValues[itemId] = [];
                    }
                  } else {
                    // 🔥 DROPDOWN-VALIDIERUNG: Prüfe ob Wert in Optionen enthalten ist
                    if (item.finding_type === 'dropdown' && Array.isArray(item.options)) {
                      const validOptions = item.options.filter(opt => opt && opt.trim());
                      if (valueToRestore && !validOptions.includes(valueToRestore)) {
                        console.warn(`⚠️ Standalone Dropdown-Wert "${valueToRestore}" nicht in Optionen [${validOptions.join(', ')}] gefunden. Setze auf leer.`);
                        valueToRestore = ''; // Leeren Wert setzen statt ungültigen
                      }
                    }
                    
                    restoredFormValues[findingKey] = valueToRestore;
                    restoredFormValues[itemId] = valueToRestore;
                  }
                }
                
                // 🔥 KOMMENTAR-WERT für yesno_comment Findings wiederherstellen
                if (item.comment_value !== undefined && item.finding_type === 'yesno_comment') {
                  const commentKey = `${findingKey}.comment`;
                  const commentIdKey = `${itemId}.comment`;
                  restoredFormValues[commentKey] = item.comment_value;
                  restoredFormValues[commentIdKey] = item.comment_value;
                }
              }
            });
          };
          
          restoreValuesRecursive(templateData);
        }
        
        console.log('🔥 RESTORE - Wiederhergestellte formValues:', restoredFormValues);
        
        setFormValues(restoredFormValues);
        
        // 🔥 WICHTIG: Setze isLoadingReport=false SOFORT nach dem RESTORE
        // Template-Daten wurden bereits geladen, wir brauchen nicht auf selectedTemplate zu warten
        console.log('✅ Template-Daten restored, setze isLoadingReport=false');
        setIsLoadingReport(false);
        setReportLoadedTimestamp(Date.now()); // Aktualisiere Timestamp nach vollständigem Laden
        
        // Speichere den initialen Zustand für Änderungserkennung
        setTimeout(() => {
          const loadedState = getCurrentReportState();
          setLastSavedState(loadedState);
          setInitialLoadedState(loadedState);
          console.log('💾 Initial state saved for change detection');
        }, 100);

        // Freitext-Inhalt wiederherstellen
        const freetextField = templateData.find(f => f.id === 'freetext_content');
        if (freetextField && freetextField.value) {
          setReportContent(freetextField.value);
          
          // HTML-Formatierung für Freitext wiederherstellen
          setTimeout(() => {
            const freetextElement = document.getElementById('freetext-content');
            if (freetextElement && freetextElement.contentEditable === 'true') {
              freetextElement.innerHTML = freetextField.value;
            }
          }, 300);

        }

        // HTML-Formatierungen für alle RichTextFields wiederherstellen - REKURSIV
        setTimeout(() => {
          const restoreHtmlRecursive = (items) => {
            if (!Array.isArray(items)) return;
            
            items.forEach(item => {
              const itemType = item.type || item.item_type;
              
              if (itemType === 'field' || itemType === 'freetext' || itemType === 'database' || itemType === 'finding') {
                // Field oder Finding mit HTML-Inhalt
                if (item.value && item.value.includes('<')) {
                  const semanticId = generateSemanticFieldId(item, item.id);
                  const fieldElement = document.getElementById(semanticId);
                  if (fieldElement && fieldElement.contentEditable === 'true') {
                    fieldElement.innerHTML = item.value;
                  } else {
                    console.warn(`⚠️ RichTextField nicht gefunden für ${item.id} (${semanticId})`);
                  }
                }
              } else if (itemType === 'group' && Array.isArray(item.findings)) {
                // Rekursiv durch Findings in Group
                item.findings.forEach(finding => {
                  if (finding.value && finding.value.includes('<')) {
                    const semanticId = generateSemanticFieldId(finding, finding.id);
                    const fieldElement = document.getElementById(semanticId);
                    if (fieldElement && fieldElement.contentEditable === 'true') {
                      fieldElement.innerHTML = finding.value;
                    } else {
                      console.warn(`⚠️ RichTextField nicht gefunden für Finding ${finding.id} (${semanticId})`);
                    }
                  }
                });
              }
            });
          };
          
          restoreHtmlRecursive(templateData);
        }, 500); // Kurze Verzögerung um sicherzustellen dass Template geladen ist
      }

      // 3. Kommentare wiederherstellen
      if (existingReport.comments && Array.isArray(existingReport.comments)) {
        setComments(existingReport.comments);
      }

              // 4. Inline-Kommentare wiederherstellen
        if (existingReport.inline_comments && Array.isArray(existingReport.inline_comments)) {
          setInlineComments(existingReport.inline_comments);
          
          // Warte bis State aktualisiert ist, dann rendere die Kommentare
          setTimeout(() => {
            renderAllInlineComments(existingReport.inline_comments);
          }, 800); // Warte länger um sicherzustellen dass Template und Content geladen sind
        }

      // WICHTIG: Snippets werden NICHT beim Laden eines gespeicherten Reports ersetzt!
      // Sie wurden bereits beim ersten Anwenden des Templates ersetzt.
      // Erneutes Ersetzen würde bearbeitete Inhalte überschreiben.

    } catch (error) {
      setIsLoadingReport(false); // Loading beendet auch bei Fehlern
    }
  };

  // Snippet-Ersetzung für Template-Felder
  const loadAndReplaceSnippets = async (template) => {
    if (!template || !template.fields_json) return;
    
    // Prüfe ob ein existierender Report geladen wurde ODER Addendum-Modus aktiv ist - wenn ja, keine Snippets ersetzen!
    if (existingReportId || reportLoadedTimestamp || isAddendumMode) {
      console.log('⏭️ SNIPPET-ERSETZUNG ABGEBROCHEN - Existierender Report/Addendum erkannt (existingReportId:', existingReportId, ', reportLoadedTimestamp:', reportLoadedTimestamp, ', isAddendumMode:', isAddendumMode, ')');
      return;
    }
    
    console.log('🔄 Lade und ersetze Snippets für Template:', template.name);
    
    // Sammle alle Felder mit Snippets
    const fieldsWithSnippets = [];
    const findFieldsWithSnippets = (items) => {
      if (!Array.isArray(items)) return;
      
      items.forEach(item => {
        const itemType = item.type || item.item_type;
        
        if ((itemType === 'field' || itemType === 'freetext' || itemType === 'textarea') && item.snippet && item.snippet.trim()) {
          fieldsWithSnippets.push(item);
        } else if (itemType === 'group' && Array.isArray(item.findings)) {
          findFieldsWithSnippets(item.findings);
        }
      });
    };
    
    findFieldsWithSnippets(template.fields_json);
    
    if (fieldsWithSnippets.length === 0) {
      console.log('✅ Keine Snippets zum Ersetzen gefunden');
      return;
    }
    
    console.log(`🔄 Gefundene Felder mit Snippets: ${fieldsWithSnippets.length}`);
    
    // Lade alle Snippets vom Backend
    try {
      const response = await apiFetch('/snippets', {
        method: 'GET'
      });
      
      if (!response.ok) {
        console.error('❌ Fehler beim Laden der Snippets:', response.status);
        return;
      }
      
      const allSnippets = await response.json();
      console.log('✅ Snippets vom Backend geladen:', allSnippets.length);
      
      // Erstelle ein Map für schnellen Zugriff
      const snippetMap = {};
      allSnippets.forEach(snippet => {
        // Unterstütze beide Formate: "@snippet_name" und "snippet_name"
        const key = snippet.label.startsWith('@') ? snippet.label : `@${snippet.label}`;
        snippetMap[key] = snippet.content;
        snippetMap[snippet.label] = snippet.content; // Auch ohne @
      });
      
      // Ersetze Snippets in formValues
      const updatedFormValues = {};
      fieldsWithSnippets.forEach(field => {
        const snippetKey = field.snippet.trim();
        const snippetContent = snippetMap[snippetKey] || snippetMap[`@${snippetKey}`] || snippetMap[snippetKey.replace('@', '')];
        
        if (snippetContent) {
          updatedFormValues[field.id] = snippetContent;
          console.log(`✅ Snippet "${snippetKey}" ersetzt für Feld "${field.label}" (${field.id})`);
        } else {
          console.warn(`⚠️ Snippet "${snippetKey}" nicht gefunden für Feld "${field.label}"`);
        }
      });
      
      // Aktualisiere formValues mit Snippet-Inhalten
      setFormValues(prev => ({ ...prev, ...updatedFormValues }));
      
      // Warte kurz und aktualisiere HTML-Elemente für RichTextFields
      setTimeout(() => {
        fieldsWithSnippets.forEach(field => {
          const snippetKey = field.snippet.trim();
          const snippetContent = snippetMap[snippetKey] || snippetMap[`@${snippetKey}`] || snippetMap[snippetKey.replace('@', '')];
          
          if (snippetContent) {
            const semanticId = generateSemanticFieldId(field, field.id);
            const fieldElement = document.getElementById(semanticId);
            if (fieldElement && fieldElement.contentEditable === 'true') {
              fieldElement.innerHTML = snippetContent;
              console.log(`✅ Snippet-HTML aktualisiert für Feld "${field.label}" (${semanticId})`);
            }
          }
        });
      }, 300);
      
    } catch (error) {
      console.error('❌ Fehler beim Laden und Ersetzen von Snippets:', error);
    }
  };

  // Lade Untersuchungsdaten
  useEffect(() => {
    const loadExamData = async () => {
      console.log('🔍 DEBUG: ReportEditor loadExamData GESTARTET');
      console.log('🔍 DEBUG: examId aus URL =', examId);
      console.log('🔍 DEBUG: isMultiExam =', isMultiExam, 'multiExamIds =', multiExamIds);
      
      // ============================================================================
      // MULTI-EXAM MODUS: Zusammengefasste Befunde
      // ============================================================================
      
      // Fall 1: Existierenden kombinierten Report öffnen (via reportId URL-Parameter)
      if (isMultiExamExisting && reportIdParam) {
        console.log('🔗 MULTI-EXAM EXISTING: Lade existierenden kombinierten Befund mit reportId:', reportIdParam);
        
        try {
          // Lade kombinierten Report via API
          const response = await fetch(createApiUrl(`reports/${reportIdParam}/combined-exams`), {
            headers: getAuthHeaders()
          });

          if (!response.ok) {
            showError(t('report.error.loadCombined', 'Fehler beim Laden des zusammengefassten Befunds'));
            setLoading(false);
            navigate('/my-tasks');
            return;
          }

          const combinedData = await response.json();
          console.log('🔗 MULTI-EXAM EXISTING: Kombinierter Report geladen:', combinedData);
          
          if (!combinedData.is_combined) {
            // Nicht wirklich ein kombinierter Report - navigiere zu normalem Editor
            console.log('🔍 Report ist nicht kombiniert - navigiere zu normalem Editor');
            navigate(`/report/${combinedData.exams[0]?.new_exam_id}`);
            return;
          }
          
          setCombinedReportId(combinedData.report_id);
          setCombinedExams(combinedData.exams);
          
          // Setze examData auf das erste Exam (als primäres)
          const primaryExam = combinedData.exams[0];
          const examDataMerged = {
            id: primaryExam.new_exam_id,
            patient_id: primaryExam.patient_id,
            patient_name: primaryExam.patient_name,
            birth_date: primaryExam.birth_date,
            sex: primaryExam.sex,
            insurance_status: primaryExam.insurance_status,
            emergency_status: primaryExam.emergency_status,
            modality: combineUniqueValues(combinedData.exams, 'modality'),
            referring_physician: combineUniqueValues(combinedData.exams, 'referring_physician'),
            admission_id: combineUniqueValues(combinedData.exams, 'admission_id'),
            study_date: combineUniqueValues(combinedData.exams, 'study_date'),
            study_description: combinedData.exams.map(e => e.study_description).join('; '),
            clinical_info: primaryExam.clinical_info,
            question: primaryExam.question,
            accession_number: combineUniqueValues(combinedData.exams, 'accession_number'),
            comment: primaryExam.comment,
            scan_protocol: primaryExam.scan_protocol,
            _isCombined: true,
            _combinedExams: combinedData.exams,
            _new_report_ids: combinedData.new_report_ids
          };
          
          setExamData(examDataMerged);
          setExistingReportId(combinedData.report_id);
          
          // CurrentNewReport für kombinierten Befund setzen (primärer NewReport)
          // Wird für Speichern benötigt
          setCurrentNewReport({
            id: primaryExam.new_report_id,
            new_exam_id: primaryExam.new_exam_id,
            accession_number: primaryExam.accession_number,
            study_instance_uid: primaryExam.study_instance_uid
          });
          
          // Studiendatum für kombinierten Befund setzen
          setStudyDate(examDataMerged.study_date);
          
          // Report-Daten laden
          await loadExistingReportById(combinedData.report_id);
          
          // Patientenkontext setzen
          setContext({
            type: 'report',
            examId: primaryExam.new_exam_id,
            patientName: primaryExam.patient_name || 'Unbekannt',
            patientId: primaryExam.patient_id,
            modality: examDataMerged.modality
          });
          
          // Voruntersuchungen laden
          if (primaryExam.patient_id) {
          }
          
          setLoading(false);
          return;
        } catch (err) {
          console.error('🔗 MULTI-EXAM EXISTING: Fehler beim Laden des kombinierten Befunds:', err);
          showError(t('report.error.loadCombined', 'Fehler beim Laden des zusammengefassten Befunds'));
          setLoading(false);
          navigate('/my-tasks');
          return;
        }
      }
      
      // Fall 2: Neuen kombinierten Report erstellen (via examIds URL-Parameter)
      if (isMultiExamNew && multiExamIds.length >= 2) {
        console.log('🔗 MULTI-EXAM NEW: Lade zusammengefassten Befund für Exams:', multiExamIds);
        
        try {
          // Schritt 1: Backend aufrufen um kombinierten Report zu erstellen
          const user = sessionService.getCurrentUser();
          const createResponse = await fetch(createApiUrl('reports/combined'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            body: JSON.stringify({
              new_exam_ids: multiExamIds,
              author_id: user?.id,
              template_id: null
            })
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.json();
            showError(errorData.detail || t('report.error.createCombined', 'Fehler beim Erstellen des zusammengefassten Befunds'));
            setLoading(false);
            navigate('/my-tasks');
            return;
          }

          const combinedData = await createResponse.json();
          console.log('🔗 MULTI-EXAM: Kombinierter Report erstellt:', combinedData);
          
          setCombinedReportId(combinedData.report_id);
          setCombinedExams(combinedData.exams);
          
          // Setze examData auf das erste Exam (als primäres)
          const primaryExam = combinedData.exams[0];
          const examDataMerged = {
            id: primaryExam.new_exam_id,
            patient_id: primaryExam.patient_id,
            patient_name: primaryExam.patient_name,
            birth_date: primaryExam.birth_date,
            sex: primaryExam.sex,
            insurance_status: primaryExam.insurance_status,
            emergency_status: primaryExam.emergency_status,
            // Für kombinierte Felder: Wenn unterschiedlich, mit ";" verbinden
            modality: combineUniqueValues(combinedData.exams, 'modality'),
            referring_physician: combineUniqueValues(combinedData.exams, 'referring_physician'),
            admission_id: combineUniqueValues(combinedData.exams, 'admission_id'),
            study_date: combineUniqueValues(combinedData.exams, 'study_date'),
            study_description: combinedData.exams.map(e => e.study_description).join('; '),
            clinical_info: primaryExam.clinical_info,
            question: primaryExam.question,
            accession_number: combineUniqueValues(combinedData.exams, 'accession_number'),
            comment: primaryExam.comment,
            scan_protocol: primaryExam.scan_protocol,
            // Zusatzinfo für Multi-Exam
            _isCombined: true,
            _combinedExams: combinedData.exams,
            _new_report_ids: combinedData.new_report_ids
          };
          
          setExamData(examDataMerged);
          setExistingReportId(combinedData.report_id);
          setReportStatus('STARTED');
          
          // CurrentNewReport für kombinierten Befund setzen (primärer NewReport)
          // Wird für Speichern benötigt
          setCurrentNewReport({
            id: primaryExam.new_report_id,
            new_exam_id: primaryExam.new_exam_id,
            accession_number: primaryExam.accession_number,
            study_instance_uid: primaryExam.study_instance_uid
          });
          
          // Studiendatum für kombinierten Befund setzen
          setStudyDate(examDataMerged.study_date);
          
          // Template-Laden für kombinierten Befund triggern
          setNeedsCombinedTemplateLoad(true);
          
          // Patientenkontext setzen
          setContext({
            type: 'report',
            examId: primaryExam.new_exam_id,
            patientName: primaryExam.patient_name || 'Unbekannt',
            patientId: primaryExam.patient_id,
            modality: examDataMerged.modality
          });
          
          // Voruntersuchungen laden
          if (primaryExam.patient_id) {
          }
          
          setLoading(false);
          return; // Beende hier für Multi-Exam Modus
        } catch (err) {
          console.error('🔗 MULTI-EXAM: Fehler beim Erstellen des kombinierten Befunds:', err);
          showError(t('report.error.createCombined', 'Fehler beim Erstellen des zusammengefassten Befunds'));
          setLoading(false);
          navigate('/my-tasks');
          return;
        }
      }
      
      // ============================================================================
      // STANDARD MODUS: Einzelner Befund
      // ============================================================================
      try {
        console.log('🔍 DEBUG: Fetche new-exams/' + examId);
        const response = await fetch(createApiUrl(`new-exams/${examId}`), {
          headers: getAuthHeaders()
        });
        
        console.log('🔍 DEBUG: Response Status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('🔍 DEBUG: Geladene Exam-Daten =', data);
          console.log('🔍 DEBUG: data.id =', data.id);
          console.log('🔍 DEBUG: data.accession_number =', data.accession_number);
          console.log('🔍 DEBUG: data.study_description =', data.study_description);
          console.log('🔍 DEBUG: data.modality =', data.modality);
          console.log('🔍 DEBUG: data.patient_name =', data.patient_name);
          
          // ⚠️ KRITISCH: Prüfe ZUERST ob ein Report existiert, BEVOR examData gesetzt wird
          // Dadurch verhindern wir, dass der useEffect das Template lädt
          console.log('🔍 DEBUG: Lade zuerst currentNewReport für new_exams.id =', data.id);
          const newReport = await loadCurrentNewReport(data.id);
          console.log('🔍 DEBUG: newReport geladen:', newReport);
          
          // Wenn ein Report existiert, setze die Flags SOFORT
          if (newReport && newReport.id) {
            // Prüfe ob ein gespeicherter Report existiert
            try {
              const reportCheckResponse = await apiFetch(`/reports/by-new-report/${newReport.id}`, {
                method: 'GET'
              });
              
              if (reportCheckResponse.ok) {
                const existingReport = await reportCheckResponse.json();
                console.log('🛑 REPORT GEFUNDEN - Setze Flags VOR setExamData!');
                setExistingReportId(existingReport.id);
                setReportLoadedTimestamp(Date.now());
                setReportStatus(existingReport.status);
              }
            } catch (err) {
              console.log('Keine vorhandenen Reports - normaler neuer Report');
            }
          }
          
          // JETZT erst examData setzen - Flags sind bereits gesetzt
          setExamData(data);
          
          // Setze Patientenkontext für Chat (wird später mit reportId erweitert)
          setContext({
            type: 'report',
            examId: data.id,
            patientName: data.patient_name || 'Unbekannt',
            patientId: data.patient_id,
            studyDate: data.study_date ? data.study_date : '',
            modality: data.modality || ''
          });
          
          // Jetzt lade den vollständigen Report
          if (newReport && newReport.id) {
            console.log('🔍 DEBUG: Rufe loadExistingReport auf mit newReport.id =', newReport.id);
            await loadExistingReport(data, newReport.id);
          } else {
            console.log('🔍 DEBUG: Keine NewReport gefunden - kein gespeicherter Report');
          }
          
          // Lade Voruntersuchungen wenn Patient ID vorhanden
          if (data.patient_id) {
          }
          
          // Lade StudyDate aus PACS wenn Accession Number vorhanden
          if (data.accession_number) {
            try {
              const studyDateResponse = await fetch(createApiUrl(`new-exams/study-date/${data.accession_number}`), {
                headers: getAuthHeaders()
              });
              
              if (studyDateResponse.ok) {
                const studyDateData = await studyDateResponse.json();
                setStudyDate(studyDateData.study_date);
              }
            } catch (err) {
              console.error('Error loading study date:', err);
            }
          }
        } else {
          console.error('🔍 DEBUG: Fehler beim Laden der Exam-Daten:', response.status);
          showError(t('report.error.loadExam'), 'ERR-REPORT-003');
          setLoading(false);
        }
      } catch (err) {
        console.error('🔍 DEBUG: Exception beim Laden der Exam-Daten:', err);
        showError(t('report.error.network'), 'ERR-REPORT-011');
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    loadExamData();
  }, [examId, t, showError]);

  // Lade App-Konfiguration
  useEffect(() => {
    console.log('🔐 REPORT EDITOR: Lade App-Konfiguration...');
    const config = sessionService.getConfig();
    console.log('🔐 REPORT EDITOR: Config geladen:', config);
    console.log('🔐 REPORT EDITOR: password_for_report_approval =', config.password_for_report_approval);
    setAppConfig(config);
  }, []);

  // Report Lock: Öffne Report beim Laden und schließe beim Verlassen
  useEffect(() => {
    let reportIdToLock = null;

    const openReport = async () => {
      // Warte bis existingReportId gesetzt ist
      if (!existingReportId) return;
      
      reportIdToLock = existingReportId;
      
      try {
        console.log(`[LOCK] Öffne Report ${reportIdToLock}`);
        const response = await fetch(createApiUrl(`reports/${reportIdToLock}/open`), {
          method: 'POST',
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('[LOCK] Report erfolgreich geöffnet:', result);
        } else if (response.status === 409) {
          // Report bereits von jemand anderem geöffnet
          const error = await response.json();
          showWarning(error.detail || 'Report wird bereits von einem anderen Benutzer bearbeitet');
        }
      } catch (error) {
        console.error('[LOCK] Fehler beim Öffnen des Reports:', error);
      }
    };

    const closeReport = async () => {
      if (!reportIdToLock) return;
      
      try {
        console.log(`[LOCK] Schließe Report ${reportIdToLock}`);
        await fetch(createApiUrl(`reports/${reportIdToLock}/close`), {
          method: 'POST',
          headers: getAuthHeaders()
        });
        console.log('[LOCK] Report erfolgreich geschlossen');
      } catch (error) {
        console.error('[LOCK] Fehler beim Schließen des Reports:', error);
      }
    };

    // Öffne Report wenn existingReportId verfügbar ist
    if (existingReportId) {
      openReport();
    }

    // Cleanup: Schließe Report beim Unmount
    return () => {
      if (reportIdToLock) {
        closeReport();
      }
    };
  }, [existingReportId, showWarning]);

  // beforeunload: Schließe Report wenn Browser/Tab geschlossen wird
  useEffect(() => {
    const handleBeforeUnload = async (e) => {
      if (existingReportId) {
        // Synchroner API-Call mit sendBeacon (funktioniert auch wenn Seite geschlossen wird)
        const url = createApiUrl(`reports/${existingReportId}/close`);
        const headers = getAuthHeaders();
        
        // sendBeacon unterstützt keine Custom Headers, daher nutzen wir fetch mit keepalive
        fetch(url, {
          method: 'POST',
          headers,
          keepalive: true // Wichtig: Request wird auch nach Page-Unload abgeschlossen
        }).catch(err => console.error('[LOCK] Fehler beim Schließen (beforeunload):', err));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [existingReportId]);

  // Lade User für Send-Funktionalität
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: getAuthHeaders()
      });
      
      // Filtere aktive Benutzer mit group_level 1-5 (Assistenzarzt bis Chefarzt)
      const activeUsers = response.data.filter(user => 
        user.is_active && 
        user.group_level >= 1 && 
        user.group_level <= 5
      );
      setUsers(activeUsers);
      setFilteredUsers(activeUsers);
    } catch (error) {
      console.error('Fehler beim Laden der User:', error);
      showError(t('reportEditor.error.loadUsers'), 'ERR-REPORT-012');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Load AI user prompts
  useEffect(() => {
    const loadAiPrompts = async () => {
      try {
        const response = await fetch(createApiUrl('profile/ai-settings'), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setAiUserPrompts(data.ai_settings?.prompts || []);
        }
      } catch (e) {
        console.error('Error loading AI prompts:', e);
      }
    };
    loadAiPrompts();
  }, []);

  const handleAiSummaryMenuOpen = (event) => {
    setAiSummaryMenuAnchor(event.currentTarget);
  };

  const handleAiSummaryMenuClose = () => {
    setAiSummaryMenuAnchor(null);
  };

  const handleAiSummarySelect = (prompt) => {
    setAiSummaryMenuAnchor(null);
    setSelectedAiPrompt(prompt);
    setAiSummaryOpen(true);
  };

  // Filter User basierend auf Suchbegriff
  useEffect(() => {
    if (userSearchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.username.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.full_name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [userSearchTerm, users]);

  // Handle Send to User
  const handleSendToUser = async (user) => {
    try {
      setSendMenuOpen(false);
      setUserSearchTerm(''); // Reset search
      
      if (!existingReportId) {
        console.error('Kein aktueller Report zum Versenden');
        showError(t('reportEditor.send.error.noCurrentReport'), 'ERR-REPORT-013');
        return;
      }

      // 1. Zuerst automatisch speichern (stumm)
      console.log('Speichere Report vor dem Versenden...');
      await handleAutoSave();
      
      // 2. API-Call um Report zu versenden
      const userDisplayName = [user.academic_degree, user.first_name, user.last_name]
        .filter(Boolean)
        .join(' ') || user.username;
      
      console.log('Sende Report an User:', {
        reportId: existingReportId,
        userId: user.id,
        userName: userDisplayName
      });
      
      const response = await axios.post(
        `${API_BASE_URL}/reports/${existingReportId}/send/${user.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data) {
        console.log('Report erfolgreich gesendet:', response.data);
        
        // 3. Sofort zu My Tasks navigieren mit Erfolgsmeldung als URL-Parameter
        const successMessage = encodeURIComponent(`${t('reportEditor.send.success')} ${userDisplayName}`);
        navigate(`/my-tasks?success=${successMessage}`);
      }
      
    } catch (error) {
      console.error('Fehler beim Versenden des Reports:', error);
      
      let errorMessage = t('reportEditor.send.error.general');
      if (error.response?.status === 404) {
        errorMessage = t('reportEditor.send.error.notFound');
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || t('reportEditor.send.error.badRequest');
      }
      
      // Zeige Fehler
      showError(errorMessage, 'ERR-REPORT-014');
    }
  };

  // PDF Preview öffnen (Live-System) - mit Template-Typ-Routing
  const openPdfPreview = (pdfData) => {
    console.log('📄 Öffne Live PDF-Vorschau:', pdfData);
    
    // Template-Typ ermitteln
    const templateType = pdfData?.report?.report?.metadata?.template_type || 'F';
    console.log('🔍 Template-Typ für PDF-Preview:', templateType);
    
    setPdfData(pdfData);
    
    if (templateType === 'S') {
      // Strukturierter Befund
      console.log('📋 Öffne strukturierten PDF-Dialog');
      setPdfPreviewStructuredOpen(true);
    } else {
      // Freitext-Befund (Standard)
      console.log('📝 Öffne Freitext PDF-Dialog');
      setPdfPreviewOpen(true);
    }
  };

  // Hilfsfunktion: Entferne alle Bildbearbeitungs-Elemente vor PDF-Generierung
  const cleanupImageHandles = () => {
    // Entferne alle Resize-Handles
    const handles = document.querySelectorAll('.image-resize-handle');
    handles.forEach(handle => handle.remove());
    
    // Entferne alle Selection-Klassen
    const selectedImages = document.querySelectorAll('.selected-image');
    selectedImages.forEach(img => img.classList.remove('selected-image'));
    
    // Entferne resizing-Klasse
    const resizingImages = document.querySelectorAll('.image-resizing');
    resizingImages.forEach(img => img.classList.remove('image-resizing'));
  };

  // PDF für aktuellen Report generieren und DIREKT HERUNTERLADEN (ohne Preview)
  const handlePrintReport = async () => {
    console.log('📥 DOWNLOAD BUTTON GEKLICKT! Starte direkten PDF-Download...');
    console.log('🔍 existingReportId:', existingReportId);
    console.log('🔍 examData:', examData);
    console.log('🔍 navigateAfterPrint Flag:', navigateAfterPrint);
    
    if (!existingReportId) {
      console.error('❌ Kein Report zum Herunterladen verfügbar');
      showError('Kein Report zum Herunterladen verfügbar. Bitte speichern Sie zuerst den Befund.', 'ERR-REPORT-015');
      return;
    }

    try {
      // Entferne alle Bildbearbeitungs-Elemente
      cleanupImageHandles();
      
      console.log('💾 Speichere Report vor dem Download (stumm)...');
      await handleAutoSave();

      console.log('🔍 Lade PDF-Daten für Report:', existingReportId);
      
      // PDF-Daten vom Backend laden
      const response = await apiFetch(`/reports/${existingReportId}/pdf`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ PDF-Daten erhalten:', result);
        
        // Öffne PDF-Vorschau und markiere für Auto-Download
        if (result.pdf_data) {
          result.pdf_data.autoDownload = true; // Flag für Auto-Download
          console.log('📄 Öffne PDF-Vorschau mit autoDownload=true');
          openPdfPreview(result.pdf_data);
        } else {
          throw new Error('Keine PDF-Daten vom Backend erhalten');
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unbekannter Fehler' }));
        throw new Error(errorData.detail || 'Fehler beim Laden der PDF-Daten');
      }

    } catch (error) {
      console.error('❌ Fehler beim Laden der PDF-Daten:', error);
      showError('Fehler beim PDF-Download: ' + error.message, 'ERR-REPORT-015');
    }
  };

  const handleSignReport = async () => {
    if (!currentUser || !existingReportId) {
      showError(t('reportEditor.error.signatureRequiresLogin'), 'ERR-REPORT-020');
      return;
    }

    try {
      // Erst speichern (stumm)
      console.log('Speichere Report vor dem Signieren...');
      const saveResult = await handleAutoSave();
      
      if (!saveResult.success) {
        showError(t('reportEditor.error.saveBeforeSign'), 'ERR-REPORT-021');
        return;
      }

      // Im Addendum-Modus: Addendum signieren; sonst Report signieren
      const endpoint = isAddendumMode 
        ? `reports/${existingReportId}/addendum/sign`
        : `reports/${existingReportId}/sign?user_id=${currentUser.id}`;
      
      console.log(`📝 Signieren: ${isAddendumMode ? 'Addendum' : 'Report'}, Endpoint: ${endpoint}`);

      const response = await fetch(
        createApiUrl(endpoint),
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Sign failed:', response.status, errorText);
        throw new Error(`Failed to sign: ${response.status}`);
      }

      const data = await response.json();
      
      // Erfolgsmeldung
      if (isAddendumMode) {
        showSuccess(t('reportEditor.success.addendumSigned', 'Addendum erfolgreich signiert'));
      } else {
        showSuccess(t('reportEditor.success.reportSigned'));
      }
      
      console.log(`✅ ${isAddendumMode ? 'Addendum' : 'Report'} erfolgreich signiert`, data);

    } catch (err) {
      console.error('Error signing:', err);
      if (isAddendumMode) {
        showError(t('reportEditor.error.addendumSignatureFailed', 'Fehler beim Signieren des Addendums'), 'ERR-ADDENDUM-001');
      } else {
        showError(t('reportEditor.error.signatureFailed'), 'ERR-REPORT-022');
      }
    }
  };

  const handleVerifyReport = async () => {
    if (!currentUser) {
      showError(t('reportEditor.error.verifyRequiresLogin'), 'ERR-REPORT-030');
      return;
    }

    if (!currentUser.can_verify) {
      showError(t('reportEditor.error.verifyNotAuthorized'), 'ERR-REPORT-031');
      return;
    }

    // Im Addendum-Modus: Passwort-Validierung nur für Report-Freigabe nötig
    // Addendum-Freigabe erfolgt ohne Passwort (bereits verifizierter Befund)
    // ODER: Passwort-Validierung überspringen wenn password_for_report_approval = false
    console.log('🔐 VERIFY REPORT: isAddendumMode =', isAddendumMode);
    console.log('🔐 VERIFY REPORT: appConfig =', appConfig);
    console.log('🔐 VERIFY REPORT: appConfig.password_for_report_approval =', appConfig.password_for_report_approval);
    
    const requiresPassword = !isAddendumMode && appConfig.password_for_report_approval !== false;
    console.log('🔐 VERIFY REPORT: requiresPassword =', requiresPassword);
    
    if (requiresPassword) {
      if (!verifyPassword || verifyPassword.trim().length === 0) {
        setVerifyPasswordError(t('reportEditor.verify.passwordRequired'));
        return;
      }
    }

    try {
      // Erst speichern (stumm)
      console.log('Speichere Report vor dem Verifizieren...');
      const saveResult = await handleAutoSave();
      
      if (!saveResult.success) {
        showError(t('reportEditor.error.saveBeforeVerify'), 'ERR-REPORT-032');
        return;
      }

      // Die reportId aus dem Speichern-Ergebnis verwenden (für neue Reports)
      const reportId = saveResult.reportId || existingReportId;
      
      if (!reportId) {
        showError('Fehler: Report konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.', 'ERR-REPORT-033');
        return;
      }

      // Dann automatisch signieren (stumm)
      console.log('Signiere Report automatisch vor dem Verifizieren...');
      try {
        const signEndpoint = isAddendumMode 
          ? `reports/${reportId}/addendum/sign`
          : `reports/${reportId}/sign?user_id=${currentUser.id}`;
        
        const signResponse = await fetch(
          createApiUrl(signEndpoint),
          {
            method: 'POST',
            headers: getAuthHeaders()
          }
        );

        if (!signResponse.ok) {
          console.error('Auto-Sign failed:', signResponse.status);
          // Wir zeigen hier keinen Fehler an, da dies ein automatischer Schritt ist
          // Falls das Signieren fehlschlägt, wird trotzdem die Freigabe versucht
        } else {
          console.log(`📝 Automatisches Signieren erfolgreich: ${isAddendumMode ? 'Addendum' : 'Report'}`);
        }
      } catch (signError) {
        console.error('Auto-Sign error:', signError);
        // Wir brechen hier nicht ab, sondern versuchen die Freigabe trotzdem
      }

      // Im Addendum-Modus: Addendum freigeben; sonst Report freigeben
      const endpoint = isAddendumMode
        ? `reports/${reportId}/addendum/approve`
        : `reports/${reportId}/verify?user_id=${currentUser.id}`;
      
      console.log(`🔐 Freigeben: ${isAddendumMode ? 'Addendum' : 'Report'}, Endpoint: ${endpoint}`);

      // Passwort nur senden wenn erforderlich
      const requestBody = (isAddendumMode || appConfig.password_for_report_approval === false) 
        ? {} 
        : { password: verifyPassword };

      const response = await fetch(
        createApiUrl(endpoint),
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Verify/Approve failed:', response.status, errorText);
        
        if (response.status === 401 || response.status === 403) {
          if (!isAddendumMode) {
            setVerifyPasswordError(t('reportEditor.verify.incorrectPassword'));
            return;
          }
        }
        
        throw new Error(`Failed to verify/approve: ${response.status}`);
      }

      const data = await response.json();
      
      // Dialog schließen und Passwort zurücksetzen
      setVerifyDialogOpen(false);
      setVerifyPassword('');
      setVerifyPasswordError('');
      
      // Status auf APPROVED setzen
      setReportStatus('APPROVED');
      console.log('✅ Status auf APPROVED gesetzt');
      
      // Erfolgsmeldung
      if (isAddendumMode) {
        showSuccess(t('reportEditor.success.addendumApproved', 'Addendum erfolgreich freigegeben'));
        console.log('✅ Addendum freigegeben:', data);
      } else {
        console.log('✅ Report verifiziert:', data);
      }

      // Autosend-Funktionalität: DICOM PDF und HL7 senden
      let sendResults = { success: [], errors: [] };
      
      if (autoSendEnabled) {
        try {
          console.log('🚀 Starte Autosend: DICOM PDF und HL7...');
          
          // DICOM PDF senden
          try {
            const dicomResponse = await apiFetch(`/reports/${existingReportId}/dicom-resend`, {
              method: 'POST',
              headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                language: 'de',
                seriesDescription: 'Report PDF'
              })
            });
            
            if (dicomResponse.ok) {
              const dicomResult = await dicomResponse.json();
              console.log('✅ DICOM PDF erfolgreich gesendet:', dicomResult);
              sendResults.success.push('DICOM PDF');
            } else {
              console.error('❌ DICOM PDF Fehler:', dicomResponse.status);
              const errorData = await dicomResponse.json().catch(() => ({}));
              sendResults.errors.push(`DICOM PDF: ${errorData.detail || 'Unbekannter Fehler'}`);
            }
          } catch (dicomError) {
            console.error('❌ DICOM PDF Exception:', dicomError);
            sendResults.errors.push(`DICOM PDF: ${dicomError.message}`);
          }

          // HL7 senden (als "final" Status)
          try {
            const hl7Response = await apiFetch(`/reports/${existingReportId}/resend-hl7`, {
              method: 'POST',
              headers: getAuthHeaders()
            });
            
            if (hl7Response.ok) {
              const hl7Result = await hl7Response.json();
              console.log('✅ HL7 erfolgreich gesendet:', hl7Result);
              sendResults.success.push('HL7');
            } else {
              console.error('❌ HL7 Fehler:', hl7Response.status);
              const errorData = await hl7Response.json().catch(() => ({}));
              sendResults.errors.push(`HL7: ${errorData.detail || 'Unbekannter Fehler'}`);
            }
          } catch (hl7Error) {
            console.error('❌ HL7 Exception:', hl7Error);
            sendResults.errors.push(`HL7: ${hl7Error.message}`);
          }
          
        } catch (sendError) {
          console.error('❌ Fehler beim Autosend:', sendError);
          sendResults.errors.push(`Allgemeiner Fehler: ${sendError.message}`);
        }
      }

      // Zusammenfassende Snackbar-Nachrichten
      if (sendResults.success.length > 0 || sendResults.errors.length === 0) {
        if (sendResults.success.length > 0) {
          const successMessage = `Freigabe erfolgreich. Versandt: ${sendResults.success.join(', ')}`;
          showSuccess(successMessage);
        } else {
          showSuccess('Befund erfolgreich freigegeben');
        }
      }
      
      if (sendResults.errors.length > 0) {
        const errorMessage = `Versandfehler: ${sendResults.errors.join('; ')}`;
        showError(errorMessage);
      }

      // Viewer schließen (leere UID senden)
      console.log('🔒 Schließe Viewer nach Verifizierung...');
      sendEmptyViewerRequest();

      // Auto-Druckdialog öffnen
      if (autoPrintEnabled) {
        console.log('🖨️ Öffne Druckdialog nach Verifizierung...');
        setNavigateAfterPrint(true); // Flag setzen für Navigation nach Druckdialog
        setTimeout(() => {
          console.log('🖨️ Timeout erreicht - rufe handlePrintReport auf...');
          handlePrintReport();
        }, 1000); // Kurze Verzögerung für bessere UX
      } else {
        // Wenn kein Drucken gewünscht ist, direkt zu MyTasks navigieren
        setTimeout(() => {
          navigate('/my-tasks');
        }, 100);
      }

    } catch (err) {
      console.error('Error verifying report:', err);
      showError(t('reportEditor.error.verifyFailed'), 'ERR-REPORT-033');
    }
  };

  // Automatischer Fokus auf Inline Comment TextField beim Öffnen des Dialogs  
  useEffect(() => {
    if (inlineCommentDialog.open) {
      console.log('🔄 useEffect: Dialog opened, attempting focus...', {
        dialogOpen: inlineCommentDialog.open,
        refAvailable: !!inlineCommentInputRef.current
      });
      
      // Mehrere Versuche mit unterschiedlichen Verzögerungen für bessere Kompatibilität
      const timer1 = setTimeout(() => {
        if (inlineCommentInputRef.current) {
          console.log('🎯 Timer 1 (50ms): Setting focus...');
          inlineCommentInputRef.current.focus();
        }
      }, 50);
      
      const timer2 = setTimeout(() => {
        if (inlineCommentInputRef.current) {
          console.log('🎯 Timer 2 (200ms): Setting focus...');
          inlineCommentInputRef.current.focus();
        }
      }, 200);
      
      const timer3 = setTimeout(() => {
        if (inlineCommentInputRef.current) {
          console.log('🎯 Timer 3 (400ms): Setting focus...');
          inlineCommentInputRef.current.focus();
        }
      }, 400);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [inlineCommentDialog.open]);



  // Wenn examData vorhanden, versuche Examination-ID nachzuladen
  useEffect(() => {
    if (!examData || !examData.study_description) return;

    const fetchExaminationId = async () => {
      try {
        const searchParam = encodeURIComponent(examData.study_description);
        const resp = await fetch(createApiUrl(`examinations/search?search=${searchParam}`), {
          headers: getAuthHeaders()
        });
        if (resp.ok) {
          const data = await resp.json();
          if (Array.isArray(data) && data.length) {
            console.log(`🔍 Examination ID für "${examData.study_description}" gefunden: ${data[0].id} (${data[0].name})`);
            setExamExaminationId(data[0].id);
          } else {
            console.log(`⚠️ Keine Examination ID für "${examData.study_description}" gefunden`);
          }
        } else {
          console.error(`❌ Fehler beim Laden der Examination ID: ${resp.status}`);
        }
      } catch (err) {
        console.error('Error fetching examination id', err);
      }
    };
    fetchExaminationId();
  }, [examData]);

  useEffect(() => {
    fetchCurrentUser();
    fetchTemplates();
    
    // DEAKTIVIERT: beforeunload Handler verursacht Auth-Probleme
    // (wird jetzt über AppBarComponent gesteuert)
    
    return () => {
      // Keine automatischen Viewer-Clear Requests beim Unmount
      // Clear patient context when leaving ReportEditor
      clearContext();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll chat to bottom when expanded
  useEffect(() => {
    if (commentsExpanded && chatMessagesRef.current) {
      // Wait for the chat container to be fully expanded
      const timer = setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 100); // Small delay to ensure the animation has started
      
      return () => clearTimeout(timer);
    }
  }, [commentsExpanded]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(createApiUrl('auth/me'), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        // Nach dem Laden des Benutzers die Viewer-Konfiguration laden
        await loadViewerConfig();
      } else {
        showError(t('reportEditor.errors.loadingUserData'), 'ERR-REPORT-016');
        setLoading(false);
      }
    } catch (err) {
      showError(t('reportEditor.errors.networkUserData'), 'ERR-REPORT-017');
      setLoading(false);
    }
  };

  const loadViewerConfig = async () => {
    try {
      const response = await fetch(createApiUrl('profile/viewer-config'), {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setViewerConfig(data.viewer_config);
      } else {
        console.error('Failed to load viewer config:', data.message);
      }
    } catch (error) {
      console.error('Error loading viewer config:', error);
    }
  };


  // Lade Voruntersuchungen – erfolgt über usePriorStudies bei patient_id

  const openStudyInViewer = (studyInstanceUID) => {

    
    if (!studyInstanceUID) {
      console.warn('No study instance UID provided');
      return;
    }

    if (!currentUser || !currentUser.viewer_preference) {
      console.warn('User viewer preference not available:', { currentUser: !!currentUser, viewer_preference: currentUser?.viewer_preference });
      return;
    }

    const userViewerType = currentUser.viewer_preference;
    const viewerSetting = viewerConfig[userViewerType];



    if (!viewerSetting) {
      console.warn(`Viewer setting for ${userViewerType} not found`);
      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      console.warn(`No base URL configured in profile for viewer type: ${userViewerType}`);
      return;
    }

    // Ersetze den Platzhalter durch die Study Instance UID
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', studyInstanceUID);
    
    // ✅ Unterdrücke Browser-Warnung beim Öffnen des Viewers
    allowNavigationRef.current = true;
    console.log('🔓 Browser-Warnung für Viewer-Öffnung deaktiviert (openStudyInViewer)');

    try {
      if (userViewerType === 'web') {
        openInViewerWindow(viewerUrl);
      } else if (userViewerType === 'client') {
        window.location.href = viewerUrl;
      }
    } catch (error) {
      console.error('Error opening viewer:', error);
    }
    
    // 🔒 Nach 2 Sekunden Browser-Warnung wieder aktivieren
    setTimeout(() => {
      allowNavigationRef.current = false;
      console.log('🔒 Browser-Warnung wieder aktiviert');
    }, 2000);
  };

  const sendEmptyViewerRequest = () => {

    
    // Sichere Überprüfungen um Auth-Probleme zu vermeiden
    if (!currentUser || !currentUser.viewer_preference || !viewerConfig) {

      return;
    }

    const userViewerType = currentUser.viewer_preference;
    const viewerSetting = viewerConfig[userViewerType];

    if (!viewerSetting) {

      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      console.warn(`No base URL configured in profile for viewer type: ${userViewerType}`);
      return;
    }

    // Leere Study Instance UID senden
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', '');

    try {
      if (userViewerType === 'web') {
        if (!document.hidden) {
          openInViewerWindow(viewerUrl);
        }
      } else if (userViewerType === 'client') {
        // Für Client-Viewer: Sichere Methode verwenden
        const link = document.createElement('a');
        link.href = viewerUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      }
    } catch (error) {

    }
  };

  const handleOpenInViewer = (study) => {
    if (!study.study_instance_uid) {
      showError(t('approval.error.noStudyUID'), 'ERR-PACS-002');
      return;
    }

    if (!currentUser || !currentUser.viewer_preference) {
      showError(t('approval.error.viewerSettingUnavailable'), 'ERR-CONFIG-004');
      return;
    }

    // Spezielle Modalitäten immer mit Webviewer öffnen
    const studyModality = study.modality ? study.modality.toUpperCase() : '';
    
    let forceWebViewer = false;
    
    // ECG/EKG: Prüfe ob Modalität ECG oder EKG ENTHÄLT (z.B. ECG/SR, ECG\SR, EKG-Monitor)
    if (studyModality.includes('ECG') || studyModality.includes('EKG')) {
      forceWebViewer = true;
    }
    // OT/DOC: Exakte Übereinstimmung
    else if (studyModality === 'OT' || studyModality === 'DOC') {
      forceWebViewer = true;
    }
    
    // Für spezielle Modalitäten (OT/ECG/DOC): Direkt öffnen
    if (forceWebViewer) {
      openStudyDirectly(study, 'web');
    } else {
      // Für alle anderen: Prüfe User-Präferenz
      if (currentUser.viewer_preference === 'web') {
        // User hat bereits Web-Viewer als Präferenz: Direkt öffnen
        openStudyDirectly(study, 'web');
      } else {
        // User hat Client-Viewer als Präferenz: Bestätigungsdialog zeigen
        setPriorStudyDialog({
          open: true,
          study: study
        });
      }
    }
  };

  // Neue Funktion für direktes Öffnen ohne Dialog
  const openStudyDirectly = (study, userViewerType) => {
    const studyModality = study.modality ? study.modality.toUpperCase() : '';
    const isForceWebViewer = userViewerType === 'web';
    
    const viewerSetting = viewerConfig[userViewerType];

    if (!viewerSetting) {
      // Spezielle Fehlermeldung für erzwungene Webviewer-Modalitäten
      if (isForceWebViewer) {
        showError(t('approval.error.webViewerNotConfigured', { 
          modality: studyModality 
        }) || `Webviewer für Modalität "${studyModality}" ist nicht konfiguriert. Bitte konfigurieren Sie den Webviewer in der Serververwaltung.`, 'ERR-CONFIG-001');
      } else {
        showError(t('approval.error.viewerConfigNotFound', { 
          viewerType: userViewerType, 
          available: Object.keys(viewerConfig).join(', ') 
        }), 'ERR-CONFIG-002');
      }
      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      showError(t('approval.error.noViewerURL', { viewerType: userViewerType }), 'ERR-CONFIG-003');
      return;
    }

    // Ersetze den Platzhalter durch die tatsächliche Study Instance UID
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', study.study_instance_uid);

    // ✅ Unterdrücke Browser-Warnung beim Öffnen des Viewers
    allowNavigationRef.current = true;
    console.log('🔓 Browser-Warnung für Viewer-Öffnung deaktiviert (openStudyDirectly)');

    try {
      if (userViewerType === 'web') {
        openInViewerWindow(viewerUrl);
      } else if (userViewerType === 'client') {
        window.location.href = viewerUrl;
      }
    } catch (error) {
      console.error('Error opening viewer:', error);
      showError(t('approval.error.viewerOpen'), 'ERR-PACS-006');
    }
    
    // 🔒 Nach 2 Sekunden Browser-Warnung wieder aktivieren
    setTimeout(() => {
      allowNavigationRef.current = false;
      console.log('🔒 Browser-Warnung wieder aktiviert');
    }, 2000);
  };

  // Handler für den Voruntersuchung Bestätigungsdialog
  const handlePriorStudyDialogConfirm = () => {
    const study = priorStudyDialog.study;
    if (study) {
      // Verwende User-Präferenz für normale Studien
      openStudyDirectly(study, currentUser.viewer_preference);
    }
    setPriorStudyDialog({ open: false, study: null });
  };

  const handlePriorStudyDialogCancel = () => {
    setPriorStudyDialog({ open: false, study: null });
  };

  const handlePriorStudyDialogWebViewer = () => {
    const study = priorStudyDialog.study;
    if (study) {
      // Öffne immer im Web-Viewer
      openStudyDirectly(study, 'web');
    }
    setPriorStudyDialog({ open: false, study: null });
  };


  // Callback: PDF anzeigen aus Text-Ansicht - Öffne Popup-Fenster
  const handleShowPdfFromTextView = (reportData) => {
    if (reportData && reportData.pdf_data) {
      // Öffne PDF Preview in separatem Popup-Fenster
      const tempKey = `pdf_preview_${reportData.report_id}_${reportData.pdf_data?.report?.report?.metadata?.template_type || 'F'}_${Date.now()}`;
      localStorage.setItem(tempKey, JSON.stringify(reportData.pdf_data));
      
      // Optimale Größe für A4-Darstellung
      const width = 950;
      const height = Math.floor(window.screen.availHeight * 0.85);
      const left = Math.floor((window.screen.availWidth / 2 - width) / 2);
      const top = Math.floor((window.screen.availHeight - height) / 2);

      const newWindow = window.open(
        `/pdf-preview?key=${tempKey}`,
        `pdf_preview_${reportData.report_id}`,
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
      );

      if (!newWindow) {
        showError(t('report.error.popupBlocked'), 'ERR-REPORT-022');
        // Fallback: Öffne als Dialog mit der existierenden openPdfPreview Funktion
        const templateType = reportData.pdf_data?.report?.report?.metadata?.template_type || 'F';
        if (templateType === 'S') {
          setPdfPreviewStructuredOpen(true);
          setPdfData(reportData.pdf_data);
        } else {
          setPdfPreviewOpen(true);
          setPdfData(reportData.pdf_data);
        }
      } else {
        // Cleanup nach 10 Minuten
        setTimeout(() => {
          try {
            localStorage.removeItem(tempKey);
          } catch (e) {
            console.warn('Cleanup-Fehler für PDF Preview Key:', e);
          }
        }, 10 * 60 * 1000);
      }
    } else {
      showError(t('report.error.noPdfData'), 'ERR-REPORT-023');
    }
  };


  const fetchTemplates = async () => {
    try {
      const response = await fetch(createApiUrl('templates/'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates', error);
    }
  };

  const handleLoadTemplate = (templateType) => {
    if (!examData) return;
    
    // Prüfe ob bereits ein Template mit anderem Typ geladen ist und ob Daten vorhanden sind
    if (selectedTemplate && selectedTemplate.template_type !== templateType) {
      // Prüfe ob bereits Daten im Report vorhanden sind
      const hasData = Object.keys(formValues).some(key => {
        const value = formValues[key];
        return value && value.toString().trim().length > 0;
      });
      
      if (hasData) {
        // Zeige Warnung
        setTemplateTypeSwitchDialog({
          open: true,
          targetType: templateType
        });
        return;
      }
    }
    
    setActiveTemplateType(templateType);

    const lowerStudyDescription = (examData.study_description || '').toLowerCase();

    // ═══════════════════════════════════════════════════════════════════════════
    // 🔍 DEBUG: TEMPLATE-AUSWAHL
    // ═══════════════════════════════════════════════════════════════════════════
    console.log('\n═══════════════════════════════════════════════════════════════');
    console.log('🔍 TEMPLATE-AUSWAHL - START');
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('📊 UNTERSUCHUNGS-PARAMETER:');
    console.log('  Template-Typ:', templateType);
    console.log('  Modalität:', examData.modality);
    console.log('  Study Description:', examData.study_description);
    console.log('  new_exams.id:', examData.id);
    console.log('  examinations.id (examExaminationId):', examExaminationId);
    console.log('  Verwendete ID für Matching:', examExaminationId ?? examData.examination_id ?? examData.id);
    console.log('  Lower Study Description:', lowerStudyDescription);
    console.log('\n📚 VERFÜGBARE TEMPLATES (Typ=' + templateType + '):');
    const relevantTemplates = templates.filter(tpl => tpl.template_type === templateType);
    relevantTemplates.forEach((tpl, idx) => {
      console.log(`  [${idx + 1}] "${tpl.name}"`);
      console.log(`      Standard für: ${tpl.standard_for?.type || 'KEINE'}`);
      console.log(`      Values:`, tpl.standard_for?.values || []);
    });
    console.log('───────────────────────────────────────────────────────────────');

    // TEMPLATE-AUSWAHL NACH PRIORITÄT (höchste zuerst):
    // 1. Standard für spezifische Untersuchung (examinations)
    // 2. Standard für Modalität (modalities)
    // 3. Standard für alle (all)
    
    // 1) Höchste Priorität: Explizite Examinations-Matches (ID oder Label-Teil)
    const examMatch = templates.filter(tpl => {
      if (tpl.template_type !== templateType || !tpl.standard_for) return false;
      if (tpl.standard_for.type !== 'examinations') return false;
      return tpl.standard_for.values.some(val => {
        if (typeof val === 'number') {
          // Vergleiche mit loaded examExaminationId
          return val === (examExaminationId ?? examData.examination_id ?? examData.id);
        }
        if (typeof val === 'string') {
          return lowerStudyDescription.includes(val.toLowerCase());
        }
        return false;
      });
    });
    console.log('\n🥇 PRIORITÄT 1 - Untersuchungs-spezifisch (examinations):');
    if (examMatch.length > 0) {
      examMatch.forEach(tpl => console.log(`  ✅ MATCH: "${tpl.name}"`, tpl.standard_for?.values));
    } else {
      console.log('  ❌ Keine Matches');
    }

    // 2) Mittlere Priorität: Modalitäten-Matches
    const modalityMatch = templates.filter(tpl => 
      tpl.template_type === templateType && 
      tpl.standard_for?.type === 'modalities' && 
      tpl.standard_for.values.includes(examData.modality)
    );
    console.log('\n🥈 PRIORITÄT 2 - Modalitäts-spezifisch (modalities):');
    if (modalityMatch.length > 0) {
      modalityMatch.forEach(tpl => console.log(`  ✅ MATCH: "${tpl.name}"`, tpl.standard_for?.values));
    } else {
      console.log('  ❌ Keine Matches');
    }

    // 3) Niedrigste Priorität: Fallback „all"
    const allMatch = templates.filter(tpl => 
      tpl.template_type === templateType && 
      tpl.standard_for?.type === 'all'
    );
    console.log('\n🥉 PRIORITÄT 3 - Allgemein (all):');
    if (allMatch.length > 0) {
      allMatch.forEach(tpl => console.log(`  ✅ MATCH: "${tpl.name}"`, tpl.standard_for?.values));
    } else {
      console.log('  ❌ Keine Matches');
    }

    // Wähle Kandidaten nach Priorität (erste nicht-leere Liste)
    let candidates = [];
    if (examMatch.length) {
      candidates = examMatch;
      console.log('\n✨ GEWÄHLT: Untersuchungs-spezifisches Template (Priorität 1)');
    } else if (modalityMatch.length) {
      candidates = modalityMatch;
      console.log('\n✨ GEWÄHLT: Modalitäts-spezifisches Template (Priorität 2)');
    } else if (allMatch.length) {
      candidates = allMatch;
      console.log('\n✨ GEWÄHLT: Allgemeines Template (Priorität 3)');
    }
    
    if (candidates.length > 0) {
      console.log('📋 KANDIDATEN (' + candidates.length + '):');
      candidates.forEach(tpl => console.log(`  → "${tpl.name}"`));
    }
    console.log('═══════════════════════════════════════════════════════════════\n');

    if (candidates.length === 0) {
      // Kein Template gefunden -> Template-Browser automatisch öffnen

      handleTemplateBrowser(templateType);
      return;
    }



    if (candidates.length === 1) {
      applyTemplate(candidates[0]);
    } else {
      setMatchingTemplates(candidates);
      setTemplateDialogOpen(true);
    }
  };
  
  // Template Type Switch Confirmation
  const handleConfirmTemplateTypeSwitch = () => {
    const targetType = templateTypeSwitchDialog.targetType;
    setTemplateTypeSwitchDialog({ open: false, targetType: null });
    
    // Lösche alle Formulardaten
    setFormValues({});
    setReportContent('');
    
    // Führe jetzt den Template-Wechsel durch
    setActiveTemplateType(targetType);
    
    const lowerStudyDescription = (examData.study_description || '').toLowerCase();

    // ═══════════════════════════════════════════════════════════════════════════
    // 🔍 DEBUG: TEMPLATE-TYP-WECHSEL
    // ═══════════════════════════════════════════════════════════════════════════
    console.log('\n═══════════════════════════════════════════════════════════════');
    console.log('🔄 TEMPLATE-TYP-WECHSEL - START');
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('📊 UNTERSUCHUNGS-PARAMETER:');
    console.log('  Neuer Template-Typ:', targetType);
    console.log('  Modalität:', examData.modality);
    console.log('  Study Description:', examData.study_description);
    console.log('  Examination ID:', examExaminationId ?? examData.examination_id ?? examData.id);
    console.log('  Lower Study Description:', lowerStudyDescription);
    console.log('\n📚 VERFÜGBARE TEMPLATES (Typ=' + targetType + '):');
    const relevantTemplates = templates.filter(tpl => tpl.template_type === targetType);
    relevantTemplates.forEach((tpl, idx) => {
      console.log(`  [${idx + 1}] "${tpl.name}"`);
      console.log(`      Standard für: ${tpl.standard_for?.type || 'KEINE'}`);
      console.log(`      Values:`, tpl.standard_for?.values || []);
    });
    console.log('───────────────────────────────────────────────────────────────');

    // TEMPLATE-AUSWAHL NACH PRIORITÄT (höchste zuerst):
    // 1. Standard für spezifische Untersuchung (examinations)
    // 2. Standard für Modalität (modalities)
    // 3. Standard für alle (all)
    
    // 1) Höchste Priorität: Explizite Examinations-Matches (ID oder Label-Teil)
    const examMatch = templates.filter(tpl => {
      if (tpl.template_type !== targetType || !tpl.standard_for) return false;
      if (tpl.standard_for.type !== 'examinations') return false;
      return tpl.standard_for.values.some(val => {
        if (typeof val === 'number') {
          return val === (examExaminationId ?? examData.examination_id ?? examData.id);
        }
        if (typeof val === 'string') {
          return lowerStudyDescription.includes(val.toLowerCase());
        }
        return false;
      });
    });
    console.log('\n🥇 PRIORITÄT 1 - Untersuchungs-spezifisch (examinations):');
    if (examMatch.length > 0) {
      examMatch.forEach(tpl => console.log(`  ✅ MATCH: "${tpl.name}"`, tpl.standard_for?.values));
    } else {
      console.log('  ❌ Keine Matches');
    }

    // 2) Mittlere Priorität: Modalitäten-Matches
    const modalityMatch = templates.filter(tpl => 
      tpl.template_type === targetType && 
      tpl.standard_for?.type === 'modalities' && 
      tpl.standard_for.values.includes(examData.modality)
    );
    console.log('\n🥈 PRIORITÄT 2 - Modalitäts-spezifisch (modalities):');
    if (modalityMatch.length > 0) {
      modalityMatch.forEach(tpl => console.log(`  ✅ MATCH: "${tpl.name}"`, tpl.standard_for?.values));
    } else {
      console.log('  ❌ Keine Matches');
    }

    // 3) Niedrigste Priorität: Fallback „all"
    const allMatch = templates.filter(tpl => 
      tpl.template_type === targetType && 
      tpl.standard_for?.type === 'all'
    );
    console.log('\n🥉 PRIORITÄT 3 - Allgemein (all):');
    if (allMatch.length > 0) {
      allMatch.forEach(tpl => console.log(`  ✅ MATCH: "${tpl.name}"`, tpl.standard_for?.values));
    } else {
      console.log('  ❌ Keine Matches');
    }

    // Wähle Kandidaten nach Priorität (erste nicht-leere Liste)
    let candidates = [];
    if (examMatch.length) {
      candidates = examMatch;
      console.log('\n✨ GEWÄHLT: Untersuchungs-spezifisches Template (Priorität 1)');
    } else if (modalityMatch.length) {
      candidates = modalityMatch;
      console.log('\n✨ GEWÄHLT: Modalitäts-spezifisches Template (Priorität 2)');
    } else if (allMatch.length) {
      candidates = allMatch;
      console.log('\n✨ GEWÄHLT: Allgemeines Template (Priorität 3)');
    }
    
    if (candidates.length > 0) {
      console.log('📋 KANDIDATEN (' + candidates.length + '):');
      candidates.forEach(tpl => console.log(`  → "${tpl.name}"`));
    }
    console.log('═══════════════════════════════════════════════════════════════\n');

    if (candidates.length === 0) {
      handleTemplateBrowser(targetType);
      return;
    }

    if (candidates.length === 1) {
      applyTemplate(candidates[0]);
    } else {
      setMatchingTemplates(candidates);
      setTemplateDialogOpen(true);
    }
  };
  
  const handleCancelTemplateTypeSwitch = () => {
    setTemplateTypeSwitchDialog({ open: false, targetType: null });
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setActiveTemplateType(template.template_type);
    setActiveStepIndex(0);
    
    // Numpad states zurücksetzen
    setFocusedFindingIndex(0);
    closeAllDropdowns();
    findingRefs.current = [];
    
    if (template.template_type === 'F') {
      let parsedFields = [];
      try {
        if (typeof template.fields_json === 'string') {
          parsedFields = JSON.parse(template.fields_json);
        } else if (Array.isArray(template.fields_json)) {
          parsedFields = template.fields_json;
        } else {
          parsedFields = [];
        }
      } catch (_) {
        parsedFields = [];
      }

      // Initialwerte aus DB oder Defaults setzen
      const initial = {};
      parsedFields.forEach((f) => {
        let val = '';
        if (f.database_field && examData && examData[f.database_field] !== undefined) {
          val = examData[f.database_field] ?? '';
        } else if (f.default_value !== undefined) {
          val = f.default_value;
        }
        initial[f.id || f.label] = val;
      });
      setFormValues(initial);
      setReportContent(''); // Nicht mehr benötigt
    } else {
      setReportContent('');
    }
    
    // Setze den Zustand nach Template-Anwendung als "initial geladen"
    // Kurze Verzögerung um sicherzustellen dass DOM aktualisiert ist
    setTimeout(() => {
      const templateState = getCurrentReportState();
      setInitialLoadedState(templateState);
    }, 100);
    
    // Snippets laden und ersetzen (nur bei neuen Reports, nicht bei existierenden oder Addendum)
    // Verzögere um 2 Sekunden, damit existierende Reports zuerst geladen werden können
    setTimeout(() => {
      // Prüfe erneut ob inzwischen ein Report geladen wurde oder Addendum-Modus aktiv ist
      if (!existingReportId && !reportLoadedTimestamp && !isAddendumMode) {
        console.log('✅ Neuer Report - Snippets werden geladen (nach 2s Wartezeit)');
        loadAndReplaceSnippets(template);
      } else {
        console.log('⏭️ Existierender Report/Addendum erkannt - Snippets werden NICHT ersetzt (existingReportId:', existingReportId, ', reportLoadedTimestamp:', reportLoadedTimestamp, ', isAddendumMode:', isAddendumMode, ')');
      }
    }, 2000);
  };

  const handleTemplateBrowser = (templateType) => {
    setTemplateBrowserType(templateType);
    setTemplateBrowserOpen(true);
  };

  const getFilteredTemplatesByType = (templateType) => {
    return templates.filter(template => template.template_type === templateType);
  };

  // Numpad Navigation Functions
  const getAllFindings = () => {
    const allFindings = [];
    
    // Exakt die gleiche Reihenfolge wie renderStructuredContent
    if (selectedTemplate && selectedTemplate.template_type === 'S') {
      try {
        const items = typeof selectedTemplate.fields_json === 'string'
          ? JSON.parse(selectedTemplate.fields_json)
          : selectedTemplate.fields_json;
        
        if (Array.isArray(items)) {
          const processedFindingIndices = new Set();
          
          for (let idx = 0; idx < items.length; idx++) {
            const item = items[idx];

            if (item.item_type === 'field') {
              // Freifelder sind auch navigierbar
              allFindings.push(item);
              
            } else if (item.item_type === 'group') {
              // Gruppe – darin Findings nach Name gruppieren
              const grouped = groupFindingsByName(item.findings || []);

              Object.entries(grouped).forEach(([findingName, groupFinds]) => {
                if (groupFinds.length === 1 && !groupFinds[0].subtitle) {
                  allFindings.push(groupFinds[0]);
                } else {
                  groupFinds.forEach(f => {
                    allFindings.push(f);
                  });
                }
              });

            } else if (item.item_type === 'finding' && !processedFindingIndices.has(idx)) {
              // Sammle nachfolgende Findings mit gleichem Namen
              const sameName = [item];
              processedFindingIndices.add(idx);

              for (let j = idx + 1; j < items.length; j++) {
                const nxt = items[j];
                if (nxt.item_type === 'finding' && nxt.name === item.name && !processedFindingIndices.has(j)) {
                  sameName.push(nxt);
                  processedFindingIndices.add(j);
                } else if (nxt.item_type !== 'finding') {
                  break;
                }
              }

              if (sameName.length === 1 && !sameName[0].subtitle) {
                allFindings.push(sameName[0]);
              } else {
                sameName.forEach(f => {
                  allFindings.push(f);
                });
              }
            }
          }
        }
      } catch (_) {
        // Ignore parsing errors
      }
    }
    
    return allFindings;
  };

  const resolveFindingIndex = (findingItem) => {
    if (!findingItem) return 0;
    const idx = getAllFindings().findIndex((f) => f === findingItem);
    return idx >= 0 ? idx : 0;
  };

  const isFindingInStructuredGroup = (group, finding) => {
    if (!Array.isArray(group?.findings)) return false;
    return group.findings.some((f) => {
      if (f === finding) return true;
      if ((f.item_type || f.type) === 'group') return isFindingInStructuredGroup(f, finding);
      return false;
    });
  };

  const resolveStepIndexForFinding = (findingIndex) => {
    const allFindings = getAllFindings();
    const finding = allFindings[findingIndex];
    if (!finding || templateSteps.length === 0) return 0;

    for (let i = 0; i < templateSteps.length; i++) {
      const step = templateSteps[i];
      if (step.type === 'structured-field' && step.item === finding) return i;
      if (step.type === 'structured-group' && isFindingInStructuredGroup(step.item, finding)) return i;
      if (step.type === 'structured-finding' && step.items?.includes(finding)) return i;
    }
    return 0;
  };

  useEffect(() => {
    if (selectedTemplate?.template_type !== 'S' || templateSteps.length === 0) return;
    const stepIndex = resolveStepIndexForFinding(focusedFindingIndex);
    setActiveStepIndex((prev) => (prev === stepIndex ? prev : stepIndex));
  }, [focusedFindingIndex, selectedTemplate?.template_type, templateSteps]);

  const navigateToNextFinding = () => {
    const totalFindings = getAllFindings().length;
    if (totalFindings === 0) return;

    // Alle Dropdowns schließen vor Navigation
    closeAllDropdowns();

    let nextIndex = (focusedFindingIndex + 1) % totalFindings;
    
    // Suche das nächste verfügbare (nicht gesperrte) Feld
    nextIndex = findNextAvailableField(nextIndex, 1);
    
    setFocusedFindingIndex(nextIndex);
    focusCurrentFinding(nextIndex, false); // skipDisabled = false da wir schon gesucht haben
  };

  const navigateToPreviousFinding = () => {
    const totalFindings = getAllFindings().length;
    if (totalFindings === 0) return;

    // Alle Dropdowns schließen vor Navigation
    closeAllDropdowns();

    let prevIndex = focusedFindingIndex <= 0 ? totalFindings - 1 : focusedFindingIndex - 1;
    
    // Suche das vorherige verfügbare (nicht gesperrte) Feld
    prevIndex = findNextAvailableField(prevIndex, -1);
    
    setFocusedFindingIndex(prevIndex);
    focusCurrentFinding(prevIndex, false); // skipDisabled = false da wir schon gesucht haben
  };

  // Navigation für Freitext-Templates
  const navigateToNextFreetextField = () => {
    if (selectedTemplate?.template_type !== 'F') return;
    
    // Alle editierbaren Freitextfelder finden (sauberer Selektor)
    const freetextFields = document.querySelectorAll('[contenteditable="true"]:not([disabled])');
    const currentField = document.activeElement;
    
    // Aktuellen Index finden
    let currentIndex = -1;
    freetextFields.forEach((field, index) => {
      if (field === currentField) {
        currentIndex = index;
      }
    });
    
    // Zum nächsten Feld navigieren
    const nextIndex = currentIndex + 1;
    if (nextIndex < freetextFields.length) {
      const nextField = freetextFields[nextIndex];
      
      // Fokus setzen mit kurzer Verzögerung für saubere Navigation
      setTimeout(() => {
        nextField.focus();
        
        // Cursor ans Ende setzen
        if (nextField.contentEditable === 'true') {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(nextField);
          range.collapse(false); // Cursor ans Ende
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
    }
  };

  const isFieldDisabled = (finding) => {
    if (!finding) return true;
    if (isAddendumMode || reportStatus === 'APPROVED') return true;

    const itemType = finding.item_type || finding.type;

    if (itemType === 'field') {
      const fieldType = finding.type || 'freetext';
      return Boolean(finding.database_field && finding.database_field.trim()) || fieldType === 'database';
    }

    if (itemType === 'finding') {
      return Boolean(finding.database_field && finding.database_field.trim()) || finding.finding_type === 'database';
    }

    return false;
  };

  const getFindingIndicesForStep = (stepIndex) => {
    const step = templateSteps[stepIndex];
    if (!step) return [];
    const allFindings = getAllFindings();

    if (step.type === 'structured-field') {
      const idx = allFindings.findIndex((f) => f === step.item);
      return idx >= 0 ? [idx] : [];
    }
    if (step.type === 'structured-group') {
      return allFindings.reduce((indices, finding, index) => {
        if (isFindingInStructuredGroup(step.item, finding)) indices.push(index);
        return indices;
      }, []);
    }
    if (step.type === 'structured-finding' && step.items?.length) {
      return step.items
        .map((item) => allFindings.findIndex((f) => f === item))
        .filter((idx) => idx >= 0);
    }
    return [];
  };

  const resolveFindingIndexForStep = (stepIndex) => {
    const indices = getFindingIndicesForStep(stepIndex);
    for (const idx of indices) {
      if (!isFieldDisabled(getAllFindings()[idx])) {
        return idx;
      }
    }
    return indices[0] ?? 0;
  };

  const handleStructuredFindingFocus = (globalIndex, item) => {
    if (!isFieldDisabled(item)) {
      setFocusedFindingIndex((prev) => (prev === globalIndex ? prev : globalIndex));
    }
  };

  const buildDropdownKey = (item, globalIndex) => `dropdown_${item?.id ?? globalIndex}`;

  const setDropdownOpen = (key, open) => {
    setDropdownStates((prev) => {
      if (open) {
        if (prev[key] && Object.keys(prev).length === 1) return prev;
        return { [key]: true };
      }
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const closeAllDropdowns = () => {
    setDropdownStates((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  };

  dropdownStatesRef.current = dropdownStates;

  const findNextAvailableField = (startIndex, direction = 1) => {
    const allFindings = getAllFindings();
    let currentIndex = startIndex;
    
    for (let i = 0; i < allFindings.length; i++) {
      const finding = allFindings[currentIndex];
      if (finding && !isFieldDisabled(finding)) {
        return currentIndex;
      }
      
      // Zum nächsten Index
      currentIndex = (currentIndex + direction + allFindings.length) % allFindings.length;
      
      // Vermeide Endlosschleife
      if (currentIndex === startIndex) break;
    }
    
    return startIndex; // Fallback zum ursprünglichen Index
  };

  useEffect(() => {
    if (selectedTemplate?.template_type !== 'S') return;
    const allFindings = getAllFindings();
    const current = allFindings[focusedFindingIndex];
    if (!current || !isFieldDisabled(current)) return;

    const corrected = findNextAvailableField(focusedFindingIndex, 1);
    if (corrected !== focusedFindingIndex && !isFieldDisabled(allFindings[corrected])) {
      setFocusedFindingIndex(corrected);
      focusCurrentFinding(corrected, false);
    }
  }, [focusedFindingIndex, selectedTemplate?.id, reportStatus, isAddendumMode]);

  const focusCurrentFinding = (index, skipDisabled = true) => {
    const allFindings = getAllFindings();
    
    // Suche das nächste verfügbare Feld wenn skipDisabled aktiviert
    const targetIndex = skipDisabled ? findNextAvailableField(index) : index;
    const currentFinding = allFindings[targetIndex];
    
    if (!currentFinding || !findingRefs.current[targetIndex]) return;

    // Update focusedFindingIndex wenn ein anderer Index gewählt wurde (nur beim ersten Load)
    if (targetIndex !== index && skipDisabled) {
      setFocusedFindingIndex(targetIndex);
      return; // useEffect wird dann focusCurrentFinding erneut aufrufen
    }

    // Auto-Focus je nach Finding-Typ
    setTimeout(() => {
      const container = findingRefs.current[targetIndex];
      if (!container) return;
      
      // Zuerst das Element ins sichtbare Scrolling bringen
      container.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      
      // Dann fokussieren
      setTimeout(() => {
        // Für 'field' items (Freifelder)
        if (currentFinding.item_type === 'field' || currentFinding.type === 'field') {
          const textField = container.querySelector('input[type="text"], textarea, [contenteditable="true"]');
          if (textField && !textField.disabled) {
            textField.focus();
            if (textField.select) {
              textField.select();
            } else if (textField.contentEditable === 'true') {
              // Für contentEditable Elemente (RichTextField)
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(textField);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          } else {
            // Wenn Textfeld gesperrt ist, Container fokussieren
            container.focus();
          }
        }
        // Für 'finding' items  
        else if (currentFinding.finding_type === 'freetext' || currentFinding.finding_type === 'freetext_unit') {
          // Textfeld fokussieren (erweitert für RichTextField)
          const textField = container.querySelector('input[type="text"], textarea, [contenteditable="true"]');
          if (textField) {
            textField.focus();
            if (textField.select) {
              textField.select(); // Text auswählen für bessere UX
            } else if (textField.contentEditable === 'true') {
              // Für contentEditable Elemente (RichTextField)
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(textField);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        } else if (currentFinding.finding_type === 'dropdown') {
          const dropdownKey = buildDropdownKey(currentFinding, targetIndex);
          setDropdownOpen(dropdownKey, true);

          const selectEl = container.querySelector('[role="combobox"], .MuiSelect-select');
          if (selectEl) {
            selectEl.focus();
          }
        } else if (currentFinding.finding_type === 'yesno_comment') {
          // Für Yes/No Felder den Container fokussieren
          container.focus();
        } else if (currentFinding.finding_type === 'checkboxes') {
          // Für Checkboxes den Container fokussieren
          container.focus();
        } else {
          // Fallback: Container fokussieren
          container.focus();
        }
      }, 300);
    }, 100);
  };

  const selectFindingOption = (optionIndex) => {
    const allFindings = getAllFindings();
    const currentFinding = allFindings[focusedFindingIndex];
    
    if (!currentFinding) return;

    // Verwende die gleiche fieldKey-Logic wie in renderStructuredItem
    const fieldKey = currentFinding.id || focusedFindingIndex;

    // Für 'field' items gibt es keine Optionen - ignoriere
    if (currentFinding.item_type === 'field' || currentFinding.type === 'field') {
      return;
    }

    // Je nach Finding-Typ entsprechende Aktion ausführen
    switch (currentFinding.finding_type) {
      case 'dropdown':
        // Filtere leere Optionen wie im Rendering
        const filteredOptions = (currentFinding.options || currentFinding.dropdown_options || [])
          .filter(opt => opt && opt.trim() !== '');
        
        if (optionIndex >= 1 && optionIndex <= filteredOptions.length) {
          const selectedOption = filteredOptions[optionIndex - 1];
          setFormValues(prev => ({
            ...prev,
            [fieldKey]: selectedOption
          }));
          
          // Dropdown schließen
          const dropdownKey = buildDropdownKey(currentFinding, focusedFindingIndex);
          setDropdownOpen(dropdownKey, false);
          
          // Automatisch zum nächsten Finding
          setTimeout(() => navigateToNextFinding(), 200);
        }
        break;
      case 'checkboxes':
        if (optionIndex >= 1 && optionIndex <= (currentFinding.options || currentFinding.checkbox_options || []).length) {
          const option = (currentFinding.options || currentFinding.checkbox_options)[optionIndex - 1];
          setFormValues(prev => {
            const currentValues = prev[fieldKey] || [];
            const isChecked = currentValues.includes(option);
            
            return {
              ...prev,
              [fieldKey]: isChecked 
                ? currentValues.filter(v => v !== option)
                : [...currentValues, option]
            };
          });
          // Automatisch zum nächsten Finding nach Checkbox-Toggle
          setTimeout(() => navigateToNextFinding(), 200);
        }
        break;
      case 'yesno_comment':
        if (optionIndex === 1) {
          setFormValues(prev => ({
            ...prev,
            [fieldKey]: 'yes'
          }));
          // Fokus auf Kommentarfeld setzen statt weitersprungen
          setTimeout(() => {
            const container = findingRefs.current[focusedFindingIndex];
            if (container) {
              const commentField = container.querySelector('textarea, input[type="text"]:last-of-type, [contenteditable="true"]:last-of-type');
              if (commentField) {
                commentField.focus();
                // Für contentEditable Elemente Cursor ans Ende setzen
                if (commentField.contentEditable === 'true') {
                  const range = document.createRange();
                  const selection = window.getSelection();
                  range.selectNodeContents(commentField);
                  range.collapse(false); // Cursor ans Ende
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }
            }
          }, 100);
        } else if (optionIndex === 2) {
          setFormValues(prev => ({
            ...prev,
            [fieldKey]: 'no'
          }));
          // Fokus auf Kommentarfeld setzen statt weitersprungen
          setTimeout(() => {
            const container = findingRefs.current[focusedFindingIndex];
            if (container) {
              const commentField = container.querySelector('textarea, input[type="text"]:last-of-type, [contenteditable="true"]:last-of-type');
              if (commentField) {
                commentField.focus();
                // Für contentEditable Elemente Cursor ans Ende setzen
                if (commentField.contentEditable === 'true') {
                  const range = document.createRange();
                  const selection = window.getSelection();
                  range.selectNodeContents(commentField);
                  range.collapse(false); // Cursor ans Ende
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }
            }
          }, 100);
        }
        break;
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    setFormValues(prev => ({ ...prev, [fieldKey]: value }));
  };

  // Hilfsfunktion für eindeutige IDs für Spracherkennung
  const generateFieldId = (fieldKey) => {
    return `field-${fieldKey?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
  };

  // Hilfsfunktion für semantische IDs basierend auf Name/Label
  const generateSemanticFieldId = (item, fieldKey) => {
    const semanticName = item.name || item.label || fieldKey;
    return `field-${semanticName?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
  };

  const renderTemplateField = (field) => {
    const key = field.id || field.label;
    const isDatabase = Boolean(field.database_field && field.database_field.trim());
    const isReadOnly = reportStatus === 'APPROVED';
    
    // ============================================================================
    // MULTI-EXAM MODUS: clinical_info und question pro Studie anzeigen
    // ============================================================================
    if (isMultiExam && combinedExams && combinedExams.length > 1 && 
        isDatabase && field.database_field &&
        (field.database_field === 'clinical_info' || field.database_field === 'question' ||
         [...new Set(combinedExams.map((e) => e[field.database_field] ?? '').filter(Boolean))].length > 1)) {
      
      return (
        <MultiDatabaseFieldAccordion
          combinedExams={combinedExams}
          databaseField={field.database_field}
          fieldLabel={field.label}
          isDarkMode={isDarkMode}
        />
      );
    }
    
    const fieldValue = isDatabase ? (examData?.[field.database_field] ?? '') : (formValues[key] ?? '');
    const fieldId = generateFieldId(key);
    
    const commonProps = {
      fullWidth: true,
      size: 'small',
      value: fieldValue,
      onChange: (e) => handleFieldChange(key, e.target.value),
      disabled: isDatabase || isReadOnly || isAddendumMode,
      databaseField: isDatabase,
      id: fieldId,
      name: key
    };

    switch (field.type) {
      case 'textarea':
        return <RichTextField {...commonProps} multiline minRows={field.height || 3} fullWidth onInlineCommentClick={handleInlineCommentIconClick} onCommentIconsChanged={handleCommentIconsChanged} onTooltipShow={handleCommentIconTooltipShow} onTooltipHide={handleCommentIconTooltipHide} isDarkMode={isDarkMode} theme={theme} label={null} onSnippetDialogOpen={openSnippetDialog} onSnippetInsertionPointSet={setSnippetInsertionPoint} />;
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={formValues[key] ?? ''}
              label={field.label}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              disabled={isReadOnly || isAddendumMode}
            >
              {Array.isArray(field.options) && field.options.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'checkbox':
        return (
          <FormControlLabel
            control={<Checkbox checked={Boolean(formValues[key])} onChange={(e) => handleFieldChange(key, e.target.checked)} disabled={isReadOnly || isAddendumMode} />}
            label={field.label}
          />
        );
      default:
        // text
        return <RichTextField {...commonProps} fullWidth onInlineCommentClick={handleInlineCommentIconClick} onCommentIconsChanged={handleCommentIconsChanged} onTooltipShow={handleCommentIconTooltipShow} onTooltipHide={handleCommentIconTooltipHide} isDarkMode={isDarkMode} theme={theme} label={null} onSnippetDialogOpen={openSnippetDialog} onSnippetInsertionPointSet={setSnippetInsertionPoint} />;
    }
  };

  const getItemType = (obj) => obj.item_type || obj.type;

  const renderStructuredItem = (item, idx, parentKey = '', indentLevel = 0, globalIndex = 0) => {
    const itemType = getItemType(item);
    const keyBase = parentKey ? `${parentKey}.${item.id || idx}` : (item.id || idx);

    // GROUP --------------------------------------------------
    if (itemType === 'group') {
      return (
        <Box key={keyBase} sx={{ width: '100%', borderLeft: '3px solid', borderColor: 'primary.main', pl: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'light', mb: 1, textDecoration: indentLevel === 0 ? 'underline' : 'none' }}>
            {item.label || item.name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Array.isArray(item.findings) && item.findings.map((f, i) => renderStructuredItem(f, i, keyBase, indentLevel + 1, resolveFindingIndex(f)))}
          </Box>
        </Box>
      );
    }

    // FIELD -------------------------------------------------- (Freifeld im strukturierten Template)
    if (itemType === 'field') {
      const fieldKey = keyBase;
      const fieldType = item.type || 'freetext';
      const isDatabase = Boolean(item.database_field && item.database_field.trim()) || fieldType === 'database';
      const val = isDatabase ? (examData?.[item.database_field] ?? '') : (formValues[fieldKey] ?? '');
      const isActive = focusedFindingIndex === globalIndex && !isFieldDisabled(item);

      return (
        <Box 
          key={fieldKey} 
          className={isDatabase ? (isDarkMode ? 'database-container-dark' : 'database-container-light') : ''}
          sx={{ 
            mb: 1,
            border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1,
            position: 'relative',
            outline: 'none',
            '&:focus': {
              outline: 'none'
            }
          }}
          ref={el => { findingRefs.current[globalIndex] = el; }}
          onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
          tabIndex={-1}
        >
          <label 
            htmlFor={generateSemanticFieldId(item, fieldKey)}
            style={{ 
              display: 'block',
              fontWeight: 'light', 
              marginBottom: '8px', 
              marginLeft: `${indentLevel * 16}px`,
              fontSize: '1rem',
              color: theme.palette.text.primary
            }}
          >
            {item.label || item.name}
          </label>
          <RichTextField
            fullWidth
            multiline
            minRows={item.height || 3}
            size="small"
            disabled={isDatabase || isAddendumMode}
            databaseField={isDatabase}
            value={val}
            placeholder={item.placeholder || t('report.enterValue')}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            onInlineCommentClick={handleInlineCommentIconClick}
            onCommentIconsChanged={handleCommentIconsChanged}
            onTooltipShow={handleCommentIconTooltipShow}
            onTooltipHide={handleCommentIconTooltipHide}
            isDarkMode={isDarkMode}
            theme={theme}
            id={generateSemanticFieldId(item, fieldKey)}
            name={item.name || item.label || fieldKey}
            onSnippetDialogOpen={openSnippetDialog}
            onSnippetInsertionPointSet={setSnippetInsertionPoint}
            label={null}
            sx={{
              minWidth: 0,
              pointerEvents: (isDatabase || isAddendumMode) ? 'none' : 'auto'
            }}
          />
        </Box>
      );
    }

    // FINDING ----------------------------------------------
    if (itemType === 'finding') {
      const fieldKey = keyBase;
      const isDatabase = Boolean(item.database_field && item.database_field.trim()) || item.finding_type === 'database';
      const val = isDatabase ? (examData?.[item.database_field] ?? '') : (formValues[fieldKey] ?? '');
      
      // 🔥 EXAKT wie StructuredReporting.js: Bei Verschachtelung (indentLevel > 1) nur Subtitle zeigen
      const isInSubGroup = indentLevel > 1;
      const labelText = isInSubGroup 
        ? (item.subtitle || item.name) // In Sub-Group: Zeige Subtitle (oder Name als Fallback)
        : (item.subtitle ? `${item.name} - ${item.subtitle}` : item.name); // Top-Level: Zeige beides
      
      const isActive = focusedFindingIndex === globalIndex && !isFieldDisabled(item);

      // Gemeinsame Label-Komponente - EXAKT wie Template Editor mit Einrückung
      const labelComp = (
        <Typography variant="body1" sx={{ 
          fontWeight: 'light', 
          color: 'text.primary', 
          minWidth: `${300 - (indentLevel * 16)}px`, // Reduziere minWidth bei Einrückung
          flexShrink: 0,
          ml: indentLevel * 2 // Nur das Label wird eingerückt
        }}>
          {labelText}:
        </Typography>
      );

      // Unterschiedliche Renderpfade je nach Typ
      switch (item.finding_type) {
        case 'freetext':
        case 'freitext':
          return (
            <Box 
              key={fieldKey} 
              className={isDatabase ? (isDarkMode ? 'database-container-dark' : 'database-container-light') : ''}
              sx={{ 
                mb: 1,
                border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                position: 'relative',
                outline: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }}
              ref={el => { findingRefs.current[globalIndex] = el; }}
              onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
              tabIndex={-1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}>
                {labelComp}
                <RichTextField
                  placeholder={item.placeholder || t('report.enterValue')}
                  size="small"
                  multiline={false}
                  sx={{ flex: 1, maxWidth: `${400 + (indentLevel * 16)}px`, flexShrink: 0 }}
                  value={val}
                  onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                  onInlineCommentClick={handleInlineCommentIconClick}
                  onCommentIconsChanged={handleCommentIconsChanged}
                  onTooltipShow={handleCommentIconTooltipShow}
                  onTooltipHide={handleCommentIconTooltipHide}
                  disabled={isDatabase || isAddendumMode}
                  databaseField={isDatabase}
                  isDarkMode={isDarkMode}
                  theme={theme}
                  id={generateSemanticFieldId(item, fieldKey)}
                  name={item.name || item.label || fieldKey}
                  onSnippetDialogOpen={openSnippetDialog}
                  onSnippetInsertionPointSet={setSnippetInsertionPoint}
                  label={null}
                />
              </Box>
            </Box>
          );

        case 'freetext_unit':
        case 'freitext_unit':
          return (
            <Box 
              key={fieldKey} 
              className={isDatabase ? (isDarkMode ? 'database-container-dark' : 'database-container-light') : ''}
              sx={{ 
                mb: 1,
                border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                position: 'relative',
                outline: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }}
              ref={el => { findingRefs.current[globalIndex] = el; }}
              onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
              tabIndex={-1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}>
                {labelComp}
                <RichTextField
                  placeholder={item.placeholder || t('report.enterValue')}
                  size="small"
                  multiline={false}
                  sx={{ flex: 1, maxWidth: `${400 + (indentLevel * 16)}px`, flexShrink: 0 }}
                  value={val}
                  onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                  onInlineCommentClick={handleInlineCommentIconClick}
                  onCommentIconsChanged={handleCommentIconsChanged}
                  onTooltipShow={handleCommentIconTooltipShow}
                  onTooltipHide={handleCommentIconTooltipHide}
                  disabled={isDatabase || isAddendumMode}
                  databaseField={isDatabase}
                  isDarkMode={isDarkMode}
                  theme={theme}
                  id={generateSemanticFieldId(item, fieldKey)}
                  name={item.name || item.label || fieldKey}
                  onSnippetDialogOpen={openSnippetDialog}
                  onSnippetInsertionPointSet={setSnippetInsertionPoint}
                  label={null}
                />
                <Typography variant="body2" sx={{ minWidth: 40, flexShrink: 0 }}>
                  {item.unit === 'custom' ? item.custom_unit : item.unit}
                </Typography>
              </Box>
            </Box>
          );

        case 'dropdown': {
          const dropdownKey = buildDropdownKey(item, globalIndex);
          return (
            <Box 
              key={fieldKey} 
              sx={{ 
                mb: 1,
                border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                position: 'relative',
                outline: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }}
              ref={el => { findingRefs.current[globalIndex] = el; }}
              onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
              tabIndex={-1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}>
                {labelComp}
                <FormControl size="small" sx={{ flex: 1, maxWidth: `${400 + (indentLevel * 16)}px`, flexShrink: 0 }}>
                  <Select
                    id={`dropdown-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                    name={item.name || item.label || fieldKey}
                    value={val}
                    displayEmpty
                    disabled={isDatabase || isAddendumMode}
                    open={dropdownStates[dropdownKey] || false}
                    onOpen={() => setDropdownOpen(dropdownKey, true)}
                    onClose={() => setDropdownOpen(dropdownKey, false)}
                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                    renderValue={(value) => value || t('report.selectOption')}
                    onFocus={() => {
                      if (isDatabase || isAddendumMode) return;
                      setDropdownOpen(dropdownKey, true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        setDropdownOpen(dropdownKey, false);
                      }
                    }}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      disableAutoFocus: true,
                      autoFocus: false,
                      PaperProps: {
                        style: {
                          maxHeight: 200
                        }
                      },
                      MenuListProps: {
                        disablePadding: false,
                        autoFocus: false,
                        autoFocusItem: false,
                        disableListWrap: true
                      }
                    }}
                  >
                    {(item.options ?? item.dropdown_options)
                      ?.filter(opt => opt && opt.trim() !== '') // Filtere leere Optionen
                      ?.map((opt, idx) => (
                      <MenuItem 
                        key={idx} 
                        value={opt}
                        id={`item-${opt?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                        data-name={opt}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span>
                            {opt}
                            <sub style={{ 
                              color: 'var(--rd-primary)', 
                              fontWeight: 'light',
                              fontSize: '0.8em',
                              marginLeft: '4px'
                            }}>
                              {idx + 1}
                            </sub>
                          </span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          );
        }

        case 'checkboxes': {
          const currentValues = Array.isArray(val) ? val : [];
          return (
            <Box 
              key={fieldKey} 
              sx={{ 
                mb: 1,
                border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                position: 'relative',
                outline: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }}
              ref={el => { findingRefs.current[globalIndex] = el; }}
              onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
              tabIndex={-1}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, maxWidth: '100%' }}>
                {labelComp}
                <Box 
                  sx={{ flex: 1, maxWidth: `${400 + (indentLevel * 16)}px`, flexShrink: 0 }}
                  id={`checkbox-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                >
                  {(item.options ?? item.checkbox_options)?.map((opt, idx) => {
                    const isChecked = currentValues.includes(opt);
                    const checkboxId = `checkbox-item-${opt?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
                    return (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            id={checkboxId}
                            name={opt}
                            checked={isChecked}
                            disabled={isDatabase || isAddendumMode}
                            onChange={(e) => {
                              setFormValues(prev => {
                                const prevVals = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
                                return {
                                  ...prev,
                                  [fieldKey]: e.target.checked
                                    ? [...prevVals, opt]
                                    : prevVals.filter(v => v !== opt)
                                };
                              });
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <span>
                              {opt}
                              <sub style={{ 
                                color: 'var(--rd-primary)', 
                                fontWeight: 'light',
                                fontSize: '0.8em',
                                marginLeft: '4px'
                              }}>
                                {idx + 1}
                              </sub>
                            </span>
                          </Box>
                        }
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          );
        }

        case 'yesno_comment': {
          return (
            <Box 
              key={fieldKey} 
              sx={{ 
                mb: 1,
                border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                position: 'relative',
                outline: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }}
              ref={el => { findingRefs.current[globalIndex] = el; }}
              onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
              tabIndex={-1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, maxWidth: '100%' }}>
                {labelComp}
                <RadioGroup 
                  row 
                  sx={{ flex: 1, maxWidth: `${400 + (indentLevel * 16)}px`, flexShrink: 0 }}
                  value={val || ""}
                  onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                  name={`yesno-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                >
                  <FormControlLabel
                    value="yes"
                    disabled={isDatabase || isAddendumMode}
                    control={
                      <Radio 
                        id={`yesno-ja-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                        name={`yesno-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span>
                          {t('report.yes')}
                          <sub style={{ 
                            color: 'var(--rd-primary)', 
                            fontWeight: 'light',
                            fontSize: '0.8em',
                            marginLeft: '4px'
                          }}>
                            1
                          </sub>
                        </span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="no"
                    disabled={isDatabase || isAddendumMode}
                    control={
                      <Radio 
                        id={`yesno-nein-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                        name={`yesno-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span>
                          {t('report.no')}
                          <sub style={{ 
                            color: 'var(--rd-primary)', 
                            fontWeight: 'light',
                            fontSize: '0.8em',
                            marginLeft: '4px'
                          }}>
                            2
                          </sub>
                        </span>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ minWidth: `${300 - (indentLevel * 16)}px`, flexShrink: 0, ml: indentLevel * 2 }}></Box>
                <RichTextField
                  multiline
                  minRows={2}
                  placeholder={item.comment_placeholder || t('report.comment')}
                  size="small"
                  sx={{ flex: 1, maxWidth: `${400 + (indentLevel * 16)}px`, flexShrink: 0 }}
                  value={formValues[`${fieldKey}.comment`] || ""}
                  onChange={(e) => handleFieldChange(`${fieldKey}.comment`, e.target.value)}
                  onInlineCommentClick={handleInlineCommentIconClick}
                  onCommentIconsChanged={handleCommentIconsChanged}
                  onTooltipShow={handleCommentIconTooltipShow}
                  onTooltipHide={handleCommentIconTooltipHide}
                  disabled={isDatabase || isAddendumMode}
                  databaseField={false}
                  isDarkMode={isDarkMode}
                  theme={theme}
                  id={`yesno-comment-${(item.name || item.label || fieldKey)?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                  name={`${item.name || item.label || fieldKey}-comment`}
                  onSnippetDialogOpen={openSnippetDialog}
                  onSnippetInsertionPointSet={setSnippetInsertionPoint}
                  label={null}
                />
              </Box>
            </Box>
          );
        }

        case 'database':
          return (
            <Box 
              key={fieldKey} 
              className={isDarkMode ? 'database-container-dark' : 'database-container-light'}
              sx={{ 
                mb: 1,
                border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                position: 'relative',
                outline: 'none',
                '&:focus': {
                  outline: 'none'
                }
              }}
              ref={el => { findingRefs.current[globalIndex] = el; }}
              onFocusCapture={() => handleStructuredFindingFocus(globalIndex, item)}
              tabIndex={-1}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, maxWidth: '100%' }}>
                {labelComp}
                <RichTextField
                  placeholder={item.placeholder || t('report.enterValue')}
                  size="small"
                  multiline
                  sx={{ 
                    flex: 1, 
                    maxWidth: `${400 + (indentLevel * 16)}px`, 
                    flexShrink: 0,
                    '& .MuiInputBase-root': { overflow: 'hidden' },
                    '& textarea': { overflow: 'hidden !important', resize: 'none' }
                  }}
                  value={val}
                  onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                  onInlineCommentClick={handleInlineCommentIconClick}
                  onCommentIconsChanged={handleCommentIconsChanged}
                  onTooltipShow={handleCommentIconTooltipShow}
                  onTooltipHide={handleCommentIconTooltipHide}
                  disabled={true}
                  databaseField={true}
                  isDarkMode={isDarkMode}
                  theme={theme}
                  id={generateSemanticFieldId(item, fieldKey)}
                  name={item.name || item.label || fieldKey}
                  onSnippetDialogOpen={openSnippetDialog}
                  onSnippetInsertionPointSet={setSnippetInsertionPoint}
                  label={null}
                />
              </Box>
            </Box>
          );

        default:
          return null;
      }
    }

    return null;
  };

  // Beim ersten vollständigen Laden automatisch Freitext-Template wählen (nur wenn KEIN existierender Report und KEIN Addendum-Modus)
  useEffect(() => {
    if (!initialTemplateLoaded && examData && templates.length && !isAddendumMode && !existingReportId && !reportLoadedTimestamp) {
      console.log('✅ Lade initiales Template (kein existierender Report, kein Addendum)');
      handleLoadTemplate('F');
      setInitialTemplateLoaded(true);
    } else if (!initialTemplateLoaded && (existingReportId || reportLoadedTimestamp || isAddendumMode)) {
      console.log('⏭️ Überspringe initiales Template-Laden (existierender Report oder Addendum erkannt)');
      setInitialTemplateLoaded(true); // Markiere als geladen, um weitere Trigger zu verhindern
    }
  }, [examData, templates, initialTemplateLoaded, isAddendumMode, existingReportId, reportLoadedTimestamp]);

  // Template-Laden für kombinierte Befunde (nach dem Erstellen des kombinierten Reports)
  useEffect(() => {
    if (needsCombinedTemplateLoad && examData && templates.length > 0) {
      console.log('🔗 MULTI-EXAM: Lade Template für kombinierten Befund');
      // Für kombinierte Befunde immer Freitext-Template verwenden
      handleLoadTemplate('F');
      setNeedsCombinedTemplateLoad(false);
      setInitialTemplateLoaded(true);
    }
  }, [needsCombinedTemplateLoad, examData, templates]);

  // Wenn examExaminationId geladen wird, Template-Auswahl neu triggern (nur bei neuem Report ohne existierenden Befund, nicht im Addendum-Modus)
  useEffect(() => {
    if (examExaminationId && initialTemplateLoaded && examData && templates.length && !existingReportId && !reportLoadedTimestamp && !isAddendumMode) {
      console.log('🔄 examExaminationId geladen - Template-Auswahl wird neu getriggert mit ID:', examExaminationId);
      handleLoadTemplate(activeTemplateType || 'F');
    }
  }, [examExaminationId]);



  // Study Instance UID ist bereits in examData verfügbar - keine PACS-API nötig

  // Study Instance UID aus currentNewReport (falls vorhanden) oder examData verwenden
  // Bei kombinierten Befunden: Alle UIDs mit Komma getrennt zusammenfassen
  const realStudyInstanceUID = useMemo(() => {
    if (isMultiExam && combinedExams && combinedExams.length > 1) {
      // Kombinierte Befunde: Alle Study Instance UIDs sammeln
      const uids = combinedExams
        .map(exam => exam.study_instance_uid)
        .filter(uid => uid) // Nur gültige UIDs
        .filter((uid, index, self) => self.indexOf(uid) === index); // Unique
      if (uids.length > 0) {
        console.log('🔗 MULTI-EXAM: Kombinierte StudyInstanceUIDs für Viewer:', uids.join(','));
        return uids.join(',');
      }
    }
    // Fallback: Einzelne UID
    return currentNewReport?.study_instance_uid || examData?.study_instance_uid;
  }, [isMultiExam, combinedExams, currentNewReport?.study_instance_uid, examData?.study_instance_uid]);
  
  // Debug Log für Study Instance UID
  useEffect(() => {
    console.log('🔄 Study UID from currentNewReport/examData:', {
      hasExamData: !!examData,
      hasCurrentNewReport: !!currentNewReport,
      studyInstanceUID: realStudyInstanceUID,
      fromNewReport: currentNewReport?.study_instance_uid,
      fromExamData: examData?.study_instance_uid,
      accessionNumber: examData?.accession_number,
      examDataId: examData?.id
    });
    
    if (currentNewReport) {
      console.log('📋 Full currentNewReport object:', currentNewReport);
    }
  }, [examData, currentNewReport, realStudyInstanceUID]);

  // Sichere Viewer-Öffnung mit User Gesture Workaround - verwendet konfigurierte viewer_config
  const safeOpenStudyInViewer = (studyUID) => {
    console.log('🔧 DEBUG safeOpenStudyInViewer called:', {
      studyUID,
      currentUser: currentUser ? { id: currentUser.id, username: currentUser.username, viewer_preference: currentUser.viewer_preference } : null,
      viewerConfig: viewerConfig,
      isMultiExam: isMultiExam,
      combinedExams: combinedExams
    });
    
    // ============================================================================
    // MULTI-EXAM MODUS: Alle StudyInstanceUIDs mit Komma kombinieren
    // ============================================================================
    let finalStudyUID = studyUID;
    if (isMultiExam && combinedExams && combinedExams.length > 1) {
      // Sammle alle StudyInstanceUIDs aus den kombinierten Exams
      const allStudyUIDs = combinedExams
        .map(exam => exam.study_instance_uid)
        .filter(uid => uid);
      
      if (allStudyUIDs.length > 1) {
        finalStudyUID = allStudyUIDs.join(',');
        console.log('🔗 MULTI-EXAM: Kombinierte StudyInstanceUIDs:', finalStudyUID);
      }
    }
    
    if (!finalStudyUID) return;
    
    if (!currentUser || !currentUser.viewer_preference) {
      console.warn('User viewer preference not available for safeOpenStudyInViewer');
      return;
    }

    const userViewerType = currentUser.viewer_preference;
    console.log('🎯 User viewer type:', userViewerType);
    
    const viewerSetting = viewerConfig[userViewerType];
    console.log('⚙️ Viewer setting found:', viewerSetting);

    if (!viewerSetting) {
      console.warn(`Viewer setting for ${userViewerType} not found in safeOpenStudyInViewer`);
      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      console.warn(`No base URL configured in profile for viewer type: ${userViewerType} in safeOpenStudyInViewer`);
      return;
    }

    // Ersetze den Platzhalter durch die Study Instance UID (oder kombinierten String)
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', finalStudyUID);
    
    // ✅ Unterdrücke Browser-Warnung beim Öffnen des Viewers
    allowNavigationRef.current = true;
    console.log('🔓 Browser-Warnung für Viewer-Öffnung deaktiviert (safeOpenStudyInViewer)');
    
    // Für Client-Viewer: User Gesture Problem umgehen
    if (userViewerType === 'client') {
      // Erstelle einen unsichtbaren Link der geklickt werden kann
      const link = document.createElement('a');
      link.href = viewerUrl; // Verwende konfigurierte URL anstatt hardcodierte
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Simuliere User-Klick
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
        console.log('🔗 Client viewer opened with configured URL:', viewerUrl);
      }, 100);
    } else {
      // Für Web-Viewer: Normal öffnen (delegiere an openStudyInViewer für Konsistenz)
      openStudyInViewer(studyUID);
    }
    
    // 🔒 Nach 2 Sekunden Browser-Warnung wieder aktivieren
    setTimeout(() => {
      allowNavigationRef.current = false;
      console.log('🔒 Browser-Warnung wieder aktiviert');
    }, 2000);
  };

  // Automatisches Öffnen der Studie im Viewer beim Laden des Report Editors
  useEffect(() => {


    console.log('🔄 AUTO-VIEWER CHECK:');
    console.log('  hasExamData:', !!examData);
    console.log('  hasStudyUID:', !!realStudyInstanceUID);
    console.log('  hasUser:', !!currentUser);
    console.log('  hasPreference:', !!currentUser?.viewer_preference);
    console.log('  hasConfig:', !!(viewerConfig && Object.keys(viewerConfig).length > 0));
    console.log('  realStudyInstanceUID:', realStudyInstanceUID);
    console.log('  userPreference:', currentUser?.viewer_preference);
    console.log('  currentUser:', currentUser);
    console.log('  viewerConfig:', viewerConfig);
    
    if (currentNewReport) {
      console.log('  currentNewReport.study_instance_uid:', currentNewReport.study_instance_uid);
      console.log('  currentNewReport.id:', currentNewReport.id);
    } else {
      console.log('  currentNewReport: null');
    }
    
    if (examData) {
      console.log('  examData.study_instance_uid:', examData.study_instance_uid);
      console.log('  examData.id:', examData.id);
      console.log('  examData.accession_number:', examData.accession_number);
    } else {
      console.log('  examData: null');
    }
    
    if (examData && realStudyInstanceUID && currentUser && currentUser.viewer_preference && viewerConfig && Object.keys(viewerConfig).length > 0) {
      console.log('✅ All conditions met - starting viewer in 1 second...');
      console.log('📊 Using Study Instance UID from currentNewReport/examData:', realStudyInstanceUID);
      
      // Kleine Verzögerung für sicheres Laden
      setTimeout(() => {
        console.log('🚀 NOW calling safeOpenStudyInViewer with:', realStudyInstanceUID);
        safeOpenStudyInViewer(realStudyInstanceUID);
      }, 1000);
    } else {
      console.log('❌ Cannot auto-open viewer - missing conditions');
    }
  }, [examData, currentNewReport, realStudyInstanceUID, currentUser, viewerConfig]);

  // Keyboard Event Listener - aktiv bei strukturierten Templates und Freitext-Templates
  useEffect(() => {
    const handleKeyDown = (event) => {
      // STRG + Enter für Freitext-Templates (überall aktiv)
      if (event.ctrlKey && event.key === 'Enter' && selectedTemplate?.template_type === 'F') {
        event.preventDefault();
        event.stopPropagation(); // Verhindere mehrfache Events
        navigateToNextFreetextField();
        return;
      }
      
      // Nur bei strukturierten Templates aktiv (restliche Logik)
      if (!selectedTemplate || selectedTemplate.template_type !== 'S') return;

      const { key, target } = event;
      
      // Prüfe ob ein Input-Element fokussiert ist (für normale Texteingaben)
      const isInputFocused = target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true'
      );
      
      // Navigation zwischen Findings
      if (key === 'Enter') {
        // WICHTIG: Unterscheide zwischen normaler Enter-Taste und Numpad-Enter
        // - Normale Enter-Taste: event.code === 'Enter' 
        // - Numpad-Enter-Taste: event.code === 'NumpadEnter'
        
        if (event.code === 'NumpadEnter') {
          // Numpad-Enter: Immer navigieren (auch in mehrzeiligen Textfeldern)
          event.preventDefault();
          navigateToNextFinding();
        } else if (event.code === 'Enter') {
          // Normale Enter-Taste: nur navigieren bei einzeiligen Inputs oder außerhalb von Inputs
          if (isInputFocused && target.tagName === 'INPUT') {
            // Einzeiliges Input-Feld: Navigation
            event.preventDefault();
            navigateToNextFinding();
          } else if (isInputFocused && target.tagName === 'TEXTAREA') {
            // Mehrzeiliges Textarea: normale Enter-Funktion (neue Zeile)
            // Kein preventDefault() - lasse normale Enter-Funktion zu
          } else if (isInputFocused && target.contentEditable === 'true') {
            // RichTextField (contentEditable): Prüfe ob Snippet gerade eingefügt wurde
            if (!snippetJustInserted) {
              // Nur navigieren wenn kein Snippet gerade eingefügt wurde
              const isMultiline = target.closest('[data-multiline="true"]') || 
                                target.style.maxHeight !== '40px';
              if (!isMultiline) {
                // Einzeiliges RichTextField: Navigation
                event.preventDefault();
                navigateToNextFinding();
              }
            }
          } else if (!isInputFocused) {
            // Enter außerhalb von Inputs: Navigation
            event.preventDefault();
            navigateToNextFinding();
          }
        }
      } else if (key === 'ArrowDown') {
        // Pfeil nach unten: Navigation (außerhalb von Inputs oder bei einzeiligen contentEditable)
        const shouldNavigate = !isInputFocused || 
          (isInputFocused && target.contentEditable === 'true' && 
           target.getAttribute('data-multiline') === 'false');
        
        if (shouldNavigate) {
          event.preventDefault();
          navigateToNextFinding();
        }
      } else if (key === 'ArrowUp') {
        // Pfeil nach oben: Navigation (außerhalb von Inputs oder bei einzeiligen contentEditable)
        const shouldNavigate = !isInputFocused || 
          (isInputFocused && target.contentEditable === 'true' && 
           target.getAttribute('data-multiline') === 'false');
        
        if (shouldNavigate) {
          event.preventDefault();
          navigateToPreviousFinding();
        }
      } else if (key >= '1' && key <= '9') {
        // Numpad-Shortcuts nur wenn KEIN Input-Element fokussiert ist
        if (!isInputFocused) {
          event.preventDefault();
          selectFindingOption(parseInt(key));
        }
        // Sonst normale Zahleneingabe zulassen
      }
    };

    // Event-Capturing verwenden, um Events vor Input-Feldern abzufangen
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [focusedFindingIndex, selectedTemplate, navigateToNextFreetextField, navigateToNextFinding, navigateToPreviousFinding, selectFindingOption, snippetJustInserted]);



  const handleInlineCommentCreate = () => {
    if (selectedText) {
      const dialogData = {
        open: true,
        text: selectedText.text,
        position: {
          start: selectedText.startOffset,
          end: selectedText.endOffset,
          context: selectedText.containerText.substring(
            Math.max(0, selectedText.startOffset - 20),
            selectedText.endOffset + 20
          )
        }
      };
      setInlineCommentDialog(dialogData);
      setTooltipOpen(false);
    }
  };

  const handleInlineCommentSave = () => {
    if (!newInlineComment.trim() || !inlineCommentDialog.position) return;
    
    const newComment = {
      name: currentUser?.username || t('reportEditor.defaults.currentUser'),
      date: format(new Date(), 'dd.MM.yyyy', { locale: de }),
      time: format(new Date(), 'HH:mm', { locale: de }),
      comment: newInlineComment.trim(),
      position: inlineCommentDialog.position,
      text: inlineCommentDialog.text
    };
    
    setInlineComments(prev => [...prev, newComment]);
    setNewInlineComment('');
    setInlineCommentDialog({ open: false, text: '', position: null });
    
    // Selection zurücksetzen
    window.getSelection().removeAllRanges();
    setSelectedText(null);
  };

  // Globaler STRG+Enter Event-Listener für Inline-Kommentar Dialog
  useEffect(() => {
    if (!inlineCommentDialog.open) return;

    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'Enter') {
        console.log('🔥 GLOBALER STRG+Enter detected!', {
          commentText: newInlineComment,
          trimmed: newInlineComment.trim(),
          isEmpty: !newInlineComment.trim(),
          dialogOpen: inlineCommentDialog.open
        });
        e.preventDefault();
        e.stopPropagation();
        
        // Nur absenden wenn Kommentar nicht leer ist
        if (newInlineComment.trim()) {
          console.log('✅ Submitting inline comment via global handler...');
          handleInlineCommentSave();
        } else {
          console.log('❌ Comment is empty, not submitting');
        }
      }
    };

    // Event-Listener am Document-Level
    document.addEventListener('keydown', handleKeyDown, true);
    console.log('🎯 Global STRG+Enter listener added for inline comment dialog');
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      console.log('🗑️ Global STRG+Enter listener removed');
    };
  }, [inlineCommentDialog.open, newInlineComment, handleInlineCommentSave]);



  const handleInlineCommentCancel = () => {
    setInlineCommentDialog({ open: false, text: '', position: null });
    setNewInlineComment('');
    setTooltipOpen(false);
    setSelectedText(null);
    window.getSelection().removeAllRanges();
  };

  // Ref für Debounce-Timer
  const renderTimerRef = useRef(null);

  // Funktion um alle Inline-Kommentare aus dem State ins DOM zu rendern
  const renderAllInlineComments = (commentsToRender = inlineComments) => {
    if (!commentsToRender || commentsToRender.length === 0) return;
    
    // Debounce: Verhindere mehrfache simultane Aufrufe
    if (renderTimerRef.current) {
      clearTimeout(renderTimerRef.current);
    }
    


    // Warte kurz bis DOM bereit ist und verwende debounced execution
    renderTimerRef.current = setTimeout(() => {
      const contentDivs = document.querySelectorAll('.report-editor-content[contenteditable="true"]');
      
      contentDivs.forEach((contentDiv, divIndex) => {
        
        commentsToRender.forEach((comment, commentIndex) => {
          const commentId = `-${commentIndex}`;
          
          // Prüfe ob dieser Kommentar bereits im DOM existiert
          const existingIcon = contentDiv.querySelector(`[data-comment-id="${commentId}"]`);
          if (existingIcon) {
            return;
          }
          
          const commentText = comment.text;
          if (!commentText) return;
          
          // Prüfe ob der Text bereits als highlighted span existiert
          const existingHighlights = contentDiv.querySelectorAll(`.comment-highlighted`);
          const alreadyHighlighted = Array.from(existingHighlights).some(highlight => 
            highlight.textContent === commentText
          );
          if (alreadyHighlighted) return;
          
          // Prüfe ob der Text als plain text vorhanden ist
          const textContent = contentDiv.textContent || contentDiv.innerText;
          if (textContent.includes(commentText)) {
            // Erstelle HTML für Highlighting und Icon (mit data-tooltip für MUI-Style Tooltip)
            const tooltipText = t('reportEditor.toolbar.showComment');
            const highlightHtml = `<span class="comment-highlighted">${commentText}</span><span class="inline-comment-icon" data-comment-id="${commentId}" data-tooltip="${tooltipText}" contenteditable="false" unselectable="on" style="margin-left: 2px; cursor: pointer; font-size: 18px; font-weight: bold; user-select: none; position: relative; z-index: 1000;">ℹ️</span>`;
            
            // Ersetze den Text mit dem highlighted HTML
            let currentHtml = contentDiv.innerHTML;
            const updatedHtml = currentHtml.replace(commentText, highlightHtml);
            
            if (updatedHtml !== currentHtml) {
              contentDiv.innerHTML = updatedHtml;
            }
          }
        });
      });
      
      // Timer zurücksetzen nach Ausführung
      renderTimerRef.current = null;
    }, 500); // Längere Wartezeit für Template-Rendering
  };

  // Funktion zum Bereinigen von HTML vor dem Speichern (entfernt Inline Comment Markups)
  const cleanHtmlForSaving = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Erstelle temporäres DOM-Element zum Säubern
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Entferne alle Inline Comment Icons
    const commentIcons = tempDiv.querySelectorAll('.inline-comment-icon');
    commentIcons.forEach(icon => icon.remove());
    
    // Entferne Comment-Highlighting, aber behalte den Text
    const highlightedElements = tempDiv.querySelectorAll('.comment-highlighted');
    highlightedElements.forEach(element => {
      // Ersetze das highlight-span durch seinen Textinhalt
      const textNode = document.createTextNode(element.textContent);
      element.parentNode.replaceChild(textNode, element);
    });
    
    return tempDiv.innerHTML;
  };

  // Snippet-Funktionen
  const openSnippetDialog = (cursorPos) => {
    setSnippetCursorPosition(cursorPos);
    setSnippetDialogOpen(true);
  };

  const closeSnippetDialog = () => {
    // Restore focus to the editor element before closing
    if (snippetInsertionPoint?.element) {
      setTimeout(() => {
        snippetInsertionPoint.element.focus();
      }, 50); // Small delay to ensure dialog is fully closed
    }
    
    setSnippetDialogOpen(false);
    setSnippetCursorPosition(null);
    setSnippetInsertionPoint(null);
  };

  const handleSnippetSelect = (snippet) => {
    if (snippetInsertionPoint && snippet) {
      // Set flag to prevent navigation on next Enter
      setSnippetJustInserted(true);
      
      // Insert snippet content at the saved insertion point
      const { element, range } = snippetInsertionPoint;
      
      try {
        // Insert snippet content directly (no @ character to remove since we prevented it)
        const textNode = document.createTextNode(snippet.content);
        range.insertNode(textNode);
        
        // Position cursor after inserted text
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Trigger onChange to update parent state
        
        // Find the corresponding onChange handler based on the element
        if (element.closest('.rich-text-field')) {
          // This is a RichTextField, trigger its onChange
          const changeEvent = new Event('input', { bubbles: true });
          element.dispatchEvent(changeEvent);
        }
        
        // Ensure focus stays on the editor element
        element.focus();
        
        // Clear flag after a short delay
        setTimeout(() => {
          setSnippetJustInserted(false);
        }, 100);
        
      } catch (error) {
        console.error('Error inserting snippet:', error);
        // Fallback: insert at current cursor position
        try {
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const textNode = document.createTextNode(snippet.content);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.setEndAfter(textNode);
            selection.removeAllRanges();
            selection.addRange(range);
          }
          // Try to focus the original element if available
          if (element) {
            element.focus();
          }
        } catch (fallbackError) {
          console.error('Fallback snippet insertion failed:', fallbackError);
        }
        
        // Clear flag even on error
        setTimeout(() => {
          setSnippetJustInserted(false);
        }, 100);
      }
    }
    
    closeSnippetDialog();
  };

  // Funktion zum Anzeigen von Inline-Kommentaren für einen Text
  const renderTextWithInlineComments = (text, fieldKey = '') => {
    if (!text || typeof text !== 'string') return text;
    
    // Finde alle Inline-Kommentare für diesen Text
    const relevantComments = inlineComments.filter(comment => 
      comment.text && text.includes(comment.text)
    );
    
    if (relevantComments.length === 0) {
      return text;
    }
    
    // Sortiere Kommentare nach Position (von hinten nach vorne für korrekte Insertion)
    const sortedComments = [...relevantComments].sort((a, b) => 
      (b.position.end || 0) - (a.position.end || 0)
    );
    
    // HTML-aware Verarbeitung: Parse HTML in temporäres DOM Element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    
    sortedComments.forEach((comment, index) => {
      const commentId = `${fieldKey}-${index}`;
      
      // Durchsuche alle Text-Nodes im DOM
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }
      
      // Finde Text-Node der den Kommentar-Text enthält
      for (let textNode of textNodes) {
        const nodeValue = textNode.nodeValue;
        const textIndex = nodeValue.indexOf(comment.text);
        
        if (textIndex !== -1) {
          // Prüfe ob der Text bereits kommentiert ist (vermeide Duplikate)
          if (textNode.parentNode && textNode.parentNode.classList && 
              textNode.parentNode.classList.contains('comment-highlighted')) {
            continue;
          }
          
          // Teile den Text-Node in drei Teile
          const beforeText = nodeValue.substring(0, textIndex);
          const commentText = nodeValue.substring(textIndex, textIndex + comment.text.length);
          const afterText = nodeValue.substring(textIndex + comment.text.length);
          
          // Erstelle neuen HTML-Content für diesen Abschnitt
          const highlightSpan = document.createElement('span');
          highlightSpan.className = 'comment-highlighted';
          highlightSpan.textContent = commentText;
          
          // Erstelle Icon-Element
          const iconSpan = document.createElement('span');
          iconSpan.className = 'inline-comment-icon';
          iconSpan.setAttribute('data-comment-id', commentId);
          iconSpan.setAttribute('contenteditable', 'false'); // Icon ist nicht editierbar
          iconSpan.setAttribute('unselectable', 'on'); // IE Schutz
          iconSpan.style.marginLeft = '2px';
          iconSpan.style.cursor = 'pointer';
          iconSpan.style.fontSize = '18px';
          iconSpan.style.fontWeight = 'bold';
          iconSpan.style.userSelect = 'none'; // Icon kann nicht ausgewählt werden
          iconSpan.style.webkitUserSelect = 'none'; // Safari
          iconSpan.style.mozUserSelect = 'none'; // Firefox
          iconSpan.style.msUserSelect = 'none'; // IE
          iconSpan.style.position = 'relative';
          iconSpan.style.zIndex = '1000';
          iconSpan.setAttribute('data-tooltip', t('reportEditor.toolbar.showComment')); // MUI-Style Tooltip
          iconSpan.textContent = 'ℹ️';
          
          // Event-Handler um Löschen zu verhindern
          iconSpan.addEventListener('keydown', (e) => {
            // Verhindere Delete, Backspace und andere gefährliche Tasten
            if (e.key === 'Delete' || e.key === 'Backspace' || e.key === 'Cut') {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          });
          
          iconSpan.addEventListener('beforeinput', (e) => {
            // Verhindere alle Input-Events
            e.preventDefault();
            e.stopPropagation();
            return false;
          });
          
          iconSpan.addEventListener('input', (e) => {
            // Verhindere alle Input-Events
            e.preventDefault();
            e.stopPropagation();
            return false;
          });
          
          // Ersetze den ursprünglichen Text-Node
          const parentNode = textNode.parentNode;
          
          // Füge "before" Text hinzu (falls vorhanden)
          if (beforeText) {
            parentNode.insertBefore(document.createTextNode(beforeText), textNode);
          }
          
          // Füge highlight span hinzu
          parentNode.insertBefore(highlightSpan, textNode);
          
          // Füge icon span hinzu
          parentNode.insertBefore(iconSpan, textNode);
          
          // Füge "after" Text hinzu (falls vorhanden)
          if (afterText) {
            parentNode.insertBefore(document.createTextNode(afterText), textNode);
          }
          
          // Entferne den ursprünglichen Text-Node
          parentNode.removeChild(textNode);
          
          break; // Nur erste Übereinstimmung pro Kommentar
        }
      }
    });
    
    return tempDiv.innerHTML;
  };

  const handleInlineCommentIconClick = (commentId, event) => {
    // Finde den Kommentar basierend auf der ID
    const [fieldKey, index] = commentId.split('-');
    const comment = inlineComments[parseInt(index)];
    
    if (comment && event) {
      // Öffne Tooltip mit Kommentar-Details
      setCommentTooltip({
        open: true,
        comment: comment,
        anchorEl: event.target
      });
    }
  };
  
  // Callbacks für Inline Comment Icon Tooltip (MUI Tooltip)
  const handleCommentIconTooltipShow = (tooltipData) => {
    setCommentIconTooltip(tooltipData);
  };
  
  const handleCommentIconTooltipHide = () => {
    setCommentIconTooltip({ open: false, anchorEl: null, text: '' });
  };

  // Funktion zum Behandeln gelöschter Comment-Icons
  const handleCommentIconsChanged = (remainingCommentIds) => {
    // Finde heraus, welche Kommentare noch im Text vorhanden sind
    const validComments = inlineComments.filter((comment, index) => {
      // Überprüfe alle möglichen ID-Formate für diesen Kommentar
      const possibleIds = [
        `${index}`, 
        `0-${index}`, 
        `field-${index}`,
        `content-${index}`
      ];
      
      return possibleIds.some(id => remainingCommentIds.includes(id));
    });
    
    // Update nur wenn sich etwas geändert hat
    if (validComments.length !== inlineComments.length) {

      setInlineComments(validComments);
      
      // Tooltip schließen falls es für einen gelöschten Kommentar offen ist
      if (commentTooltip.open) {
        setCommentTooltip({ open: false, comment: null, anchorEl: null });
      }
    }
  };

  // Rich Text Formatierungsfunktionen
  const applyFormat = (command, value = null) => {
    // Sichere moderne Formatierung mit try-catch
    try {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;
      
      // Für Enter/Zeilenumbrüche
      if (command === 'insertLineBreak') {
        const range = selection.getRangeAt(0);
        const br = document.createElement('br');
        range.deleteContents();
        range.insertNode(br);
        
        // Cursor nach dem <br> positionieren
        range.setStartAfter(br);
        range.setEndAfter(br);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      
      // Führe Formatierungsbefehl aus
      document.execCommand(command, false, value);
      
    } catch (error) {
      console.warn(t('reportEditor.errors.formattingFailed'), error);
    }
  };

  const handleBold = () => {
    document.execCommand('bold', false, null);
    // Toolbar bleibt offen für weitere Formatierungen
  };

  const handleItalic = () => {
    document.execCommand('italic', false, null);
    // Toolbar bleibt offen für weitere Formatierungen
  };

  const handleUnderline = () => {
    document.execCommand('underline', false, null);
    // Toolbar bleibt offen für weitere Formatierungen
  };

  const handleFontSizeChange = (newSize) => {
    console.log('📝 FONT-SIZE DEBUG - Real size selected:', newSize);
    
    // Echte Größe wird ausgewählt, aber mit +2 im Editor angezeigt
    const displaySize = newSize + 2; // Größer für bessere Lesbarkeit
    console.log('📝 FONT-SIZE DEBUG - Display size for editor:', displaySize);
    
    setFontSize(newSize); // Echte Größe für Toolbar-State
    const selection = window.getSelection();
    
    console.log('📝 FONT-SIZE DEBUG - Selection:', {
      rangeCount: selection.rangeCount,
      toString: selection.toString().substring(0, 50) + '...',
      anchorNode: selection.anchorNode?.nodeName,
      focusNode: selection.focusNode?.nodeName
    });
    
    if (selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed) {
      const range = selection.getRangeAt(0);
      
      console.log('📝 FONT-SIZE DEBUG - Range:', {
        collapsed: range.collapsed,
        startContainer: range.startContainer?.nodeName,
        endContainer: range.endContainer?.nodeName,
        commonAncestorContainer: range.commonAncestorContainer?.nodeName
      });
      
      // INTELLIGENTE DETECTION: Prüfe ob WIRKLICH alles markiert ist
      const selectionText = selection.toString();
      
      // Finde das aktuelle Feld
      const currentField = range.commonAncestorContainer.closest ? 
        range.commonAncestorContainer.closest('[contenteditable="true"]') :
        document.querySelector('#field-befund'); // Fallback
        
      const fieldTotalText = currentField ? currentField.textContent : '';
      const selectionRatio = selectionText.length / fieldTotalText.length;
      
      // Verbesserte Heuristik: 90%+ des Feldes markiert = "alles markiert"
      const isLargeSelection = selectionRatio >= 0.9;
      
      console.log('📝 FONT-SIZE DEBUG - Selection analysis:', {
        selectionLength: selectionText.length,
        fieldTotalLength: fieldTotalText.length,
        selectionRatio: selectionRatio.toFixed(2),
        isLargeSelection: isLargeSelection,
        preview: selectionText.substring(0, 50) + '...'
      });
      
      let allSpansInSelection = [];
      
      if (isLargeSelection) {
        // OPTION A: Große Selection = alle spans im Feld ändern
        const currentField = range.commonAncestorContainer.closest ? 
          range.commonAncestorContainer.closest('[contenteditable="true"]') :
          document.querySelector('#field-befund'); // Fallback
          
        if (currentField) {
          allSpansInSelection = Array.from(currentField.querySelectorAll('span[style*="font-size"]'));
          console.log('📝 FONT-SIZE DEBUG - OPTION A: Alle spans im Feld gefunden:', allSpansInSelection.length);
        }
      } else {
        // OPTION B: Kleine Selection = PRÄZISE spans in Selection
        console.log('📝 FONT-SIZE DEBUG - OPTION B: Analyzing precise selection...');
        
        // Klone den Inhalt der Selection
        const clonedContents = range.cloneContents();
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(clonedContents);
        
        // Finde spans im geklonten Inhalt
        const spansInClonedContent = tempDiv.querySelectorAll('span[style*="font-size"]');
        console.log('📝 FONT-SIZE DEBUG - Spans in cloned content:', spansInClonedContent.length);
        
        // Wenn spans im geklonten Inhalt gefunden wurden, finde die entsprechenden Original-spans
        if (spansInClonedContent.length > 0) {
          spansInClonedContent.forEach((clonedSpan, index) => {
            const spanText = clonedSpan.textContent;
            const spanFontSize = clonedSpan.style.fontSize;
            
            console.log(`📝 CLONED SPAN ${index + 1}:`, {
              fontSize: spanFontSize,
              textPreview: spanText.substring(0, 30) + '...',
              textLength: spanText.length
            });
            
            // Finde den entsprechenden Original-span im DOM
            const currentField = range.commonAncestorContainer.closest ? 
              range.commonAncestorContainer.closest('[contenteditable="true"]') :
              document.querySelector('#field-befund');
              
            if (currentField) {
              const allFieldSpans = currentField.querySelectorAll('span[style*="font-size"]');
              
              // Finde Match basierend auf Text-Inhalt und Font-Size
              const matchingSpan = Array.from(allFieldSpans).find(originalSpan => {
                return originalSpan.textContent.includes(spanText.trim()) && 
                       originalSpan.style.fontSize === spanFontSize &&
                       spanText.trim().length > 0; // Ignoriere leere spans
              });
              
              if (matchingSpan && !allSpansInSelection.includes(matchingSpan)) {
                allSpansInSelection.push(matchingSpan);
                console.log(`📝 MATCHED ORIGINAL SPAN:`, {
                  fontSize: matchingSpan.style.fontSize,
                  textPreview: matchingSpan.textContent.substring(0, 30) + '...'
                });
              }
            }
          });
        } else {
          console.log('📝 FONT-SIZE DEBUG - No spans in cloned content, fallback to TreeWalker');
          // Fallback to TreeWalker if cloning doesn't work
          const walker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_ELEMENT,
            {
              acceptNode: function(node) {
                if (node.nodeName === 'SPAN' && node.style?.fontSize && range.intersectsNode(node)) {
                  return NodeFilter.FILTER_ACCEPT;
                }
                return NodeFilter.FILTER_REJECT;
              }
            }
          );
          
          let currentNode;
          while (currentNode = walker.nextNode()) {
            allSpansInSelection.push(currentNode);
          }
        }
        
        console.log('📝 FONT-SIZE DEBUG - OPTION B: Final spans gefunden:', allSpansInSelection.length);
      }
      
      console.log('📝 FONT-SIZE DEBUG - FINALE spans für Update:', allSpansInSelection.length);
      allSpansInSelection.forEach((span, index) => {
        console.log(`📝 SPAN ${index + 1} for Update:`, {
          fontSize: span.style.fontSize,
          textPreview: span.textContent.substring(0, 30) + '...',
          textLength: span.textContent.length
        });
      });
      
      if (allSpansInSelection.length > 0) {
        console.log('📝 FONT-SIZE DEBUG - UPDATING ALLE font spans in selection:', {
          count: allSpansInSelection.length,
          realSize: `${newSize}px`,
          displaySize: `${displaySize}px`,
          isLargeSelection: isLargeSelection
        });
        
        // Update ALLE font-size spans in der Selection
        let updatedCount = 0;
        allSpansInSelection.forEach((span, index) => {
          const oldSize = span.style.fontSize;
          const needsUpdate = oldSize !== `${displaySize}px` || isLargeSelection; // Bei großer Selection: IMMER updaten
          
          if (needsUpdate) {
            span.style.fontSize = `${displaySize}px`; // Größere Display-Size für Editor
            
            // 🔧 FIX: ENTFERNE VERSCHACHTELTE SPANS MIT FONT-SIZE
            const nestedSpans = span.querySelectorAll('span[style*="font-size"]');
            nestedSpans.forEach(nestedSpan => {
              console.log(`📝 REMOVING nested font-size from span: ${nestedSpan.style.fontSize} → inherit`, {
                preview: nestedSpan.textContent.substring(0, 30) + '...'
              });
              // Entferne nur font-size, behalte andere styles
              const styleWithoutFontSize = nestedSpan.style.cssText.replace(/font-size:\s*[^;]*;?\s*/g, '');
              nestedSpan.style.cssText = styleWithoutFontSize;
            });
            
            updatedCount++;
            
            console.log(`📝 UPDATED SPAN ${index + 1}: ${oldSize} → ${displaySize}px (Real: ${newSize}px)`, {
              preview: span.textContent.substring(0, 30) + '...',
              forced: isLargeSelection && oldSize === `${displaySize}px`,
              removedNestedSpans: nestedSpans.length
            });
          } else {
            console.log(`📝 SKIPPED SPAN ${index + 1}: Already ${displaySize}px`, {
              preview: span.textContent.substring(0, 30) + '...'
            });
          }
        });
        
        console.log('📝 FONT-SIZE DEBUG - ✅ Successfully updated spans:', {
          totalUpdated: updatedCount,
          totalScanned: allSpansInSelection.length,
          realSize: `${newSize}px`,
          displaySize: `${displaySize}px`,
          forceMode: isLargeSelection
        });
        
        // Keep selection as it is (user had everything selected)
        console.log('📝 FONT-SIZE DEBUG - Keeping original selection (user selected everything)');
        
        // CLEANUP: Verschachtelte font-size spans bereinigen
        if (isLargeSelection && updatedCount > 0) {
          console.log('📝 FONT-SIZE DEBUG - Cleaning up nested spans...');
          setTimeout(() => {
            const currentField = document.querySelector('#field-befund');
            if (currentField) {
              cleanupNestedFontSizeSpans(currentField);
            }
          }, 100);
        }
        
        // Trigger auto-save after multi-span update
        if (updatedCount > 0) {
          console.log('📝 FONT-SIZE DEBUG - Triggering silent auto-save after multi-span update...');
          setTimeout(() => {
            if (window.reportEditorAutoSave) {
              window.reportEditorAutoSave();
            }
          }, 500);
        }
        
      } else {
        console.log('📝 FONT-SIZE DEBUG - Creating new font-size span');
        
        // Create new font-size span (improved method without execCommand)
        try {
          const selectedContent = range.extractContents();
          const fontSpan = document.createElement('span');
          fontSpan.style.fontSize = `${displaySize}px`; // Größere Display-Size für Editor
          
          fontSpan.appendChild(selectedContent);
          range.insertNode(fontSpan);
          
          console.log('📝 FONT-SIZE DEBUG - Created new span with font-size:', `${displaySize}px (Real: ${newSize}px)`);
          
          // Set selection to new span
          const newRange = document.createRange();
          newRange.selectNodeContents(fontSpan);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          console.log('📝 FONT-SIZE DEBUG - Updated selection to new font span');
          
        } catch (error) {
          console.error('📝 FONT-SIZE DEBUG - Error occurred:', error);
          
          // Fallback to old method if new method fails
          console.log('📝 FONT-SIZE DEBUG - Falling back to execCommand method');
          document.execCommand('fontSize', false, '3');
          
          const selectedElements = document.querySelectorAll('font[size="3"]');
          selectedElements.forEach(el => {
            const span = document.createElement('span');
            span.style.fontSize = `${displaySize}px`; // Größere Display-Size für Editor
            span.innerHTML = el.innerHTML;
            el.parentNode.replaceChild(span, el);
          });
        }
      }
    } else {
      console.log('📝 FONT-SIZE DEBUG - No valid selection for font size change');
    }
    
  console.log('📝 FONT-SIZE DEBUG - Font size change completed for:', newSize);
  
  // ZUSÄTZLICHE ANALYSE: Alle font-size spans im aktuellen Editor finden
  setTimeout(() => {
    const befundField = document.querySelector('#field-befund');
    if (befundField) {
      const allFontSizeSpans = befundField.querySelectorAll('span[style*="font-size"]');
      console.log('📝 FONT-SIZE DEBUG - ANALYSE: Alle font-size spans im Befund-Feld:');
      console.log(`📝 FONT-SIZE DEBUG - Gefunden: ${allFontSizeSpans.length} spans mit font-size`);
      
      const fontSizeCount = {};
      allFontSizeSpans.forEach((span, index) => {
        const fontSize = span.style.fontSize;
        const textContent = span.textContent.substring(0, 50) + '...';
        
        console.log(`📝 SPAN ${index + 1}:`, {
          fontSize: fontSize,
          textLength: span.textContent.length,
          preview: textContent,
          outerHTML: span.outerHTML.substring(0, 100) + '...'
        });
        
        fontSizeCount[fontSize] = (fontSizeCount[fontSize] || 0) + 1;
      });
      
      console.log('📝 FONT-SIZE DEBUG - ZUSAMMENFASSUNG:');
      Object.entries(fontSizeCount).forEach(([size, count]) => {
        console.log(`📝   ${size}: ${count} spans`);
      });
    } else {
      console.log('📝 FONT-SIZE DEBUG - Befund-Feld (#field-befund) nicht gefunden');
    }
  }, 100);
  
    // Toolbar bleibt offen für weitere Formatierungen
  };

// 🔧 HELPER FUNKTION: Verschachtelte font-size spans bereinigen
const cleanupNestedFontSizeSpans = (container) => {
  console.log('🧹 CLEANUP - Starting nested font-size span cleanup');
  
  let cleanupCount = 0;
  const allFontSizeSpans = Array.from(container.querySelectorAll('span[style*="font-size"]'));
  
  allFontSizeSpans.forEach((span, index) => {
    try {
      // Prüfe ob span noch im DOM ist
      if (!span.parentNode) {
        console.log(`🧹 CLEANUP - Span ${index + 1} already removed, skipping`);
        return;
      }
      
      // Suche nach direkten child spans mit font-size
      const directChildFontSpans = Array.from(span.children).filter(child => 
        child.nodeName === 'SPAN' && child.style?.fontSize
      );
      
      directChildFontSpans.forEach(childSpan => {
        try {
          // ERWEITERTE CLEANUP: Auch spans mit verschiedenen font-sizes mergen
          if (childSpan.style.fontSize === span.style.fontSize) {
            console.log(`🧹 CLEANUP - Merging nested span with SAME font-size: ${span.style.fontSize}`);
            
            // Ersetze den child span durch seinen Inhalt
            const childContent = childSpan.innerHTML;
            childSpan.outerHTML = childContent;
            cleanupCount++;
            
          } else if (childSpan.style.fontSize && span.style.fontSize) {
            console.log(`🧹 CLEANUP - Found nested span with DIFFERENT font-size: ${span.style.fontSize} → ${childSpan.style.fontSize}`);
            
            // Bei verschiedenen font-sizes: Child gewinnt (neuere Änderung)
            console.log(`🧹 CLEANUP - Child font-size (${childSpan.style.fontSize}) overrides parent (${span.style.fontSize})`);
            
            // Setze parent font-size auf child font-size
            span.style.fontSize = childSpan.style.fontSize;
            
            // Entferne das redundante child span
            const childContent = childSpan.innerHTML;
            childSpan.outerHTML = childContent;
            
            cleanupCount++;
            console.log(`🧹 CLEANUP - Updated parent to ${span.style.fontSize} and merged child`);
          }
        } catch (childError) {
          console.warn('🧹 CLEANUP - Error processing child span:', childError);
        }
      });
      
    } catch (spanError) {
      console.warn('🧹 CLEANUP - Error processing span:', spanError);
    }
  });
  
  console.log(`🧹 CLEANUP - Finished: ${cleanupCount} nested spans merged`);
  return cleanupCount;
};

// 🔧 HELPER FUNKTION: Alle font-size spans in einem Feld ändern
const changeAllFontSizesInField = (fieldId, newSize) => {
  console.log('🔧 BULK FONT-SIZE - Changing all spans in field:', fieldId, 'to size:', newSize);
  
  const field = document.querySelector(`#${fieldId}`);
  if (!field) {
    console.log('🔧 BULK FONT-SIZE - Field not found:', fieldId);
    return;
  }
  
  const displaySize = newSize + 2; // Real → Display size conversion
  const allFontSizeSpans = field.querySelectorAll('span[style*="font-size"]');
  console.log(`🔧 BULK FONT-SIZE - Found ${allFontSizeSpans.length} spans with font-size (Real: ${newSize}px → Display: ${displaySize}px)`);
  
  let changedCount = 0;
  allFontSizeSpans.forEach((span, index) => {
    const oldSize = span.style.fontSize;
    span.style.fontSize = `${displaySize}px`; // Größere Display-Size für Editor
    changedCount++;
    
    console.log(`🔧 BULK SPAN ${index + 1}: ${oldSize} → ${displaySize}px (Real: ${newSize}px)`, {
      preview: span.textContent.substring(0, 30) + '...'
    });
  });
  
  console.log(`🔧 BULK FONT-SIZE - Successfully changed ${changedCount} spans to ${displaySize}px (Real: ${newSize}px)`);
  
  // Trigger auto-save after bulk change
  if (changedCount > 0 && window.reportEditorAutoSave) {
    console.log('🔧 BULK FONT-SIZE - Triggering silent auto-save...');
    setTimeout(() => {
      window.reportEditorAutoSave();
    }, 500);
  }
  
  // Automatic cleanup after bulk change
  if (changedCount > 0) {
    console.log('🔧 BULK FONT-SIZE - Running automatic cleanup...');
    setTimeout(() => {
      const cleanupResult = cleanupNestedFontSizeSpans(field);
      console.log(`🔧 BULK FONT-SIZE - Cleanup completed: ${cleanupResult} spans merged`);
    }, 200);
  }
};

  // Hilfsfunktion zum Speichern der Selection
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      return {
        startContainer: range.startContainer,
        startOffset: range.startOffset,
        endContainer: range.endContainer,
        endOffset: range.endOffset,
        collapsed: range.collapsed
      };
    }
    return null;
  };

  // Hilfsfunktion zum Wiederherstellen der Selection
  const restoreSelection = (savedRange) => {
    if (savedRange && !savedRange.collapsed) {
      const selection = window.getSelection();
      const range = document.createRange();
      try {
        range.setStart(savedRange.startContainer, savedRange.startOffset);
        range.setEnd(savedRange.endContainer, savedRange.endOffset);
        selection.removeAllRanges();
        selection.addRange(range);
        return true;
      } catch (error) {
        console.warn('Could not restore selection:', error);
        return false;
      }
    }
    return false;
  };

  const handleColorChange = (color) => {
    const colorValue = color.hex || color; // react-color gibt ein Objekt zurück, normaler String für direkte Werte
    
    if (colorPickerMode === 'palette') {
      // Bei Palette: Sofort anwenden und schließen
      setFontColor(colorValue);
      
      // Stelle gespeicherte Selection wieder her und wende Farbe an
      if (savedSelection && restoreSelection(savedSelection)) {
        document.execCommand('foreColor', false, colorValue);
      }
      
      setColorPickerOpen(false);
      setSavedSelection(null);
    }
    // Bei Spectrum: Nur temporär setzen, nicht sofort anwenden
  };

  // Separate Funktion für Spectrum Mode - nur temporäre Änderungen
  const handleSpectrumColorChange = (color) => {
    setTempColor(color.hex);
  };

  // Apply-Funktion für Spectrum Mode
  const handleApplyColor = () => {
    setFontColor(tempColor);
    
    // Stelle gespeicherte Selection wieder her und wende Farbe an
    if (savedSelection && restoreSelection(savedSelection)) {
      document.execCommand('foreColor', false, tempColor);
    }
    
    setColorPickerOpen(false);
    setSavedSelection(null);
  };

  const handleAlign = (alignment) => {
    console.log('🎯 ALIGN DEBUG - Alignment requested:', alignment);
    
    const selection = window.getSelection();
    console.log('🎯 ALIGN DEBUG - Selection:', {
      rangeCount: selection.rangeCount,
      toString: selection.toString(),
      anchorNode: selection.anchorNode?.nodeName,
      focusNode: selection.focusNode?.nodeName
    });
    
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      console.log('🎯 ALIGN DEBUG - Range:', {
        collapsed: range.collapsed,
        startContainer: range.startContainer?.nodeName,
        endContainer: range.endContainer?.nodeName,
        commonAncestorContainer: range.commonAncestorContainer?.nodeName
      });
      
      // Erstelle einen div-Wrapper nur für den markierten Text
      if (!range.collapsed) {
        try {
          // Debug: Schaue dir den aktuellen HTML-Inhalt an
          const selectedHTML = range.cloneContents();
          const tempDiv = document.createElement('div');
          tempDiv.appendChild(selectedHTML);
          console.log('🎯 ALIGN DEBUG - Selected HTML before:', tempDiv.innerHTML);
          
          // Check if selection is already in an aligned div
          let parentElement = range.commonAncestorContainer;
          if (parentElement.nodeType === Node.TEXT_NODE) {
            parentElement = parentElement.parentElement;
          }
          
          console.log('🎯 ALIGN DEBUG - Parent element:', {
            nodeName: parentElement?.nodeName,
            className: parentElement?.className,
            currentTextAlign: getComputedStyle(parentElement)?.textAlign,
            style: parentElement?.style?.textAlign
          });
          
          // VERBESSERTE DETECTION: Suche nach existierenden alignment divs
          let existingAlignDiv = null;
          let checkElement = range.startContainer;
          
          // Traverse upwards to find alignment div
          while (checkElement && checkElement !== document.body) {
            if (checkElement.nodeType === Node.TEXT_NODE) {
              checkElement = checkElement.parentElement;
              continue;
            }
            
            console.log('🎯 ALIGN DEBUG - Checking element:', {
              nodeName: checkElement.nodeName,
              hasStyleTextAlign: !!checkElement.style?.textAlign,
              styleTextAlign: checkElement.style?.textAlign,
              tagName: checkElement.tagName
            });
            
            // Found a div with explicit textAlign style
            if (checkElement.nodeName === 'DIV' && checkElement.style?.textAlign) {
              existingAlignDiv = checkElement;
              console.log('🎯 ALIGN DEBUG - Found existing alignment div:', {
                element: existingAlignDiv,
                currentAlign: existingAlignDiv.style.textAlign
              });
              break;
            }
            
            checkElement = checkElement.parentElement;
          }
          
          if (existingAlignDiv) {
            console.log('🎯 ALIGN DEBUG - UPDATING existing align div:', {
              nodeName: existingAlignDiv.nodeName,
              currentAlign: existingAlignDiv.style.textAlign,
              newAlign: alignment
            });
            
            // Update existing alignment instead of creating new div
            existingAlignDiv.style.textAlign = alignment;
            console.log('🎯 ALIGN DEBUG - ✅ Successfully updated existing div alignment to:', alignment);
            
            // Make sure selection stays in the updated div
            const newRange = document.createRange();
            newRange.selectNodeContents(existingAlignDiv);
            selection.removeAllRanges();
            selection.addRange(newRange);
            console.log('🎯 ALIGN DEBUG - Updated selection to existing updated div');
          } else {
            // Create new alignment div
            console.log('🎯 ALIGN DEBUG - Creating new alignment div');
            
          const selectedContent = range.extractContents();
          const alignDiv = document.createElement('div');
          
          switch (alignment) {
            case 'left':
              alignDiv.style.textAlign = 'left';
              break;
            case 'center':
              alignDiv.style.textAlign = 'center';
              break;
            case 'right':
              alignDiv.style.textAlign = 'right';
              break;
            case 'justify':
              alignDiv.style.textAlign = 'justify';
              break;
          }
            
            console.log('🎯 ALIGN DEBUG - Applied style:', alignDiv.style.textAlign);
          
          alignDiv.appendChild(selectedContent);
          range.insertNode(alignDiv);
            
            console.log('🎯 ALIGN DEBUG - Inserted new div with content');
          
          // Neue Auswahl setzen
          const newRange = document.createRange();
          newRange.selectNodeContents(alignDiv);
          selection.removeAllRanges();
          selection.addRange(newRange);
            
            console.log('🎯 ALIGN DEBUG - Updated selection to new div');
          }
          
          // Debug: Final HTML structure
          const finalHTML = range.commonAncestorContainer.parentElement?.innerHTML || 'N/A';
          console.log('🎯 ALIGN DEBUG - Final HTML structure (excerpt):', finalHTML.substring(0, 200) + '...');
          
        } catch (error) {
          console.error('🎯 ALIGN DEBUG - Error occurred:', error);
          console.warn(t('reportEditor.errors.alignmentFailed'), error);
        }
      } else {
        console.log('🎯 ALIGN DEBUG - Range is collapsed, no text selected');
      }
    } else {
      console.log('🎯 ALIGN DEBUG - No selection range available');
    }
    
    // Schließe Toolbar nach Alignment (nur diese Operation schließt die Toolbar)
    setTooltipOpen(false);
    setTooltipAnchor(null);
    setSelectedText(null);
    
    console.log('🎯 ALIGN DEBUG - Alignment process completed for:', alignment);
  };

  const handleColorPickerOpen = (event) => {
    // Speichere die aktuelle Selection bevor der Color Picker geöffnet wird
    const currentSelection = saveSelection();
    if (currentSelection && !currentSelection.collapsed) {
      setSavedSelection(currentSelection);
      setTempColor(fontColor); // Initialisiere tempColor mit aktueller Farbe
      setColorPickerAnchor(event.currentTarget);
      setColorPickerOpen(true);
    }
  };

  const handleColorPickerClose = () => {
    setColorPickerOpen(false);
    setColorPickerAnchor(null);
    setSavedSelection(null); // Lösche gespeicherte Selection beim Schließen
  };

  // Kommentar löschen Funktion
  const handleDeleteComment = (commentToDelete) => {
    // Entferne Kommentar aus der Liste
    setInlineComments(prev => prev.filter(comment => 
      !(comment.text === commentToDelete.text && 
        comment.comment === commentToDelete.comment &&
        comment.date === commentToDelete.date &&
        comment.time === commentToDelete.time)
    ));
    
    // Schließe Tooltip
    setCommentTooltip({ open: false, comment: null, anchorEl: null });
    
    // Sofortiges DOM-Update: Entferne Icon und Highlighting
    setTimeout(() => {
      const contentDivs = document.querySelectorAll('.report-editor-content[contenteditable="true"]');
      contentDivs.forEach(contentDiv => {
        // Finde und entferne alle Elemente für diesen Kommentar
        const commentIcons = contentDiv.querySelectorAll('.inline-comment-icon');
        const highlightedSpans = contentDiv.querySelectorAll('.comment-highlighted');
        
        // Prüfe welche Icons/Highlights zu dem gelöschten Kommentar gehören
        commentIcons.forEach(icon => {
          // Finde den dazugehörigen highlighted text
          let prevSibling = icon.previousSibling;
          if (prevSibling && prevSibling.classList && prevSibling.classList.contains('comment-highlighted')) {
            const highlightedText = prevSibling.textContent;
            if (highlightedText === commentToDelete.text) {
              // Ersetze highlighted span mit normalen Text
              const textNode = document.createTextNode(highlightedText);
              prevSibling.parentNode.replaceChild(textNode, prevSibling);
              // Entferne das Icon
              icon.remove();
            }
          }
        });
      });
    }, 50);
  };

  // Kommentarfunktionen
  const loadComments = async (reportId) => {
    try {
      const response = await fetch(createApiUrl(`reports/${reportId}/comments`), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error(t('reportEditor.errors.loadingComments'), error);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    const comment = {
      name: currentUser?.username || t('reportEditor.defaults.unknownUser'),
      date: format(new Date(), 'dd.MM.yyyy', { locale: de }),
      time: format(new Date(), 'HH:mm', { locale: de }),
      comment: newComment.trim()
    };

    
    // Rufe Comment-Only Save auf
    await handleSaveCommentOnly(comment);
    
    // Leere Input nur bei erfolgreichem Save
    setNewComment('');
  };

  const formatCommentTime = (dateStr, timeStr) => {
    try {
      const [day, month, year] = dateStr.split('.');
      const [hours, minutes] = timeStr.split(':');
      const commentDate = new Date(year, month - 1, day, hours, minutes);
      
      const now = new Date();
      const diffMs = now - commentDate;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return `Heute um ${timeStr}`;
      } else if (diffDays === 1) {
        return `Gestern um ${timeStr}`;
      } else if (diffDays < 7) {
        return `vor ${diffDays} Tagen`;
      } else {
        return `${dateStr} um ${timeStr}`;
      }
    } catch (e) {
      return `${dateStr} um ${timeStr}`;
    }
  };

  // Auto-Focus erstes verfügbares Finding bei strukturierten Templates
  useEffect(() => {
    if (!selectedTemplate || selectedTemplate.template_type !== 'S') return undefined;

    setFocusedFindingIndex(0);
    setActiveStepIndex(0);
    closeAllDropdowns();
    findingRefs.current = [];

    const timer = setTimeout(() => {
      const allFindings = getAllFindings();
      if (allFindings.length === 0) return;

      const firstIndex = findNextAvailableField(0, 1);
      setFocusedFindingIndex(firstIndex);
      setActiveStepIndex(resolveStepIndexForFinding(firstIndex));
      focusCurrentFinding(firstIndex, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedTemplate?.id]);

  // Auto-Focus für das erste Textfeld bei Freitext-Templates
  useEffect(() => {
    if (selectedTemplate && selectedTemplate.template_type === 'F') {
      // Warte kurz, bis das Template gerendert ist
      setTimeout(() => {
        const firstTextField = document.querySelector('#report-form-freetext [contenteditable="true"]:not([disabled]), #report-form-freetext input:not([disabled]), #report-form-freetext textarea:not([disabled])');
        if (firstTextField) {
          firstTextField.focus();
          if (firstTextField.select) {
            firstTextField.select();
          } else if (firstTextField.contentEditable === 'true') {
            // Für contentEditable Elemente (RichTextField)
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(firstTextField);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }, 300);
    }
  }, [selectedTemplate]);

  // Event-Listener für Freitext Navigation entfernt - wird jetzt direkt vom globalen Handler behandelt

  // Dropdown schließen bei Klick außerhalb oder Fokuswechsel (z. B. Tab)
  useEffect(() => {
    const isInsideDropdownUi = (element) => (
      element?.closest?.('[role="listbox"]')
      || element?.closest?.('[role="combobox"]')
      || element?.closest?.('.MuiSelect-select')
      || element?.closest?.('.MuiPopover-root')
      || element?.closest?.('.MuiMenu-root')
    );

    const handlePointerDown = (event) => {
      const states = dropdownStatesRef.current;
      const anyDropdownOpen = Object.values(states).some(Boolean);
      if (!anyDropdownOpen) return;

      if (isInsideDropdownUi(event.target)) return;

      closeAllDropdowns();
    };

    const handleFocusIn = () => {
      requestAnimationFrame(() => {
        const states = dropdownStatesRef.current;
        if (!Object.values(states).some(Boolean)) return;

        if (!isInsideDropdownUi(document.activeElement)) {
          closeAllDropdowns();
        }
      });
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  // Globaler Schutz für Kommentar-Icons vor Löschung
  useEffect(() => {
    const protectCommentIcons = (event) => {
      // Verhindere Delete/Backspace auf Kommentar-Icons
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;
          
          // Prüfe ob ein Kommentar-Icon in der Selection ist
          let element = container;
          if (element.nodeType === Node.TEXT_NODE) {
            element = element.parentElement;
          }
          
          const commentIcon = element?.closest('.inline-comment-icon') || 
                             element?.querySelector('.inline-comment-icon');
          
          if (commentIcon) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
          
          // Prüfe auch ob die Range ein Icon überlappt
          const iconInRange = range.toString().includes('ℹ️');
          if (iconInRange) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        }
      }
    };

    document.addEventListener('keydown', protectCommentIcons, true);
    
    return () => {
      document.removeEventListener('keydown', protectCommentIcons, true);
    };
  }, []);

  // Aktuellen Zustand erfassen für Vergleiche
  const getCurrentReportState = () => {
    const templateData = [];
    
    if (selectedTemplate && selectedTemplate.fields_json) {
      let templateFields;
      if (typeof selectedTemplate.fields_json === 'string') {
        templateFields = JSON.parse(selectedTemplate.fields_json);
      } else {
        templateFields = selectedTemplate.fields_json;
      }
      
      templateFields.forEach(field => {
        const fieldKey = field.id || field.label;
        let value = '';
        
        if (field.database_field && field.database_field.trim()) {
          value = examData[field.database_field] || '';
        } else {
          const semanticId = generateSemanticFieldId(field, fieldKey);
          const richTextElement = document.getElementById(semanticId);
          if (richTextElement && richTextElement.contentEditable === 'true') {
            value = cleanHtmlForSaving(richTextElement.innerHTML) || '';
          } else {
            value = formValues[fieldKey] || '';
          }
        }
        
        templateData.push({
          id: field.id,
          value: value
        });
      });
    }
    
    // Freitext-Inhalt hinzufügen
    const freetextElement = document.getElementById('freetext-content');
    if (freetextElement && freetextElement.contentEditable === 'true') {
      templateData.push({
        id: 'freetext_content',
        value: cleanHtmlForSaving(freetextElement.innerHTML) || ''
      });
    } else if (reportContent) {
      templateData.push({
        id: 'freetext_content',
        value: reportContent
      });
    }
    
    return {
      template_data: templateData,
      comments: comments.filter(c => c.comment && c.comment.trim()),
      inline_comments: inlineComments,
      template_id: selectedTemplate?.id
    };
  };

  // Prüfe ob Änderungen seit letztem Speichern oder Laden vorliegen
  const hasUnsavedChanges = () => {
    // Während des Ladens: Keine Änderungen melden
    if (isLoadingReport) {
      console.log('🔍 hasUnsavedChanges: isLoadingReport=true, return false');
      return false;
    }
    
    // Sicherheitsabfrage: Keine Warnung in den ersten 2 Sekunden nach dem Laden
    // Verhindert nervige Browser-Warnungen beim initialen Öffnen
    if (reportLoadedTimestamp && (Date.now() - reportLoadedTimestamp) < 2000) {
      console.log('🔍 hasUnsavedChanges: Report gerade geladen (<2s), return false');
      return false;
    }
    
    const currentState = getCurrentReportState();
    
    // Bestimme Vergleichszustand: Gespeichert hat Priorität, dann geladen, dann "leer"
    let compareState = lastSavedState || initialLoadedState;
    
    console.log('🔍 hasUnsavedChanges DEBUG:', {
      hasCompareState: !!compareState,
      hasLastSavedState: !!lastSavedState,
      hasInitialLoadedState: !!initialLoadedState,
      currentTemplateDataLength: currentState.template_data.length,
      compareTemplateDataLength: compareState?.template_data?.length,
      currentTemplateId: currentState.template_id,
      compareTemplateId: compareState?.template_id
    });
    
    if (!compareState) {
      // Komplett neuer Report: Prüfe ob überhaupt Inhalt vorhanden ist
      const hasAnyContent = currentState.template_data.some(d => d.value && d.value.trim()) ||
                           currentState.comments.length > 0 ||
                           currentState.inline_comments.length > 0;
      console.log('🔍 hasUnsavedChanges: Neuer Report, hasAnyContent=', hasAnyContent);
      return hasAnyContent;
    }

    
    // Vergleiche Template-ID
    if (currentState.template_id !== compareState.template_id) {
      console.log('🔍 hasUnsavedChanges: Template-ID changed, return true');
      return true;
    }
    
    // Vergleiche Template-Daten
    if (currentState.template_data.length !== compareState.template_data.length) {
      console.log('🔍 hasUnsavedChanges: Template data length different, return true');
      return true;
    }
    
    for (let i = 0; i < currentState.template_data.length; i++) {
      const current = currentState.template_data[i];
      const compare = compareState.template_data.find(d => d.id === current.id);
      if (!compare) {
        console.log('🔍 hasUnsavedChanges: Field not found in compare state:', current.id);
        return true;
      }
      if (current.value !== compare.value) {
        console.log('🔍 hasUnsavedChanges: Field value changed:', {
          fieldId: current.id,
          currentValue: current.value?.substring(0, 100),
          compareValue: compare.value?.substring(0, 100),
          currentLength: current.value?.length,
          compareLength: compare.value?.length
        });
        return true;
      }
    }
    
    // Vergleiche Kommentare
    if (JSON.stringify(currentState.comments) !== JSON.stringify(compareState.comments)) {
      console.log('🔍 hasUnsavedChanges: Comments changed, return true');
      return true;
    }
    if (JSON.stringify(currentState.inline_comments) !== JSON.stringify(compareState.inline_comments)) {
      console.log('🔍 hasUnsavedChanges: Inline comments changed, return true');
      return true;
    }
    
    console.log('🔍 hasUnsavedChanges: No changes detected, return false');
    return false;
  };

  // Browser-Warnung beim Verlassen der Seite mit ungespeicherten Änderungen
  // WICHTIG: useEffect MUSS NACH hasUnsavedChanges Definition sein!
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // ✅ Navigation wurde durch eigenen Dialog bestätigt - keine Browser-Warnung
      if (allowNavigationRef.current) {
        console.log('✅ Navigation bereits durch Dialog bestätigt - keine Browser-Warnung');
        return;
      }
      
      // Sicherheitsabfrage: Keine Warnung in den ersten 2 Sekunden nach dem Laden
      // Verhindert nervige Browser-Warnungen beim initialen Öffnen
      if (reportLoadedTimestamp && (Date.now() - reportLoadedTimestamp) < 2000) {
        console.log('🔍 beforeunload: Report gerade geladen (<2s), keine Warnung');
        return;
      }
      
      // Fall 1: Addendum-Modus - warnen wenn Addendum-Feld leer ist
      if (isAddendumMode && existingReportId && !addendumContent.trim()) {
        e.preventDefault();
        e.returnValue = ''; // Chrome benötigt das
        return ''; // Andere Browser
      }
      
      // Fall 2: Normaler Modus - warnen wenn ungespeicherte Änderungen vorhanden sind
      if (!isAddendumMode && hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = ''; // Chrome benötigt das
        return ''; // Andere Browser
      }
    };

    const handleUnload = () => {
      // Sicherheitsabfrage: Kein API-Call in den ersten 2 Sekunden nach dem Laden
      if (reportLoadedTimestamp && (Date.now() - reportLoadedTimestamp) < 2000) {
        console.log('🔍 unload: Report gerade geladen (<2s), kein API-Call');
        return;
      }
      
      // Nur im Addendum-Modus: API-Call wenn Addendum-Feld leer ist
      if (isAddendumMode && existingReportId && !addendumContent.trim()) {
        console.log('🔄 unload: Addendum ist leer, sende Cancel-Request...', {
          reportId: existingReportId,
          isAddendumMode,
          addendumContentLength: addendumContent.length
        });
        
        try {
          const url = createApiUrl(`reports/${existingReportId}/cancel-addendum`);
          const authHeaders = getAuthHeaders();
          const token = authHeaders.Authorization?.replace('Bearer ', '') || sessionStorage.getItem('token');
          
          // Synchroner XHR für zuverlässigen Call beim Verlassen der Seite
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url, false); // false = synchron
          xhr.setRequestHeader('Content-Type', 'application/json');
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          xhr.send(JSON.stringify({}));
          
          console.log('✅ Cancel addendum response status:', xhr.status);
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            console.log('✅ Cancel addendum response:', response);
            if (response.success) {
              console.log('✅ Leeres Addendum automatisch abgebrochen (unload)');
            } else {
              console.warn('⚠️ Cancel addendum failed:', response.message);
            }
          } else {
            console.error('❌ Cancel addendum HTTP error:', xhr.status, xhr.responseText);
          }
        } catch (error) {
          console.error('❌ Fehler beim Abbrechen des Addendums:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [isAddendumMode, existingReportId, addendumContent, hasUnsavedChanges, reportLoadedTimestamp]);

  // Stelle Funktionen global zur Verfügung für AppBar (nach Definition von handleSaveReport)

  // Auto-Save-Funktion (OHNE Erfolgsmeldung für Formatierungen)
  // 🔥 REKURSIVE HILFSFUNKTION: Sammelt ALLE Werte aus Template (inkl. Groups und Findings)
  const collectTemplateDataRecursive = (items, parentKey = '') => {
    const collectedData = [];
    
    if (!Array.isArray(items)) return collectedData;
    
    items.forEach((item, idx) => {
      const itemType = item.item_type || item.type;
      const itemId = item.id || `item_${idx}`;
      // 🔥 KRITISCH: Verwende die GLEICHE keyBase-Logik wie renderStructuredItem!
      const keyBase = parentKey ? `${parentKey}.${itemId}` : itemId;
      
      if (itemType === 'field') {
        // Top-Level Field (Freitext oder Datenbank)
        let value = '';
        
        if (item.database_field && item.database_field.trim()) {
          // Datenbankfeld - hole Wert aus examData
          value = examData[item.database_field] || '';
        } else {
          // Freitextfeld - hole HTML aus DOM oder formValues
          const semanticId = generateSemanticFieldId(item, itemId);
          const richTextElement = document.getElementById(semanticId);
          if (richTextElement && richTextElement.contentEditable === 'true') {
            value = cleanHtmlForSaving(richTextElement.innerHTML) || '';
          } else {
            value = formValues[itemId] || '';
          }
        }
        
        collectedData.push({
          id: item.id,
          label: item.label,
          type: item.type,
          value: value,
          width: item.width,
          height: item.height,
          required: item.required,
          options: item.options,
          database_field: item.database_field
        });
        
      } else if (itemType === 'group') {
        // Group mit Findings - rekursiv sammeln
        const groupData = {
          id: item.id,
          label: item.label,
          name: item.name,
          type: item.type || 'group',
          findings: [] // Hier sammeln wir die Finding-Werte
        };
        
        // Rekursiv durch alle Findings gehen
        if (Array.isArray(item.findings)) {
          item.findings.forEach((finding, findingIdx) => {
            const findingId = finding.id || `finding_${findingIdx}`;
            // 🔥 KRITISCH: Verwende keyBase für Group als parentKey für Findings!
            const findingKey = `${keyBase}.${findingId}`;
            
            console.log(`🔍 DEBUG: Sammle Finding-Wert für Key: "${findingKey}" (findingId: "${findingId}", finding_type: "${finding.finding_type}")`);
            
            // Hole Finding-Wert aus formValues oder DOM
            // 🔥 WICHTIG: Zuerst den kurzen Key (findingId) prüfen, da die UI diesen verwendet!
            let findingValue = formValues[findingId] || formValues[findingKey] || '';
            let commentValue = ''; // Für yesno_comment Felder
            
            // 🔥 SPEZIAL-BEHANDLUNG für verschiedene Finding-Typen
            if (finding.finding_type === 'checkboxes') {
              // Checkboxes: Array zu komma-getrenntem String konvertieren
              // 🔥 WICHTIG: Zuerst den kurzen Key (findingId) prüfen, da die UI diesen verwendet!
              const checkboxArray = formValues[findingId] || formValues[findingKey] || [];
              if (Array.isArray(checkboxArray)) {
                findingValue = checkboxArray.join(',');
              } else if (typeof checkboxArray === 'string') {
                findingValue = checkboxArray; // Bereits als String gespeichert
              } else {
                findingValue = '';
              }
            } else if (finding.finding_type === 'yesno_comment') {
              // Yes/No mit Kommentar: Hole auch den Kommentar-Wert
              // 🔥 WICHTIG: Die UI verwendet `.comment` nicht `_comment`!
              const commentKey = `${findingKey}.comment`;
              const commentIdKey = `${findingId}.comment`;
              commentValue = formValues[commentIdKey] || formValues[commentKey] || '';
              console.log(`🔥 DEBUG YESNO: findingKey="${findingKey}", findingId="${findingId}"`);
              console.log(`🔥 DEBUG YESNO: commentKey="${commentKey}", commentIdKey="${commentIdKey}"`);
              console.log(`🔥 DEBUG YESNO: formValues[commentIdKey]="${formValues[commentIdKey]}"`);
              console.log(`🔥 DEBUG YESNO: formValues[commentKey]="${formValues[commentKey]}"`);
              console.log(`🔥 DEBUG YESNO: commentValue="${commentValue}"`);
            }
            
            // Für RichTextFields auch DOM prüfen
            const semanticId = generateSemanticFieldId(finding, findingId);
            const richTextElement = document.getElementById(semanticId);
            
            if (richTextElement && richTextElement.contentEditable === 'true') {
              const domValue = cleanHtmlForSaving(richTextElement.innerHTML);
              
              // 🚨 NUR für FREETEXT-Felder DOM verwenden, NICHT für Checkboxes/Dropdowns!
              if (finding.finding_type === 'freetext' || finding.finding_type === 'freetext_unit') {
                findingValue = domValue || findingValue;
              }
            }
            
            const findingData = {
              id: finding.id,
              name: finding.name,
              subtitle: finding.subtitle || '',
              finding_type: finding.finding_type,
              unit: finding.unit,
              options: finding.options,
              comment_placeholder: finding.comment_placeholder,
              group_id: finding.group_id,
              value: findingValue
            };
            
            // Kommentar-Wert nur hinzufügen wenn vorhanden
            if (commentValue) {
              findingData.comment_value = commentValue;
            }
            
            groupData.findings.push(findingData);
          });
        }
        
        collectedData.push(groupData);
        
      } else if (itemType === 'finding') {
        // Standalone Finding (außerhalb von Group)
        const findingKey = keyBase; // Verwende keyBase direkt
        let findingValue = formValues[findingKey] || formValues[itemId] || '';
        let commentValue = ''; // Für yesno_comment Felder
        
        console.log(`🔍 DEBUG: Sammle Standalone-Finding-Wert für Key: "${findingKey}" (itemId: "${itemId}", finding_type: "${item.finding_type}")`);
        
        // 🔥 SPEZIAL-BEHANDLUNG für verschiedene Finding-Typen
        if (item.finding_type === 'checkboxes') {
          // Checkboxes: Array zu komma-getrenntem String konvertieren
          const checkboxArray = formValues[findingKey] || formValues[itemId] || [];
          console.log(`🔍 DEBUG: Standalone Checkbox-Array für "${findingKey}":`, checkboxArray);
          if (Array.isArray(checkboxArray)) {
            findingValue = checkboxArray.join(',');
            console.log(`✅ DEBUG: Standalone Checkbox-Wert konvertiert: "${findingValue}"`);
          } else if (typeof checkboxArray === 'string') {
            findingValue = checkboxArray; // Bereits als String gespeichert
            console.log(`✅ DEBUG: Standalone Checkbox-Wert bereits String: "${findingValue}"`);
          } else {
            findingValue = '';
            console.log(`⚠️ DEBUG: Standalone Checkbox-Wert leer oder ungültig`);
          }
        } else if (item.finding_type === 'yesno_comment') {
          // Yes/No mit Kommentar: Hole auch den Kommentar-Wert
          const commentKey = `${findingKey}_comment`;
          const commentIdKey = `${itemId}_comment`;
          commentValue = formValues[commentKey] || formValues[commentIdKey] || '';
          console.log(`🔍 DEBUG: Standalone YesNo-Kommentar für "${commentKey}": "${commentValue}"`);
        }
        
        console.log(`🔍 DEBUG: Finaler Standalone-Finding-Wert für "${findingKey}": "${findingValue}"`);
        
        const semanticId = generateSemanticFieldId(item, itemId);
        const richTextElement = document.getElementById(semanticId);
        if (richTextElement && richTextElement.contentEditable === 'true') {
          findingValue = cleanHtmlForSaving(richTextElement.innerHTML) || findingValue;
        }
        
        const standaloneFindingData = {
          id: item.id,
          name: item.name,
          subtitle: item.subtitle || '', // ✅ Subtitle explizit übernehmen
          type: 'finding',
          finding_type: item.finding_type,
          value: findingValue,
          unit: item.unit,
          options: item.options,
          comment_placeholder: item.comment_placeholder,
          group_id: item.group_id
        };
        
        // 🔥 Kommentar-Wert nur hinzufügen wenn vorhanden
        if (commentValue) {
          standaloneFindingData.comment_value = commentValue;
        }
        
        collectedData.push(standaloneFindingData);
      }
    });
    
    return collectedData;
  };

  const handleAutoSave = async () => {
    console.log('🔄 AUTO-SAVE - Silent save triggered...');
    
    try {
      if (!examData || !currentUser) {
        return;
      }
      
      // Erstelle Template-Daten JSON
      let templateData = [];
      const templateType = selectedTemplate?.template_type || 'F';
      
      console.log('🔄 AUTO-SAVE - Template Type:', templateType);
      
      if (selectedTemplate && selectedTemplate.fields_json) {
        // Prüfe ob fields_json bereits ein Objekt ist oder ein String
        let templateFields;
        if (typeof selectedTemplate.fields_json === 'string') {
          templateFields = JSON.parse(selectedTemplate.fields_json);
        } else {
          templateFields = selectedTemplate.fields_json;
        }
        
        // Unterscheide zwischen Freitext und strukturierten Templates
        if (templateType === 'F') {
          // 📝 FREITEXT-TEMPLATE: Einfache Sammlung
          templateFields.forEach(field => {
            let value = '';
            
            if (field.database_field && field.database_field.trim()) {
              // Datenbankfeld - hole Wert aus examData
              value = examData[field.database_field] || '';
            } else {
              // Freitextfeld - hole HTML aus DOM oder formValues
              const semanticId = generateSemanticFieldId(field, field.id);
              const richTextElement = document.getElementById(semanticId);
              if (richTextElement && richTextElement.contentEditable === 'true') {
                value = cleanHtmlForSaving(richTextElement.innerHTML) || '';
              } else {
                value = formValues[field.id] || '';
              }
            }
            
            templateData.push({
              id: field.id,
              label: field.label,
              type: field.type,
              value: value,
              width: field.width,
              height: field.height,
              required: field.required,
              options: field.options,
              database_field: field.database_field
            });
          });
        } else {
          // 🏗️ STRUKTURIERTES TEMPLATE: REKURSIV alle Daten sammeln (inkl. Groups und Findings)
          templateData = collectTemplateDataRecursive(templateFields);
        }
        
        console.log('🔥 AUTO-SAVE - Gesammelte Template-Daten:', templateData);
      }
      
      // Für Freitext-Reports - hole HTML aus DOM
      const freetextElement = document.getElementById('freetext-content');
      if (freetextElement && (reportContent && reportContent.trim() || freetextElement.innerHTML.trim())) {
        const freetextValue = freetextElement.contentEditable === 'true' ? 
          cleanHtmlForSaving(freetextElement.innerHTML) : reportContent;
        
        templateData.push({
          id: 'freetext_content',
          label: 'Freitext Befund',
          type: 'textarea',
          value: freetextValue,
          width: 12,
          height: 10
        });
      }

      // Erstelle Report JSON
      const reportJson = {
        template_data: templateData,
        metadata: {
          template_id: selectedTemplate?.id || null,
          template_name: selectedTemplate?.name || 'Freitext',
          template_type: selectedTemplate?.template_type || 'F',
          saved_at: new Date().toISOString(),
          version: 1
        }
      };

      // Bestimme author_id
      let authorId;
      if (currentUser.id) {
        authorId = currentUser.id.toString();
      } else if (currentUser.user_id) {
        authorId = currentUser.user_id.toString();
      } else if (currentUser.userId) {
        authorId = currentUser.userId.toString();
      } else {
        console.error('❌ Kann author_id nicht bestimmen:', currentUser);
        authorId = '1'; // Fallback
      }
      
      // Prüfe ob currentNewReport existiert - WICHTIG für korrekte new_report_id
      if (!currentNewReport || !currentNewReport.id) {
        console.error('❌ AUTO-SAVE FEHLER: currentNewReport nicht verfügbar!', currentNewReport);
        return false; // Fehler zurückgeben
      }
      
      // Erstelle Report-Objekt für API
      const reportData = {
        new_report_id: currentNewReport.id, // WICHTIG: currentNewReport.id ist new_reports.id, NICHT new_exams.id!
        template_id: selectedTemplate?.id || null,
        author_id: authorId,
        report: reportJson,
        comments: comments.filter(c => c.comment && c.comment.trim()),
        inline_comments: inlineComments
      };

      // Im Addendum-Modus: Addendum-Content hinzufügen
      if (isAddendumMode && addendumContent !== undefined) {
        reportData.addendum_content = addendumContent;
        console.log('📝 AUTO-SAVE - Addendum-Content wird gespeichert:', {
          length: addendumContent.length,
          isEmpty: !addendumContent.trim()
        });
      }

      // Status ONLY beim ersten Erstellen setzen, NICHT bei Updates überschreiben!
      if (!existingReportId) {
        reportData.status = 'STARTED'; // Nur für neue Reports
        console.log('🔄 AUTO-SAVE - Neuer Report - Status wird auf STARTED gesetzt');
      } else {
        console.log('🔄 AUTO-SAVE - Update bestehender Report - Status wird NICHT überschrieben');
      }

      // Unterscheide zwischen CREATE und UPDATE
      let response;
      if (existingReportId) {
        // UPDATE: Report bereits vorhanden
        response = await apiFetch(`/reports/${existingReportId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(reportData)
        });
      } else {
        // CREATE: Neuer Report
        response = await apiFetch('/reports/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(reportData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        
        let reportId = existingReportId;
        if (!existingReportId) {
          setExistingReportId(result.id);
          reportId = result.id; // Für sofortige Verwendung
          // Aktualisiere Patientenkontext mit reportId
          setContext(prev => ({
            ...prev,
            reportId: result.id
          }));
          // Auto-Save ändert KEINE URL - nur handleSaveReport macht das
        }
        
        // Speichere den aktuellen Zustand als "letzten gespeicherten Zustand"
        setLastSavedState(getCurrentReportState());
        setReportLoadedTimestamp(Date.now()); // Reset timestamp nach Speichern
        
        // ✅ KEINE Erfolgsmeldung bei Auto-Save!
        console.log('🔄 AUTO-SAVE - Successfully saved without notification');
        return { success: true, reportId }; // ✅ Erfolg und reportId zurückgeben
        
      } else {
        console.error('🔄 AUTO-SAVE - Failed:', response.statusText);
        return { success: false }; // ❌ Fehler zurückgeben
      }
      
    } catch (error) {
      console.error('❌ AUTO-SAVE - Error:', error);
      return { success: false }; // ❌ Fehler zurückgeben
    }
  };

  // Report speichern Funktion (MIT Erfolgsmeldung für manuelles Speichern)
  const handleCancelAddendum = async () => {
    try {
      console.log('🔄 Canceling addendum...', {
        reportId: existingReportId,
        addendumContent: addendumContent,
        isEmpty: !addendumContent.trim()
      });
      
      const response = await apiFetch(`/reports/${existingReportId}/cancel-addendum`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Cancel addendum response:', result);
        
        // Prüfe ob Backend erfolgreich war
        if (result.success === false) {
          console.warn('⚠️ Backend returned success=false:', result.message);
          showError(result.message || t('report.addendum.cancelError', 'Fehler beim Abbrechen des Addendums'));
          return;
        }
        
        showSuccess(t('report.addendum.canceled', 'Addendum-Prozess wurde abgebrochen'));
        // Zurück zur vorherigen Seite
        navigate(-1);
      } else {
        const error = await response.json();
        console.error('❌ Cancel addendum failed:', error);
        showError(error.detail || t('report.addendum.cancelError', 'Fehler beim Abbrechen des Addendums'));
      }
    } catch (error) {
      console.error('❌ Fehler beim Abbrechen des Addendums:', error);
      showError(t('report.addendum.cancelError', 'Fehler beim Abbrechen des Addendums'));
    } finally {
      setCancelAddendumDialogOpen(false);
    }
  };

  const handleSaveReport = async () => {
    
    try {
      if (!examData || !currentUser) {
        return;
      }
      
      // Erstelle Template-Daten JSON
      let templateData = [];
      const templateType = selectedTemplate?.template_type || 'F';
      
      console.log('🔄 SAVE-REPORT - Template Type:', templateType);
      
      if (selectedTemplate && selectedTemplate.fields_json) {
        // Prüfe ob fields_json bereits ein Objekt ist oder ein String
        let templateFields;
        if (typeof selectedTemplate.fields_json === 'string') {
          templateFields = JSON.parse(selectedTemplate.fields_json);
        } else {
          templateFields = selectedTemplate.fields_json;
        }
        
        // Unterscheide zwischen Freitext und strukturierten Templates
        if (templateType === 'F') {
          // 📝 FREITEXT-TEMPLATE: Einfache Sammlung
          templateFields.forEach(field => {
            let value = '';
            
            if (field.database_field && field.database_field.trim()) {
              // Datenbankfeld - hole Wert aus examData
              value = examData[field.database_field] || '';
            } else {
              // Freitextfeld - hole HTML aus DOM oder formValues
              const semanticId = generateSemanticFieldId(field, field.id);
              const richTextElement = document.getElementById(semanticId);
              if (richTextElement && richTextElement.contentEditable === 'true') {
                value = cleanHtmlForSaving(richTextElement.innerHTML) || '';
              } else {
                value = formValues[field.id] || '';
              }
            }
            
            templateData.push({
              id: field.id,
              label: field.label,
              type: field.type,
              value: value,
              width: field.width,
              height: field.height,
              required: field.required,
              options: field.options,
              database_field: field.database_field
            });
          });
        } else {
          // 🏗️ STRUKTURIERTES TEMPLATE: REKURSIV alle Daten sammeln (inkl. Groups und Findings)
          console.log('🔍 DEBUG: templateFields VOR collectTemplateDataRecursive:', templateFields);
          console.log('🔍 DEBUG: templateFields Details:', JSON.stringify(templateFields, null, 2));
          templateData = collectTemplateDataRecursive(templateFields);
          console.log('🔍 DEBUG: templateData NACH collectTemplateDataRecursive:', templateData);
          console.log('🔍 DEBUG: templateData Details:', JSON.stringify(templateData, null, 2));
        }
        
        console.log('🔥 SAVE-REPORT - Gesammelte Template-Daten:', templateData);
      }
      
      // Für Freitext-Reports - hole HTML aus DOM
      const freetextElement = document.getElementById('freetext-content');
      if (freetextElement && (reportContent && reportContent.trim() || freetextElement.innerHTML.trim())) {
        const freetextValue = freetextElement.contentEditable === 'true' ? 
          cleanHtmlForSaving(freetextElement.innerHTML) : reportContent;
        
        templateData.push({
          id: 'freetext_content',
          label: 'Freitext Befund',
          type: 'textarea',
          value: freetextValue,
          width: 12,
          height: 10
        });
      }

      // Erstelle Report JSON
      const reportJson = {
        template_data: templateData,
        metadata: {
          template_id: selectedTemplate?.id || null,
          template_name: selectedTemplate?.name || 'Freitext',
          template_type: selectedTemplate?.template_type || 'F',
          saved_at: new Date().toISOString(),
          version: 1
        }
      };

      // Bestimme author_id - verschiedene mögliche Strukturen berücksichtigen
      let authorId;
      if (currentUser.id) {
        authorId = currentUser.id.toString();
      } else if (currentUser.user_id) {
        authorId = currentUser.user_id.toString();
      } else if (currentUser.userId) {
        authorId = currentUser.userId.toString();
      } else {
        console.error('❌ Kann author_id nicht bestimmen:', currentUser);
        authorId = '1'; // Fallback
      }
      
      // Prüfe ob currentNewReport existiert - WICHTIG für korrekte new_report_id
      if (!currentNewReport || !currentNewReport.id) {
        console.error('❌ FEHLER: currentNewReport nicht verfügbar!', currentNewReport);
        showError('Fehler: Befund kann nicht gespeichert werden - NewReport fehlt', 'ERR-REPORT-NEW-REPORT-ID');
        return;
      }
      
      // Erstelle Report-Objekt für API
      const reportData = {
        new_report_id: currentNewReport.id, // WICHTIG: currentNewReport.id ist new_reports.id, NICHT new_exams.id!
        template_id: selectedTemplate?.id || null,
        author_id: authorId, // Author ID aus currentUser - als String da Backend String erwartet
        report: reportJson,
        comments: comments.filter(c => c.comment && c.comment.trim()), // Nur nicht-leere Kommentare
        inline_comments: inlineComments
      };

      console.log('🚀 BACKEND REQUEST - Vollständige Daten die an Backend gesendet werden:');
      console.log('📊 reportData:', JSON.stringify(reportData, null, 2));
      console.log('📋 reportData.report.template_data:', reportData.report?.template_data);

      // Status UND initial_author_id ONLY beim ersten Erstellen setzen, NICHT bei Updates!
      if (!existingReportId) {
        reportData.status = 'STARTED'; // Nur für neue Reports
        reportData.initial_author_id = authorId; // Wer den Befund zuerst gespeichert hat
        console.log('🆕 Neuer Report - Status wird auf STARTED gesetzt, initial_author_id =', authorId);
      } else {
        console.log('🔄 Update bestehender Report - Status und initial_author_id werden NICHT überschrieben');
      }


      // Unterscheide zwischen CREATE und UPDATE
      let response;
      if (existingReportId) {
        // UPDATE: Report bereits vorhanden
        response = await apiFetch(`/reports/${existingReportId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(reportData)
        });
      } else {
        // CREATE: Neuer Report
        response = await apiFetch('/reports/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(reportData)
        });
      }

      if (response.ok) {
        const savedReport = await response.json();
        
        // Bei neuem Report: Speichere die ID für zukünftige Updates
        if (!existingReportId && savedReport.id) {
          setExistingReportId(savedReport.id);
          // Aktualisiere Patientenkontext mit reportId
          setContext(prev => ({
            ...prev,
            reportId: savedReport.id
          }));
        }
        
        // Speichere den aktuellen Zustand als "letzten gespeicherten Zustand"
        setLastSavedState(getCurrentReportState());
        setReportLoadedTimestamp(Date.now()); // Reset timestamp nach Speichern
        
        // Zeige Erfolgsnachricht
        showSuccess(t('reportEditor.save.success'));
        
      } else {
        const errorData = await response.json();
        console.error('❌ Fehler beim Speichern:', errorData);
        showError(t('reportEditor.save.error'), 'ERR-REPORT-018');
      }

    } catch (error) {
      console.error('❌ Kritischer Fehler beim Speichern:', error);
      showError(t('reportEditor.save.criticalError'), 'ERR-REPORT-019');
    }
  };

  // Kommentar-Only Save Funktion
  const handleSaveCommentOnly = async (newComment) => {
    
    try {
      if (!examData || !currentUser) {
        // Fehlende Daten: examData oder currentUser
        return;
      }

      if (existingReportId) {
        // UPDATE: Report existiert bereits - nur Kommentare aktualisieren
        
        // Hole aktuelle Kommentare und füge neuen hinzu
        const updatedComments = [...comments, newComment].filter(c => c.comment && c.comment.trim());
        
        const commentData = {
          comments: updatedComments
        };

        const response = await apiFetch(`/reports/${existingReportId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(commentData)
        });

        if (response.ok) {
          setComments(updatedComments);
          
          // Aktualisiere lastSavedState nur für Kommentare
          if (lastSavedState) {
            setLastSavedState({
              ...lastSavedState,
              comments: updatedComments
            });
            setReportLoadedTimestamp(Date.now()); // Reset timestamp nach Kommentar-Speichern
          }
          
          // Zeige dezente Erfolgsnachricht auch für Kommentare
          showSuccess(t('reportEditor.comment.saved'));
        } else {
          const errorData = await response.json();
          console.error('❌ Fehler beim Speichern des Kommentars:', errorData);
        }
      } else {
        // CREATE: Report existiert noch nicht - vollständiger Save
        
        // Füge Kommentar zu State hinzu und führe vollständigen Save aus
        const updatedComments = [...comments, newComment];
        setComments(updatedComments);
        
        // Vollständiger Report-Save mit neuem Kommentar (stumm)
        await handleAutoSave();
        
        // Nach erfolgreichem Save sollte existingReportId gesetzt sein
      }
      
    } catch (error) {
      console.error('❌ Kritischer Fehler beim Speichern des Kommentars:', error);
    }
  };

  // Stelle Funktionen global zur Verfügbar für AppBar
  window.reportEditorHasUnsavedChanges = hasUnsavedChanges;
  window.reportEditorSaveReport = handleSaveReport; // Manuelles Speichern (MIT Meldung)
  window.reportEditorAutoSave = handleAutoSave;     // Automatisches Speichern (OHNE Meldung)
  window.reportEditorAllowNavigation = () => {      // Erlaube Navigation ohne Browser-Warnung
    allowNavigationRef.current = true;
    console.log('✅ Navigation freigegeben - Browser-Warnung wird unterdrückt');
  };
  window.reportEditorCancelEmptyAddendum = async () => {  // Bricht leeres Addendum ab
    console.log('🔄 reportEditorCancelEmptyAddendum called', {
      isAddendumMode,
      existingReportId,
      addendumContentLength: addendumContent?.length,
      addendumContentTrimmed: addendumContent?.trim()?.length
    });
    
    if (isAddendumMode && existingReportId && !addendumContent.trim()) {
      console.log('🔄 Canceling empty addendum from AppBar...', {
        reportId: existingReportId,
        url: `/reports/${existingReportId}/cancel-addendum`
      });
      try {
        const response = await apiFetch(`/reports/${existingReportId}/cancel-addendum`, {
          method: 'POST'
        });
        console.log('🔄 API response:', { status: response.status, ok: response.ok });
        if (response.ok) {
          const result = await response.json();
          console.log('🔄 API result:', result);
          if (result.success !== false) {
            console.log('✅ Empty addendum canceled from AppBar');
            return true;
          } else {
            console.warn('⚠️ API returned success=false:', result.message);
          }
        }
      } catch (error) {
        console.error('❌ Failed to cancel addendum from AppBar:', error);
      }
    } else {
      console.log('⚠️ Conditions not met for canceling addendum');
    }
    return false;
  };
  
  // 🔧 DEBUG-TOOLS global verfügbar machen
  window.changeAllFontSizesInField = changeAllFontSizesInField;
  window.cleanupNestedFontSizeSpans = cleanupNestedFontSizeSpans;

  // Cleanup: Entferne globale Funktionen beim Unmount
  useEffect(() => {
    return () => {
      delete window.reportEditorHasUnsavedChanges;
      delete window.reportEditorSaveReport;
      delete window.reportEditorAutoSave;
      delete window.reportEditorAllowNavigation;
      delete window.reportEditorCancelEmptyAddendum;
      delete window.changeAllFontSizesInField;
      delete window.cleanupNestedFontSizeSpans;
      // States werden automatisch durch React cleanup behandelt
    };
  }, []);

  // Event-Listener für Text-Selection (Inline-Kommentare)
  useEffect(() => {
    const handleMouseUp = (event) => {
      // Nur in relevanten Bereichen
      const isInReportArea = event.target.closest('.report-editor-content') || 
                           event.target.classList.contains('report-editor-content') ||
                           event.target.closest('.rich-text-field') ||
                           event.target.closest('textarea') || 
                           event.target.closest('input[type="text"]');
      
      // Exclude database fields and disabled fields from toolbar functionality
      const isDatabaseField = event.target.closest('.database-field-light') ||
                             event.target.closest('.database-field-dark') ||
                             event.target.classList.contains('database-field-light') ||
                             event.target.classList.contains('database-field-dark');
      const isDisabled = event.target.getAttribute('contenteditable') === 'false' ||
                        event.target.closest('[contenteditable="false"]');
      

      
      if (isInReportArea && !isDatabaseField && !isDisabled) {
        setTimeout(() => {
          const selection = window.getSelection();
          const selectedText = selection.toString().trim();
          

          
          if (selectedText.length >= 1) {
            // Verwende Mausposition als Fallback
            const mouseX = event.clientX;
            const mouseY = event.clientY;
            
            setSelectedText({
              text: selectedText,
              range: selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null,
              startOffset: selection.rangeCount > 0 ? selection.getRangeAt(0).startOffset : 0,
              endOffset: selection.rangeCount > 0 ? selection.getRangeAt(0).endOffset : selectedText.length,
              containerText: selectedText
            });
            
            // VERBESSERTE Ermittlung: Analysiere ALLE font-size spans in der Selection
            const range = selection.getRangeAt(0);
            let currentFontSize = 12; // Default
            
            console.log('🎯 TOOLBAR FONT-SIZE DETECTION - Analyzing selection...');
            
            // Finde alle spans mit font-size in der aktuellen Selection
            const selectionFontSizes = new Set();
            
            // Klone Selection-Inhalt für Analyse
            const clonedContents = range.cloneContents();
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(clonedContents);
            
            // Analysiere spans im geklonten Inhalt
            const spansInSelection = tempDiv.querySelectorAll('span[style*="font-size"]');
            
            if (spansInSelection.length > 0) {
              spansInSelection.forEach(span => {
                const fontSize = span.style.fontSize;
                if (fontSize) {
                  const sizeValue = parseInt(fontSize.replace('px', ''));
                  if (sizeValue) {
                    selectionFontSizes.add(sizeValue);
                  }
                }
              });
            }
            
            console.log('🎯 TOOLBAR FONT-SIZES in selection:', Array.from(selectionFontSizes));
            
            // Entscheide basierend auf gefundenen Font-Sizes
            if (selectionFontSizes.size === 0) {
              // Keine spans gefunden - verwende computed style des ersten Elements
              let element = range.startContainer;
              if (element.nodeType === Node.TEXT_NODE) {
                element = element.parentElement;
              }
              if (element && element.style) {
                const computedStyle = window.getComputedStyle(element);
                const fontSize = computedStyle.fontSize;
                if (fontSize) {
                  const displayFontSize = parseInt(fontSize.replace('px', '')) || 14;
                  currentFontSize = displayFontSize - 2; // Display → Real für Toolbar
                } else {
                  currentFontSize = 12; // Default Real (Display: 14)
                }
              }
              console.log('🎯 TOOLBAR - Using computed style:', `Real: ${currentFontSize}px (Display: ${currentFontSize+2}px)`);
              
            } else if (selectionFontSizes.size === 1) {
              // Einheitliche Font-Size - konvertiere Display-Size zu Real-Size für Toolbar
              const displayFontSize = Array.from(selectionFontSizes)[0];
              currentFontSize = displayFontSize - 2; // Display → Real für Toolbar-State
              console.log('🎯 TOOLBAR - Uniform font-size:', `Display: ${displayFontSize}px → Real: ${currentFontSize}px`);
              
            } else {
              // Gemischte Font-Sizes - setze neutral state 
              currentFontSize = -1; // Signal für "gemischt"
              const displaySizes = Array.from(selectionFontSizes); // Sind bereits Display-Sizes
              const realSizes = displaySizes.map(size => size - 2); // Convert zu Real-Sizes
              console.log('🎯 TOOLBAR - Mixed font-sizes detected:', `Display: [${displaySizes.join(', ')}] → Real: [${realSizes.join(', ')}] → setting neutral state`);
            }
            
            setFontSize(currentFontSize);

            const anchorRect = {
              top: mouseY - 30,
              left: mouseX,
              right: mouseX,
              bottom: mouseY - 30,
              width: 0,
              height: 0
            };



            setTooltipAnchor({
              getBoundingClientRect: () => anchorRect
            });
            
            setTooltipOpen(true);
          }
        }, 50);
      }
    };

    const handleClickAway = (event) => {
      // Schließe Tooltip nur wenn nicht auf Tooltip-Buttons geklickt wurde
      // ODER wenn auf Formatierungs-Buttons geklickt wurde (die sollen Toolbar offen lassen)
      const isTooltipContent = event.target.closest('[data-tooltip-content]');
      const isFormattingButton = event.target.closest('[data-keep-toolbar-open]');
      
      if (!isTooltipContent && !isFormattingButton) {
        setTooltipOpen(false);
        setTooltipAnchor(null);
        setSelectedText(null);
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('click', handleClickAway);
    
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('click', handleClickAway);
    };
  }, []);

  // Text-Selection bei Scroll oder Resize schließen
  useEffect(() => {
    const handleCloseSelection = () => {
      setTooltipOpen(false);
      setTooltipAnchor(null);
      setSelectedText(null);
    };

    window.addEventListener('scroll', handleCloseSelection);
    window.addEventListener('resize', handleCloseSelection);
    
    return () => {
      window.removeEventListener('scroll', handleCloseSelection);
      window.removeEventListener('resize', handleCloseSelection);
    };
  }, []);

  // Re-render alle contentEditable divs wenn sich Inline-Kommentare ändern
  useEffect(() => {
    // Timeout um sicherzustellen, dass DOM vollständig gerendert ist
    setTimeout(() => {
      // Finde alle Rich Text Fields
      const contentDivs = document.querySelectorAll('.report-editor-content[contenteditable="true"]');
      
      if (contentDivs.length > 0 && inlineComments.length > 0) {
        contentDivs.forEach(contentDiv => {
          // WICHTIG: Verwende den aktuellen HTML Content statt innerText um Formatierung zu erhalten
          const currentHTML = contentDiv.innerHTML;
          
          if (currentHTML) {
            const newHTML = renderTextWithInlineComments(currentHTML);
            // Nur aktualisieren wenn sich tatsächlich etwas geändert hat
            if (newHTML !== currentHTML) {
              contentDiv.innerHTML = newHTML;
            }
          }
        });
      }
    }, 100);
  }, [inlineComments]);

  const handleStepperStepChange = (index) => {
    setActiveStepIndex(index);
    if (selectedTemplate?.template_type === 'S') {
      const findingIndex = resolveFindingIndexForStep(index);
      setFocusedFindingIndex(findingIndex);
    }
    requestAnimationFrame(() => {
      const section = stepSectionRefs.current[index];
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (selectedTemplate?.template_type === 'S') {
          const findingIndex = resolveFindingIndexForStep(index);
          if (!isFieldDisabled(getAllFindings()[findingIndex])) {
            focusCurrentFinding(findingIndex, false);
          }
          return;
        }
        const focusable = section.querySelector(
          'textarea, input:not([type="hidden"]), select, [contenteditable="true"]'
        );
        if (focusable) {
          focusable.focus();
        }
      }
    });
  };

  const handleStepFocus = (index) => {
    setActiveStepIndex((prev) => (prev === index ? prev : index));
  };

  const renderStepSection = (step, stepIndex) => {
    if (!step) return null;

    if (selectedTemplate.template_type === 'F' && step.type === 'freetext-field') {
      const field = step.field;
      const isDatabase = Boolean(field.database_field && field.database_field.trim());
      return (
        <Box
          className={isDatabase ? (isDarkMode ? 'database-container-dark' : 'database-container-light') : ''}
          sx={{ borderRadius: 1 }}
        >
          {!step.isMultiDatabase && (
            <label
              htmlFor={generateFieldId(field.id || field.label)}
              style={{
                display: 'block',
                fontWeight: 'light',
                marginBottom: '8px',
                fontSize: '1rem',
                color: theme.palette.text.primary,
              }}
            >
              {field.label}
            </label>
          )}
          {renderTemplateField(field)}
        </Box>
      );
    }

    if (selectedTemplate.template_type === 'S') {
      if (step.type === 'structured-field') {
        const item = step.item;
        if (step.isMultiDatabase && item.database_field) {
          return (
            <MultiDatabaseFieldAccordion
              combinedExams={combinedExams}
              databaseField={item.database_field}
              fieldLabel={item.label || item.name}
              isDarkMode={isDarkMode}
            />
          );
        }
        return renderStructuredItem(item, step.itemIndex, '', 0, resolveFindingIndex(item));
      }
      if (step.type === 'structured-group') {
        return renderStructuredContent([step.item]);
      }
      if (step.type === 'structured-finding') {
        return (
          <Box>
            {step.items.map((f, i) => renderStructuredItem(f, `${step.itemIndex}-${i}`, '', 1, resolveFindingIndex(f)))}
          </Box>
        );
      }
    }

    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100%',
        px: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - 200px)`
      }}>
      <ReportEditorLayout
        sidebarDetached={sidebarInPopup}
        layoutSettings={layoutSettings}
        onLayoutChange={updateLayoutSettings}
        showSidebarPanels={!sidebarInPopup}
        leftColumn={(
          <>
            <PatientDataCard
              examData={examData}
              combinedExams={combinedExams}
              isMultiExam={isMultiExam}
            />
            {!sidebarInPopup && (
              <>
                <PriorResultsCard
                  theme={theme}
                  showPriorReportText={priorStudiesState.showPriorReportText}
                  selectedPriorStudy={priorStudiesState.selectedPriorStudy}
                  onBackFromPriorReportText={priorStudiesState.handleBackFromPriorReportText}
                  onShowPdfFromTextView={handleShowPdfFromTextView}
                  priorStudiesSearchText={priorStudiesState.priorStudiesSearchText}
                  onPriorStudiesSearchChange={(e) => priorStudiesState.setPriorStudiesSearchText(e.target.value)}
                  priorStudiesModalityFilter={priorStudiesState.priorStudiesModalityFilter}
                  onPriorStudiesModalityFilterChange={(e) => priorStudiesState.setPriorStudiesModalityFilter(e.target.value)}
                  getUniqueModalities={priorStudiesState.getUniqueModalities}
                  getFilteredAndSortedPriorStudies={priorStudiesState.getFilteredAndSortedPriorStudies}
                  priorStudiesColumns={priorStudiesState.priorStudiesColumns}
                  priorStudiesApiRef={priorStudiesState.priorStudiesApiRef}
                  getGridLocale={priorStudiesState.getGridLocale}
                  onRowDoubleClick={(params) => {
                    const study = params.row;
                    if (study && study.study_instance_uid) {
                      handleOpenInViewer(study);
                    }
                  }}
                  sidebarDetached={popupOpen}
                  onToggleDetach={toggleDetach}
                  fillAvailable
                />
                <AISummaryCard
                  patientId={examData?.patient_id}
                  examId={examData?.id}
                  enabled={!!examData?.patient_id}
                  fillAvailable
                />
              </>
            )}
          </>
        )}
        rightColumn={(
          <>
            <StudyInfoCard
              examData={examData}
              combinedExams={combinedExams}
              isMultiExam={isMultiExam}
              studyDate={studyDate}
            />
          <Paper 
            elevation={1} 
            sx={{ 
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                {t('report.editor')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ButtonGroup id="buttongroup" size="small" variant="outlined" aria-label="structured report group">
                  <Button className={activeTemplateType === 'S' ? 'button' : ''} variant={activeTemplateType === 'S' ? 'contained' : 'outlined'} onClick={() => handleLoadTemplate('S')}>
                    {t('report.structuredReport')}
                  </Button>
                  <Tooltip title={t('report.browseTemplates')}>
                    <Button className="button" variant="outlined" onClick={() => handleTemplateBrowser('S')}>
                      <BrowserUpdatedOutlinedIcon />
                    </Button>
                  </Tooltip>
                </ButtonGroup>
                <ButtonGroup id="buttongroup" size="small" variant="outlined" aria-label="freetext report group">
                  <Button className={activeTemplateType === 'F' ? 'button' : ''} variant={activeTemplateType === 'F' ? 'contained' : 'outlined'} onClick={() => handleLoadTemplate('F')}>
                    {t('report.freetextReport')}
                  </Button>
                  <Tooltip title={t('report.browseTemplates')}>
                    <Button className="button" variant="outlined" onClick={() => handleTemplateBrowser('F')}>
                      <BrowserUpdatedOutlinedIcon />
                    </Button>
                  </Tooltip>
                </ButtonGroup>
                
                <Tooltip title={t('report.reloadViewer')}>
                  <IconButton 
                    size="small"
                    onClick={() => {
                      if (realStudyInstanceUID) {
                        safeOpenStudyInViewer(realStudyInstanceUID);
                      }
                    }}
                    disabled={!realStudyInstanceUID}
                    sx={{ 
                      color: 'var(--rd-primary)',
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                      },
                      '&.Mui-disabled': {
                        color: 'color-mix(in srgb, var(--rd-primary) 30%, transparent)'
                      }
                    }}
                  >
                    <PhotoLibrary />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              px: '14px'
            }}>
              {selectedTemplate && selectedTemplate.template_type === 'F' ? (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <form id="report-form-freetext" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <ReportStepper
                    steps={templateSteps}
                    activeStep={activeStepIndex}
                    onStepChange={handleStepperStepChange}
                    onStepFocus={handleStepFocus}
                    sectionRefs={stepSectionRefs}
                    renderStepContent={(step, stepIndex) => renderStepSection(step, stepIndex)}
                  />
                  </form>
                  
                  {/* Approved Addenda - read-only Anzeige (für Freitext-Template) */}
                  {allAddenda.filter(a => a.approved_by).map((addendum, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        borderTop: `2px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50]
                      }}
                    >
                      <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                        {t('report.addendum.title', 'Addendum')} #{index + 1}
                      </Typography>
                      
                      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {addendum.content}
                        </Typography>
                      </Paper>
                      
                      {addendum.signatures_json && addendum.signatures_json.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('report.addendum.signatures', 'Signaturen')}:
                          </Typography>
                          {addendum.signatures_json.map((sig, sigIndex) => (
                            <Typography key={sigIndex} variant="body2" sx={{ ml: 2 }}>
                              {sig.academic_degree && `${sig.academic_degree} `}
                              {sig.first_name} {sig.last_name}
                              {sig.job_title && `, ${sig.job_title}`}
                              {sig.position && ` (${sig.position})`}
                              {' - '}
                              {new Date(sig.signed_at).toLocaleString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      
                      {addendum.approval_json && (
                        <Box sx={{ ml: 0, mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Freigegeben:
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {addendum.approval_json.academic_degree && `${addendum.approval_json.academic_degree} `}
                            {addendum.approval_json.first_name} {addendum.approval_json.last_name}
                            {' - '}
                            {addendum.approval_json.approved_at} {','} {addendum.approval_json.approved_time}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Editierbares Addendum (für Freitext-Template) */}
                  {isAddendumMode && (
                    <Box sx={{ p: 2, borderTop: `2px solid ${theme.palette.divider}` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                          {(() => {
                            const approvedCount = allAddenda.filter(a => a.approved_by).length;
                            return approvedCount > 0 
                              ? `${t('report.addendum.title', 'Addendum')} #${approvedCount + 1}`
                              : t('report.addendum.title', 'Addendum');
                          })()}
                        </Typography>
                        <Button
                          onClick={() => setCancelAddendumDialogOpen(true)}
                          className="button-cancel"
                          sx={{ ml: 2 }}
                        >
                          {t('report.addendum.cancelProcess', 'Addendum abbrechen')}
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={addendumContent}
                        onChange={(e) => setAddendumContent(e.target.value)}
                        placeholder={t('report.addendum.placeholder', 'Geben Sie hier das Addendum ein...')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'var(--rd-bg-field)',
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ) : selectedTemplate && selectedTemplate.template_type === 'S' ? (
                <Box 
                  className="report-editor-content" 
                  sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', maxWidth: '100%', boxSizing: 'border-box' }}
                >
                  <form id="report-form-structured" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <ReportStepper
                    steps={templateSteps}
                    activeStep={activeStepIndex}
                    onStepChange={handleStepperStepChange}
                    onStepFocus={handleStepFocus}
                    sectionRefs={stepSectionRefs}
                    renderStepContent={(step, stepIndex) =>
                      renderStepSection(step, stepIndex) || (
                        <Typography>{t('report.error.unsupportedTemplate')}</Typography>
                      )
                    }
                  />
                  </form>
                  
                  {/* Approved Addenda - read-only Anzeige (für Strukturiertes Template) */}
                  {allAddenda.filter(a => a.approved_by).map((addendum, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        borderTop: `2px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                        mt: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                        {t('report.addendum.title', 'Addendum')} #{index + 1}
                      </Typography>
                      
                      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {addendum.content}
                        </Typography>
                      </Paper>
                      
                      {addendum.signatures_json && addendum.signatures_json.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('report.addendum.signatures', 'Signaturen')}:
                          </Typography>
                          {addendum.signatures_json.map((sig, sigIndex) => (
                            <Typography key={sigIndex} variant="body2" sx={{ ml: 2 }}>
                              {sig.academic_degree && `${sig.academic_degree} `}
                              {sig.first_name} {sig.last_name}
                              {sig.job_title && `, ${sig.job_title}`}
                              {sig.position && ` (${sig.position})`}
                              {' - '}
                              {new Date(sig.signed_at).toLocaleString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      
                      {addendum.approval_json && (
                        <Box sx={{ ml: 0, mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Freigegeben:
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {addendum.approval_json.academic_degree && `${addendum.approval_json.academic_degree} `}
                            {addendum.approval_json.first_name} {addendum.approval_json.last_name}
                            {' - '}
                            {addendum.approval_json.approved_at}{', '}{addendum.approval_json.approved_time}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Editierbares Addendum (für Strukturiertes Template) */}
                  {isAddendumMode && (
                    <Box sx={{ p: 2, borderTop: `2px solid ${theme.palette.divider}`, mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                          {(() => {
                            const approvedCount = allAddenda.filter(a => a.approved_by).length;
                            return approvedCount > 0 
                              ? `${t('report.addendum.title', 'Addendum')} #${approvedCount + 1}`
                              : t('report.addendum.title', 'Addendum');
                          })()}
                        </Typography>
                        <Button
                          onClick={() => setCancelAddendumDialogOpen(true)}
                          className="button-cancel"
                          sx={{ ml: 2 }}
                        >
                          {t('report.addendum.cancelProcess', 'Addendum abbrechen')}
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={addendumContent}
                        onChange={(e) => setAddendumContent(e.target.value)}
                        placeholder={t('report.addendum.placeholder', 'Geben Sie hier das Addendum ein...')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'var(--rd-bg-field)',
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                  <form id="report-form-freetext-editor" style={{ display: 'contents' }}>
                    <div className="form-group">
                      <label htmlFor="freetext-content" style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '1rem',
                        fontWeight: 'light',
                        color: theme.palette.text.primary
                      }}>
                        {t('report.content')}
                      </label>
                      <Box
                        id="freetext-content"
                        name="freetext-content"
                        className="report-editor-content"
                        sx={{
                          flex: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          padding: 2,
                          minHeight: '200px',
                          backgroundColor: 'var(--rd-bg-field)',
                          outline: 'none',
                          fontFamily: 'inherit',
                          fontSize: '0.875rem',
                          lineHeight: 1.4,
                          '&:focus': {
                            borderColor: theme.palette.primary.main,
                            borderWidth: '2px'
                          },
                          '&:empty:before': {
                            content: `"${t('report.enterReport')}"`,
                            color: theme.palette.text.secondary,
                            fontStyle: 'italic'
                          }
                        }}
                        contentEditable={!isAddendumMode && reportStatus !== 'APPROVED'}
                        suppressContentEditableWarning={true}
                        onInput={(e) => {
                          setReportContent(e.target.innerText);
                        }}
                        style={{
                          cursor: reportStatus === 'APPROVED' ? 'not-allowed' : 'text',
                          opacity: reportStatus === 'APPROVED' ? 0.6 : 1
                        }}
                        onClick={(e) => {
                          if (e.target.classList.contains('inline-comment-icon')) {
                            e.preventDefault();
                            const commentId = e.target.getAttribute('data-comment-id');
                            handleInlineCommentIconClick(commentId, e);
                          }
                        }}
                        dangerouslySetInnerHTML={{
                          __html: renderTextWithInlineComments(reportContent || '')
                        }}
                      />
                    </div>
                  </form>
                  
                  {/* Approved Addenda - read-only Anzeige (für Freitext-Editor) */}
                  {allAddenda.filter(a => a.approved_by).map((addendum, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        borderTop: `2px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                        mt: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                        {t('report.addendum.title', 'Addendum')} #{index + 1}
                      </Typography>
                      
                      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {addendum.content}
                        </Typography>
                      </Paper>
                      
                      {addendum.signatures_json && addendum.signatures_json.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('report.addendum.signatures', 'Signaturen')}:
                          </Typography>
                          {addendum.signatures_json.map((sig, sigIndex) => (
                            <Typography key={sigIndex} variant="body2" sx={{ ml: 2 }}>
                              {sig.academic_degree && `${sig.academic_degree} `}
                              {sig.first_name} {sig.last_name}
                              {sig.job_title && `, ${sig.job_title}`}
                              {sig.position && ` (${sig.position})`}
                              {' - '}
                              {new Date(sig.signed_at).toLocaleString('de-DE', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      
                      {addendum.approval_json && (
                        <Box sx={{ ml: 0, mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Freigegeben:
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {addendum.approval_json.academic_degree && `${addendum.approval_json.academic_degree} `}
                            {addendum.approval_json.first_name} {addendum.approval_json.last_name}
                            {' - '}
                            {addendum.approval_json.approved_at} {','} {addendum.approval_json.approved_time}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Editierbares Addendum (für Freitext-Editor) */}
                  {isAddendumMode && (
                    <Box sx={{ p: 2, borderTop: `2px solid ${theme.palette.divider}`, mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                          {(() => {
                            const approvedCount = allAddenda.filter(a => a.approved_by).length;
                            return approvedCount > 0 
                              ? `${t('report.addendum.title', 'Addendum')} #${approvedCount + 1}`
                              : t('report.addendum.title', 'Addendum');
                          })()}
                        </Typography>
                        <Button
                          onClick={() => setCancelAddendumDialogOpen(true)}
                          className="button-cancel"
                          sx={{ ml: 2 }}
                        >
                          {t('report.addendum.cancelProcess', 'Addendum abbrechen')}
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={addendumContent}
                        onChange={(e) => setAddendumContent(e.target.value)}
                        placeholder={t('report.addendum.placeholder', 'Geben Sie hier das Addendum ein...')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'var(--rd-bg-field)',
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
              {/* Speichern */}
              <Tooltip title={reportStatus === 'APPROVED' ? t('reportEditor.readonly.reportApproved') : t('reportEditor.buttons.save')}>
                <span>
                  <IconButton 
                    onClick={handleSaveReport}
                    disabled={reportStatus === 'APPROVED'}
                    sx={{ 
                      color: 'var(--rd-primary)',
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                      },
                      '&:disabled': {
                        color: 'var(--rd-text-disabled)'
                      }
                    }}
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
              
              {/* Signieren */}
              <Tooltip title={reportStatus === 'APPROVED' ? t('reportEditor.readonly.reportApproved') : (t('reportEditor.buttons.signature') || 'Signatur')}>
                <span>
                  <IconButton 
                    onClick={handleSignReport}
                    disabled={reportStatus === 'APPROVED'}
                    sx={{ 
                      color: 'var(--rd-primary)',
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                      },
                      '&:disabled': {
                        color: 'var(--rd-text-disabled)'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={signatureIcon}
                      alt="Signature"
                      sx={{
                        width: 24,
                        height: 24,
                        filter: reportStatus === 'APPROVED' 
                          ? 'grayscale(100%) brightness(2) opacity(0.3)' 
                          : 'invert(54%) sepia(89%) saturate(2476%) hue-rotate(359deg) brightness(103%) contrast(97%)'
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              
              {/* Senden */}
              <Tooltip title={reportStatus === 'APPROVED' ? t('reportEditor.readonly.reportApproved') : t('reportEditor.buttons.send')}>
                <span>
                  <IconButton 
                    ref={(el) => setSendMenuAnchor(el)}
                    onClick={(event) => {
                      if (reportStatus !== 'APPROVED') {
                        setSendMenuOpen(!sendMenuOpen);
                      }
                    }}
                    disabled={reportStatus === 'APPROVED'}
                    sx={{ 
                      color: 'var(--rd-primary)',
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                      },
                      '&:disabled': {
                        color: 'var(--rd-text-disabled)'
                      }
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
              
              {/* Freigeben - nur sichtbar wenn User Berechtigung hat */}
              {currentUser?.can_verify && (
                <Tooltip title={reportStatus === 'APPROVED' ? t('reportEditor.readonly.reportApproved') : t('reportEditor.buttons.verify')}>
                  <span>
                    <IconButton 
                      onClick={() => {
                        if (reportStatus !== 'APPROVED') {
                          setVerifyDialogOpen(true);
                          setVerifyPassword('');
                          setVerifyPasswordError('');
                        }
                      }}
                      disabled={reportStatus === 'APPROVED'}
                      sx={{ 
                        color: 'var(--rd-primary)',
                        '&:hover': {
                          backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                        },
                        '&:disabled': {
                          color: 'var(--rd-text-disabled)'
                        }
                      }}
                    >
                      <VerifiedUserIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              
              {/* Drucken */}
              <Tooltip title={t('reportEditor.buttons.print')}>
                <IconButton 
                  onClick={() => {
                    console.log('🔥 BUTTON CLICKED - BASIC TEST');
                    handlePrintReport();
                  }}
                  sx={{ 
                    color: 'var(--rd-primary)',
                    '&:hover': {
                      backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                    }
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              
              {/* Provisional Release Button - AUSGEBLENDET */}
              <Tooltip title={t('reportEditor.buttons.provisionalRelease')} sx={{ display: 'none' }}>
                <IconButton 
                  onClick={() => {
                    setProvisionalReleaseDialogOpen(true);
                    setProvisionalPassword('');
                    setProvisionalPasswordError('');
                    setProvisionalActionType('send_and_print');
                  }}
                  sx={{ 
                    display: 'none',
                    color: 'var(--rd-primary)',
                    '&:hover': {
                      backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
                    }
                  }}
                >
                  <AvTimerIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
          </>
        )}
        commentOverlay={(
        <>
        {/* Kommentarleiste - Fixed am Bildschirmrand */}
        <Box sx={{ 
          position: 'fixed',
          bottom: '-25px', // Immer zur Hälfte eingeklappt - keine Bewegung
          left: '25%', // Angepasst an Chatfenster-Position
          transform: 'translateX(-50%)',
          zIndex: 1300 // Über allem anderen
        }}>
          {/* Kommentar Bean */}
          <Box
            onClick={() => setCommentsExpanded(!commentsExpanded)}
            sx={{
              backgroundColor: 'var(--rd-primary)',
              color: 'white',
              borderRadius: '25px 25px 0 0', // Nur oben abgerundet für "halbe Bean"
              px: 3,
              pt: 0.5, // Weniger Padding oben
              pb: 2.5, // Mehr Padding unten 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'flex-start', // Text ganz oben ausrichten
              justifyContent: 'center',
              gap: 1,
              width: '500px', // Genauso breit wie das Chatfenster
              minHeight: '50px', // Feste Höhe für bessere Kontrolle
              boxShadow: '0 -2px 8px color-mix(in srgb, var(--rd-primary) 30%, transparent)', // Schatten nach oben
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'var(--rd-primary-hover)',
                color: 'var(--rd-text-primary)',
                boxShadow: '0 -4px 12px color-mix(in srgb, var(--rd-primary) 40%, transparent)',
              }
            }}
          >
            <CommentIcon sx={{ fontSize: '1rem', mt: 0.2 }} />
            <Typography variant="caption" sx={{ 
              fontWeight: 'bold',
              fontSize: '0.75rem',
              mt: 0.2,
              lineHeight: 1
            }}>
              {t('reportEditor.chat.commentCount', { count: comments.length })}
            </Typography>
            {commentsExpanded ? 
              <KeyboardArrowDownIcon sx={{ fontSize: '1rem', mt: 0.2 }} /> : 
              <KeyboardArrowUpIcon sx={{ fontSize: '1rem', mt: 0.2 }} />
            }
          </Box>

          {/* Aufklappbares Chat-Interface mit Smooth-Effekt */}
          <Paper
            elevation={6}
            sx={{
              position: 'fixed',
              bottom: '25px', // Direkt am Bildschirmrand ohne Lücke
              left: '50%', // EXAKT wie Bean - unter Vorbefunde
              transform: 'translateX(-50%)',
              width: '500px', // Exakt gleiche Breite wie Bean
              height: commentsExpanded ? '420px' : '0px', // Höher für mehr Platz unten
              borderRadius: '4px 4px 0 0', // Obere Kanten mit gleichem Radius wie andere Paper
              overflow: 'hidden',
              zIndex: 1200, // Unter der Bean aber über dem Rest
              backgroundColor: theme.palette.background.paper,
              border: 'none', // Alle Borders entfernen für perfekte Deckungsgleichheit
              boxSizing: 'border-box', // Gleiches Box-Modell wie Bean
              transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth Effekt
              opacity: commentsExpanded ? 1 : 0,
              transitionProperty: 'height, opacity',
              transitionDuration: '0.4s, 0.2s',
              transitionDelay: commentsExpanded ? '0s, 0.1s' : '0s, 0s',
              // Flexbox Layout für fixierte Input-Position
              display: 'flex',
              flexDirection: 'column'
            }}
          >
                {/* Chat Header */}
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'var(--rd-primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0 // Header bleibt oben fixiert
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('reportEditor.chat.comments')}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setCommentsExpanded(false)}
                    sx={{ color: 'white' }}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                </Box>

                {/* Chat Messages */}
                <Box 
                  ref={chatMessagesRef}
                  sx={{ 
                    flex: 1, // Nimmt verfügbaren Platz zwischen Header und Input ein
                    overflow: 'auto', // Scrolling nur im Messages-Bereich
                    p: 1,
                    backgroundColor: theme.palette.background.default,
                    minHeight: 0, // Wichtig für flex-overflow
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.15)',
                      borderRadius: '3px',
                    }
                  }}>
                  {comments.length === 0 ? (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 4,
                      color: 'text.secondary' 
                    }}>
                      <CommentIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">
                        {t('reportEditor.chat.noCommentsYet')}
                      </Typography>
                    </Box>
                  ) : (
                    comments.map((comment, index) => {
                      const isOwnMessage = comment.name === currentUser?.username || comment.name === 'Sie';
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                            mb: 1.5,
                            px: 1
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '75%',
                              backgroundColor: isOwnMessage 
                                ? 'var(--rd-primary)'  // Orange für eigene Nachrichten
                                : 'var(--rd-bg-subtle)',
                              color: isOwnMessage 
                                ? 'white'    // Weiße Schrift für eigene Nachrichten
                                : theme.palette.text.primary,
                              borderRadius: isOwnMessage 
                                ? '20px 20px 5px 20px'  // WhatsApp-Style für eigene
                                : '20px 20px 20px 5px', // WhatsApp-Style für andere
                              px: 2.5,
                              py: 1.5,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                              position: 'relative'
                            }}
                          >
                            {!isOwnMessage && (
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'light',
                                display: 'block',
                                mb: 0.5,
                                color: 'var(--rd-primary)'
                              }}>
                                {comment.name}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ 
                              mb: 0.5,
                              wordBreak: 'break-word',
                              lineHeight: 1.4
                            }}>
                              {comment.comment}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: isOwnMessage ? 0.9 : 0.7,
                                fontSize: '0.7rem',
                                display: 'block',
                                textAlign: 'right',
                                mt: 0.5
                              }}
                            >
                              {formatCommentTime(comment.date, comment.time)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                {/* Chat Input */}
                <Box sx={{ 
                  pt: 2, // Padding oben
                  px: 2, // Padding links/rechts  
                  pb: 2, // Kein Padding unten - direkt am Rand
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: theme.palette.background.default,
                  flexShrink: 0 // Input bleibt unten fixiert
                }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('reportEditor.chat.enterMessage')}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          addComment();
                        }
                      }}
                      multiline
                      rows={3} // Feste 3 Zeilen Höhe
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'var(--rd-bg-field)',
                          borderRadius: '20px',
                          minHeight: '72px', // Feste Mindesthöhe für 3 Zeilen
                          maxHeight: '72px', // Feste Maximalhöhe für 3 Zeilen
                          alignItems: 'flex-start', // Text oben ausrichten
                          '& textarea': {
                            overflowY: 'auto !important', // Nur vertikale Scrollbar ab 4. Zeile
                            overflowX: 'hidden !important', // Keine horizontale Scrollbar
                            resize: 'none', // Kein manuelles Resizing
                            '&::-webkit-scrollbar': {
                              width: '6px', // Dünne Scrollbar
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'rgba(0,0,0,0.05)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.15)',
                              borderRadius: '3px',
                            }
                          }
                        }
                      }}
                    />
                    <IconButton 
                      onClick={addComment}
                      disabled={!newComment.trim()}
                      sx={{ 
                        backgroundColor: 'var(--rd-primary)',
                        color: 'white',
                        minWidth: '40px', // Feste Breite
                        minHeight: '40px', // Feste Höhe
                        maxWidth: '40px', // Feste Breite
                        maxHeight: '40px', // Feste Höhe
                        flexShrink: 0, // Button verändert sich nie
                        alignSelf: 'flex-end', // Immer unten ausgerichtet
                        mb: 0.5, // Kleiner Margin unten für bessere Ausrichtung
                        '&:hover': {
                          backgroundColor: 'var(--rd-primary-hover)',
                          color: 'var(--rd-text-primary)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'grey.300',
                          color: 'grey.500',
                        }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
        </Box>
        </>
        )}
      />
      <TemplateSelectDialog
        open={templateDialogOpen}
        templates={matchingTemplates}
        onClose={() => setTemplateDialogOpen(false)}
        onSelect={(tpl) => {
          setTemplateDialogOpen(false);
          applyTemplate(tpl);
        }}
      />
      <TemplateSelectDialog
        open={templateBrowserOpen}
        templates={getFilteredTemplatesByType(templateBrowserType)}
        onClose={() => setTemplateBrowserOpen(false)}
        onSelect={(tpl) => {
          setTemplateBrowserOpen(false);
          applyTemplate(tpl);
        }}
      />
      
      {/* Bestätigungsdialog für Voruntersuchungen */}
      <Dialog
        open={priorStudyDialog.open}
        onClose={handlePriorStudyDialogCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('report.priorStudyDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography>
              {t('report.priorStudyDialog.message')}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              p: 2, 
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t('report.priorStudyDialog.clientHint')}
              </Typography>
              <Box 
                component="img"
                src={showPatientHistoryIcon}
                alt="Patient History Icon"
                sx={{ width: 24, height: 24 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            className="button-cancel"
            onClick={handlePriorStudyDialogCancel}
          >
            {t('report.priorStudyDialog.cancel')}
          </Button>
          <Button
            className="button"
            onClick={handlePriorStudyDialogWebViewer}
          >
            {t('report.priorStudyDialog.openInWebViewer')}
          </Button>
          <Button
            className="button"
            onClick={handlePriorStudyDialogConfirm}
          >
            {t('report.priorStudyDialog.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Formatierungs-Toolbar für markierten Text */}
      <Popper
        open={tooltipOpen}
        anchorEl={tooltipAnchor}
        placement="top"
        transition
        style={{ zIndex: 9999 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={3}
              data-tooltip-content
              sx={{
                p: 1,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {/* Kommentar */}
                <Tooltip title={t('reportEditor.toolbar.addComment')}>
                  <IconButton
                    size="small"
                    onClick={handleInlineCommentCreate}
                    sx={{ color: 'primary.main' }}
                  >
                    <CommentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Trennlinie */}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: '24px' }} />

                {/* Bold, Kursiv, Unterstrichen */}
                <Tooltip title={t('reportEditor.toolbar.bold')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={handleBold}
                    data-keep-toolbar-open="true"
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={t('reportEditor.toolbar.italic')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={handleItalic}
                    data-keep-toolbar-open="true"
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={t('reportEditor.toolbar.underline')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={handleUnderline}
                    data-keep-toolbar-open="true"
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Trennlinie */}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: '24px' }} />

                {/* Schriftgröße */}
                <Tooltip title={t('reportEditor.toolbar.fontSize')}>
                  <FormControl size="small" sx={{ minWidth: 60 }} data-keep-toolbar-open="true">
                    <Select
                      value={fontSize === -1 ? -1 : fontSize} // Handle mixed font sizes
                      displayEmpty
                      onChange={(e) => {
                        // Ignoriere den "Mixed" Wert - wird automatisch durch Änderung ersetzt
                        if (e.target.value !== -1) {
                          handleFontSizeChange(e.target.value);
                        }
                      }}
                      sx={{ 
                        height: '32px',
                        '& .MuiSelect-select': {
                          padding: '4px 8px',
                          fontSize: '12px'
                        }
                      }}
                      data-keep-toolbar-open="true"
                    >
                    {/* Zeige "Mixed" Option nur bei gemischten Font-Sizes */}
                    {fontSize === -1 && (
                      <MenuItem value={-1} disabled data-keep-toolbar-open="true">
                        <em style={{ color: 'var(--rd-text-secondary)', fontStyle: 'italic' }}>Mixed</em>
                      </MenuItem>
                    )}
                    {/* Echte Größen (werden mit +2 im Editor angezeigt) */}
                    <MenuItem value={8} data-keep-toolbar-open="true">8</MenuItem>
                    <MenuItem value={10} data-keep-toolbar-open="true">10</MenuItem>
                    <MenuItem value={12} data-keep-toolbar-open="true">12</MenuItem>
                    <MenuItem value={14} data-keep-toolbar-open="true">14</MenuItem>
                    <MenuItem value={16} data-keep-toolbar-open="true">16</MenuItem>
                    <MenuItem value={18} data-keep-toolbar-open="true">18</MenuItem>
                    <MenuItem value={20} data-keep-toolbar-open="true">20</MenuItem>
                    <MenuItem value={24} data-keep-toolbar-open="true">24</MenuItem>
                  </Select>
                </FormControl>
                </Tooltip>

                {/* Trennlinie */}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: '24px' }} />

                {/* Schriftfarbe */}
                <Tooltip title={t('reportEditor.toolbar.fontColor')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={handleColorPickerOpen}
                    data-keep-toolbar-open="true"
                  >
                    <PaletteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Trennlinie */}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: '24px' }} />

                {/* Text-Ausrichtung */}
                <Tooltip title={t('reportEditor.toolbar.alignLeft')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={() => handleAlign('left')}
                  >
                    <FormatAlignLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={t('reportEditor.toolbar.alignCenter')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={() => handleAlign('center')}
                  >
                    <FormatAlignCenterIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={t('reportEditor.toolbar.alignRight')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={() => handleAlign('right')}
                  >
                    <FormatAlignRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={t('reportEditor.toolbar.alignJustify')}>
                  <IconButton
                    size="small"
                    sx={{ color: 'text.primary' }}
                    onClick={() => handleAlign('justify')}
                  >
                    <FormatAlignJustifyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>

      {/* Color Picker Popup */}
      {colorPickerOpen && (
        <ClickAwayListener onClickAway={colorPickerMode === 'palette' ? handleColorPickerClose : () => {}}>
          <Popper
            open={colorPickerOpen}
            anchorEl={colorPickerAnchor}
            placement="bottom-start"
            style={{ zIndex: 9999 }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ]}
          >
            <Fade in={colorPickerOpen} timeout={200}>
              <Paper
                elevation={3}
                data-keep-toolbar-open="true"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  minWidth: colorPickerMode === 'spectrum' ? 320 : 240,
                  overflow: 'hidden'
                }}
              >
                {/* Tab Header */}
                <Box sx={{ 
                  display: 'flex', 
                  backgroundColor: theme.palette.action.hover,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <Button
                    onClick={() => setColorPickerMode('palette')}
                    size="small"
                    sx={{
                      flex: 1,
                      borderRadius: 0,
                      backgroundColor: colorPickerMode === 'palette' ? theme.palette.background.paper : 'transparent',
                      color: colorPickerMode === 'palette' ? theme.palette.primary.main : theme.palette.text.secondary,
                      fontWeight: colorPickerMode === 'palette' ? 'bold' : 'normal',
                      '&:hover': {
                        backgroundColor: colorPickerMode === 'palette' ? theme.palette.background.paper : theme.palette.action.hover,
                      }
                    }}
                  >
                    {t('reportEditor.colorPicker.palette')}
                  </Button>
                  <Button
                    onClick={() => setColorPickerMode('spectrum')}
                    size="small"
                    sx={{
                      flex: 1,
                      borderRadius: 0,
                      backgroundColor: colorPickerMode === 'spectrum' ? theme.palette.background.paper : 'transparent',
                      color: colorPickerMode === 'spectrum' ? theme.palette.primary.main : theme.palette.text.secondary,
                      fontWeight: colorPickerMode === 'spectrum' ? 'bold' : 'normal',
                      '&:hover': {
                        backgroundColor: colorPickerMode === 'spectrum' ? theme.palette.background.paper : theme.palette.action.hover,
                      }
                    }}
                  >
                    {t('reportEditor.colorPicker.spectrum')}
                  </Button>
                </Box>

                {/* Content */}
                <Box sx={{ p: 2 }}>
                  {colorPickerMode === 'palette' ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center' }}>
                        {t('reportEditor.colorPicker.selectFontColor')}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>

                        <CirclePicker
                          color={fontColor}
                          onChangeComplete={handleColorChange}
                          colors={[
                            // Zeile 1: Helle Farben
                            '#F5F5F5', '#FFFF99', '#FFB6C1', '#F0E68C', '#FFA500', '#FFFF00',
                            // Zeile 2: Mittlere Farben  
                            '#00FFFF', '#00FF00', '#FF00FF', '#FF0000', '#0000FF', '#FF69B4',
                            // Zeile 3: Dunklere Farben
                            '#C0C0C0', '#808080', '#800080', '#008000', '#000080', '#008080',
                            // Zeile 4: Dunkle Farben
                            '#800000', '#808000', '#8B4513', '#4B0082', '#2F4F4F', '#1a1a1a'
                          ]}
                          width="240px"
                          circleSize={22}
                          circleSpacing={6}
                          styles={{
                            default: {
                              card: {
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none'
                              }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ 
                        fontFamily: theme.typography.fontFamily,
                        fontSize: theme.typography.body2.fontSize 
                      }}>
                        <SketchPicker
                          color={tempColor}
                          onChange={handleSpectrumColorChange}
                          disableAlpha={true}
                          presetColors={[
                            '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
                            '#FFA500', '#800080', '#008000', '#000080', '#808080', '#C0C0C0'
                          ]}
                          width="260px"
                          styles={{
                            default: {
                              picker: {
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.body2.fontSize,
                                boxShadow: 'none',
                                border: 'none',
                                background: 'transparent',
                              },
                            },
                          }}
                        />
                      </div>
                      
                      {/* Apply and Cancel Buttons */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleColorPickerClose}
                          className="button-cancel"
                          sx={{ minWidth: 80 }}
                        >
                          {t('reportEditor.inlineComment.cancel')}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleApplyColor}
                          startIcon={<CheckIcon />}
                          className="button"
                          sx={{ minWidth: 80 }}
                        >
                          {t('reportEditor.colorPicker.apply')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Fade>
          </Popper>
        </ClickAwayListener>
      )}

      {/* Dialog für Inline-Kommentar erstellen */}
      <Dialog
        open={inlineCommentDialog.open}
        onClose={handleInlineCommentCancel}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            // Fokus setzen nachdem Dialog-Animation beendet ist
            console.log('🎯 Dialog entered, setting focus...');
            if (inlineCommentInputRef.current) {
              inlineCommentInputRef.current.focus();
              console.log('✅ Focus set on input field');
            } else {
              console.warn('⚠️ Input ref not available');
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'var(--rd-primary)', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <ChatIcon />
          {t('reportEditor.inlineComment.dialogTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Markierter Text:
            </Typography>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}
            >
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{inlineCommentDialog.text}"
              </Typography>
            </Paper>
          </Box>
          
          <TextField
            inputRef={inlineCommentInputRef}
            fullWidth
            multiline
            rows={3}
            label={t('reportEditor.inlineComment.yourComment')}
            value={newInlineComment}
            onChange={(e) => setNewInlineComment(e.target.value)}
            placeholder={t('reportEditor.inlineComment.placeholder')}
            helperText={t('reportEditor.inlineComment.helperText')}
            sx={{ mt: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleInlineCommentCancel}
            className="button-cancel"
          >
            {t('reportEditor.inlineComment.cancel')}
          </Button>
          <Button
            onClick={handleInlineCommentSave}
            disabled={!newInlineComment.trim()}
            className="button"
          >
            {t('reportEditor.inlineComment.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kommentar Tooltip */}
      <Popper
        open={commentTooltip.open}
        anchorEl={commentTooltip.anchorEl}
        placement="top"
        style={{ zIndex: 1300 }}
      >
        <Fade in={commentTooltip.open} timeout={200}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              maxWidth: 300,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {commentTooltip.comment && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SmsFailedIcon sx={{ fontSize: 16, color: 'var(--rd-primary)' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'var(--rd-primary)' }}>
                    {commentTooltip.comment.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {commentTooltip.comment.date} {commentTooltip.comment.time}
                  </Typography>
                  
                  {/* Löschen Button - Rotes X */}
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title={t('reportEditor.toolbar.deleteComment')}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteComment(commentTooltip.comment)}
                      sx={{ 
                        color: 'var(--rd-error)',
                        width: 24,
                        height: 24,
                        borderRadius: '50%', // Macht den Button rund
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          color: 'var(--rd-danger)',
                          borderRadius: '50%' // Bleibt rund beim Hover
                        }
                      }}
                    >
                      ✕
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  "{commentTooltip.comment.comment}"
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {t('reportEditor.inlineComment.marked')}: "{commentTooltip.comment.text}"
                </Typography>
              </>
            )}
          </Paper>
        </Fade>
      </Popper>

      {/* ClickAwayListener für Tooltip */}
      {commentTooltip.open && (
        <ClickAwayListener 
          onClickAway={() => setCommentTooltip({ open: false, comment: null, anchorEl: null })}
        >
          <div />
        </ClickAwayListener>
      )}

      {/* MUI Tooltip für Inline Comment Icons */}
      <Tooltip
        open={commentIconTooltip.open}
        title={commentIconTooltip.text}
        placement="top"
        arrow
        PopperProps={{
          anchorEl: commentIconTooltip.anchorEl,
          style: { zIndex: 10000 }
        }}
      >
        <span />
      </Tooltip>

      {/* Send User Selection Popup */}
      <Popper
        open={sendMenuOpen}
        anchorEl={sendMenuAnchor}
        placement="top"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setSendMenuOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              minWidth: 300,
              maxWidth: 400
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('reportEditor.send.selectUser')}
              </Typography>
              
              {/* Suchfeld */}
              <TextField
                fullWidth
                size="small"
                placeholder={t('reportEditor.send.searchUsers')}
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              
              {/* User Liste */}
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.action.disabled,
                    borderRadius: '4px',
                  },
                }}
              >
                {loadingUsers ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('reportEditor.send.loadingUsers')}
                    </Typography>
                  </Box>
                ) : filteredUsers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {userSearchTerm ? t('reportEditor.send.noUsersFound') : t('reportEditor.send.noUsers')}
                    </Typography>
                  </Box>
                ) : (
                  <MenuList dense>
                    {filteredUsers.map((user) => (
                      <MenuItem 
                        key={user.id}
                        onClick={() => handleSendToUser(user)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {[user.academic_degree, user.first_name, user.last_name]
                              .filter(Boolean)
                              .join(' ') || user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </MenuList>
                )}
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Provisional Release Dialog */}
      <Dialog
        open={provisionalReleaseDialogOpen}
        onClose={() => setProvisionalReleaseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AvTimerIcon sx={{ color: 'var(--rd-primary)', fontSize: 28 }} />
              <Typography variant="h6">
                {t('reportEditor.buttons.provisionalRelease')}
              </Typography>
            </Box>
            <IconButton onClick={() => setProvisionalReleaseDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold' }}>
              {t('reportEditor.provisionalRelease.passwordRequired')}
            </Typography>
            
            <TextField
              type="password"
              fullWidth
              label={t('reportEditor.provisionalRelease.passwordLabel')}
              value={provisionalPassword}
              onChange={(e) => {
                setProvisionalPassword(e.target.value);
                setProvisionalPasswordError('');
              }}
              error={!!provisionalPasswordError}
              helperText={provisionalPasswordError}
              autoFocus
              autoComplete="off"
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('reportEditor.provisionalRelease.selectAction')}:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={() => setProvisionalActionType('send')}
                variant='outlined'
                disabled={!provisionalPassword.trim()}
                startIcon={<SendIcon />}
                sx={{ 
                  flex: 1,
                  backgroundColor: 'transparent',
                  borderColor: 'var(--rd-primary)',
                  color: 'var(--rd-primary)',
                  '&:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--rd-primary) 4%, transparent)'
                  }
                }}
              >
                {t('reportEditor.provisionalRelease.sendOnly')}
              </Button>
              <Button
                onClick={() => setProvisionalActionType('send_and_print')}
                variant='contained'
                disabled={!provisionalPassword.trim()}
                startIcon={<PrintIcon />}
                sx={{ 
                  flex: 1,
                  backgroundColor: 'var(--rd-primary)',
                  borderColor: 'var(--rd-primary)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'var(--rd-primary-hover)'
                  }
                }}
              >
                {t('reportEditor.provisionalRelease.sendAndPrint')}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {t('reportEditor.provisionalRelease.note')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              className="button-cancel"
              onClick={() => {
                setProvisionalReleaseDialogOpen(false);
                setProvisionalPassword('');
                setProvisionalPasswordError('');
                setProvisionalActionType('send_and_print');
              }}
            >
              {t('reportEditor.provisionalRelease.cancel')}
            </Button>
            <Button
              className={!provisionalPassword.trim() || !provisionalActionType ? 'button-disabled' : 'button'}
              onClick={async () => {
                try {
                  // Prüfe ob Report gespeichert ist
                  if (!existingReportId) {
                    setProvisionalPasswordError('Bericht muss zuerst gespeichert werden');
                    return;
                  }

                  console.log('🎯 Provisional Release:', {
                    reportId: existingReportId,
                    password: provisionalPassword,
                    actionType: provisionalActionType
                  });

                  const response = await apiFetch(`/reports/${existingReportId}/provisional-release`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      password: provisionalPassword,
                      action_type: provisionalActionType
                    })
                  });

                  if (response.ok) {
                    const result = await response.json();
                    console.log('✅ Provisional Release erfolgreich:', result);
                    
                    // Dialog schließen
                    setProvisionalReleaseDialogOpen(false);
                    setProvisionalPassword('');
                    setProvisionalPasswordError('');
                    setProvisionalActionType('send_and_print');
                    
                    // Erfolgsmeldung anzeigen
                    showSuccess('Befund wurde vorläufig freigegeben');
                    
                    // Live PDF öffnen falls send_and_print
                    if (result.action_type === 'send_and_print' && result.print_ready) {
                      console.log('📄 Live PDF-Generierung erfolgreich, öffne Vorschau');
                      console.log('📄 PDF Data:', result.pdf_data);
                      if (result.pdf_data) {
                        openPdfPreview(result.pdf_data);
                      } else if (result.show_pdf_dialog) {
                        console.log('📄 Lade PDF-Daten separat');
                        // Lade PDF-Daten separat
                        try {
                          const pdfResponse = await apiFetch(`/reports/${existingReportId}/pdf`, {
                            method: 'GET',
                            headers: getAuthHeaders()
                          });
                          if (pdfResponse.ok) {
                            const pdfResult = await pdfResponse.json();
                            if (pdfResult.pdf_data) {
                              openPdfPreview(pdfResult.pdf_data);
                            }
                          }
                        } catch (pdfError) {
                          console.error('Fehler beim Laden der PDF-Daten:', pdfError);
                        }
                      }
                    } else if (result.action_type === 'send_and_print' && result.error) {
                      console.error('📄 Live PDF-Generierung fehlgeschlagen:', result.error);
                      showError(`PDF-Generierung fehlgeschlagen: ${result.error}`, 'ERR-PDF-002');
                    } else if (result.action_type === 'send_and_print') {
                      console.log('📄 Live PDF-Status unbekannt, versuche trotzdem zu laden');
                      // Lade PDF-Daten direkt
                      try {
                        const pdfResponse = await apiFetch(`/reports/${existingReportId}/pdf`, {
                          method: 'GET',
                          headers: getAuthHeaders()
                        });
                        if (pdfResponse.ok) {
                          const pdfResult = await pdfResponse.json();
                          if (pdfResult.pdf_data) {
                            openPdfPreview(pdfResult.pdf_data);
                          }
                        }
                      } catch (pdfError) {
                        console.error('Fehler beim Laden der PDF-Daten:', pdfError);
                      }
                    }
                  } else {
                    const errorData = await response.json().catch(() => ({ detail: 'Unbekannter Fehler' }));
                    setProvisionalPasswordError(errorData.detail || 'Fehler bei der vorläufigen Freigabe');
                  }
                } catch (error) {
                  console.error('❌ Provisional Release Fehler:', error);
                  setProvisionalPasswordError('Verbindungsfehler zum Server');
                }
              }}
              disabled={!provisionalPassword.trim() || !provisionalActionType}
            >
              {t('reportEditor.provisionalRelease.confirm')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedUserIcon sx={{ color: 'var(--rd-primary)', fontSize: 28 }} />
              <Typography variant="h6">
                {t('reportEditor.verify.title')}
              </Typography>
            </Box>
            <IconButton onClick={() => setVerifyDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('reportEditor.verify.description')}
            </Typography>
            
            {/* Toggle-Buttons für Autosend und Drucken */}
            <Box sx={{ mb: 3, p: 2, bgcolor: isDarkMode ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('reportEditor.verify.autoActionsTitle')}
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoSendEnabled}
                      onChange={(e) => setAutoSendEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('reportEditor.verify.autoSendLabel')}
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoPrintEnabled}
                      onChange={(e) => setAutoPrintEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('reportEditor.verify.autoPrintLabel')}
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            {/* Passwortfeld nur anzeigen wenn erforderlich */}
            {(appConfig.password_for_report_approval !== false && !isAddendumMode) && (
              <>
                <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold' }}>
                  {t('reportEditor.verify.passwordRequired')}
                </Typography>
                
                <TextField
                  type="password"
                  fullWidth
                  label={t('reportEditor.verify.passwordLabel')}
                  value={verifyPassword}
                  onChange={(e) => {
                    setVerifyPassword(e.target.value);
                    setVerifyPasswordError('');
                  }}
                  error={!!verifyPasswordError}
                  helperText={verifyPasswordError}
                  autoFocus
                  autoComplete="off"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && verifyPassword.trim()) {
                      handleVerifyReport();
                    }
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('reportEditor.verify.note')}
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            className="button-cancel"
            onClick={() => {
              setVerifyDialogOpen(false);
              setVerifyPassword('');
              setVerifyPasswordError('');
            }}
            sx={{ color: 'var(--rd-text-secondary)' }}
          >
            {t('reportEditor.buttons.cancel')}
          </Button>
          <Button
            onClick={handleVerifyReport}
            variant='contained'
            disabled={(appConfig.password_for_report_approval !== false && !isAddendumMode) && !verifyPassword.trim()}
            startIcon={<VerifiedUserIcon />}
            sx={{ 
              backgroundColor: 'var(--rd-primary)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'var(--rd-primary-hover)'
              },
              '&:disabled': {
                backgroundColor: 'var(--rd-border)',
                color: 'var(--rd-text-secondary)'
              }
            }}
          >
            {t('reportEditor.verify.approveAndSend')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Type Switch Warning Dialog */}
      <Dialog
        open={templateTypeSwitchDialog.open}
        onClose={handleCancelTemplateTypeSwitch}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmsFailedIcon sx={{ color: 'var(--rd-accent)', fontSize: 28 }} />
              <Typography variant="h6">
                {t('reportEditor.templateTypeSwitch.title')}
              </Typography>
            </Box>
            <IconButton onClick={handleCancelTemplateTypeSwitch}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('reportEditor.templateTypeSwitch.message')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            className="button-cancel"
            onClick={handleCancelTemplateTypeSwitch}
            sx={{ color: 'var(--rd-text-secondary)' }}
          >
            {t('reportEditor.buttons.cancel')}
          </Button>
          <Button
            onClick={handleConfirmTemplateTypeSwitch}
            variant='contained'
            sx={{ 
              backgroundColor: 'var(--rd-accent)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'var(--rd-primary-hover)'
              }
            }}
          >
            {t('reportEditor.templateTypeSwitch.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snippet Selector Dialog */}
      <SnippetSelector
        open={snippetDialogOpen}
        onClose={closeSnippetDialog}
        onSnippetSelect={handleSnippetSelect}
        cursorPosition={snippetCursorPosition}
      />

      {/* PDF Preview Dialog - nur rendern wenn offen */}
      {pdfPreviewOpen && (
      <PDFPreviewDialog
        open={pdfPreviewOpen}
        onClose={() => {
          console.log('📄 PDF-Dialog wird geschlossen...');
          console.log('🔍 navigateAfterPrint Flag vor Reset:', navigateAfterPrint);
          setPdfPreviewOpen(false);
          // Nach Druckdialog zu MyTasks navigieren wenn Flag gesetzt
          if (navigateAfterPrint) {
            setNavigateAfterPrint(false);
            console.log('🔄 Navigiere zu MyTasks nach Druckdialog...');
            setTimeout(() => {
              navigate('/my-tasks');
            }, 100);
          }
        }}
        pdfData={pdfData}
        reportTitle={`Befund ${examData?.patient_name || ''}`}
      />
      )}
      
      {/* PDF Preview Dialog STRUCTURED - nur rendern wenn offen */}
      {pdfPreviewStructuredOpen && (
      <PDFPreviewDialogStructured
        open={pdfPreviewStructuredOpen}
        onClose={() => {
          console.log('📋 Strukturierter PDF-Dialog wird geschlossen...');
          console.log('🔍 navigateAfterPrint Flag vor Reset:', navigateAfterPrint);
          setPdfPreviewStructuredOpen(false);
          // Nach Druckdialog zu MyTasks navigieren wenn Flag gesetzt
          if (navigateAfterPrint) {
            setNavigateAfterPrint(false);
            console.log('🔄 Navigiere zu MyTasks nach Druckdialog...');
            setTimeout(() => {
              navigate('/my-tasks');
            }, 100);
          }
        }}
        pdfData={pdfData}
        reportTitle={`Strukturierter Befund ${examData?.patient_name || ''}`}
      />
      )}

      {/* Dialog: Addendum-Prozess abbrechen */}
      <Dialog
        open={cancelAddendumDialogOpen}
        onClose={() => setCancelAddendumDialogOpen(false)}
      >
        <DialogTitle>{t('report.addendum.cancelDialogTitle', 'Addendum-Prozess abbrechen?')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('report.addendum.cancelDialogMessage', 'Möchten Sie den Addendum-Prozess wirklich abbrechen? Der Befund wird wieder auf "Freigegeben" gesetzt und alle Änderungen gehen verloren.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelAddendumDialogOpen(false)} className="button">
            {t('common.no', 'Nein')}
          </Button>
          <Button onClick={handleCancelAddendum} className="button-cancel" autoFocus>
            {t('common.yes', 'Ja')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

// RichTextField Komponente - sieht aus wie TextField, unterstützt aber Rich Text
const RichTextField = ({ 
  multiline,
  minRows, 
  height,
  label, 
  placeholder, 
  size, 
  value, 
  onChange, 
  sx, 
  fullWidth,
  disabled,
  databaseField,
  isDarkMode,
  theme,
  id,
  name,
  onInlineCommentClick,
  onCommentIconsChanged,
  onSnippetDialogOpen,
  onSnippetInsertionPointSet,
  onTooltipShow,
  onTooltipHide,
  ...otherProps 
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const editorRef = useRef(null);
  const lastSavedContent = useRef('');
  const saveTimer = useRef(null);

  // Berechne Höhe basierend auf minRows oder height
  const calculatedHeight = height ? `${height * 24}px` : `${(minRows || 1) * 24}px`;

  // Lokale Formatierungsfunktion für Enter/Zeilenumbrüche
  const insertLineBreak = () => {
    try {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;
      
      const range = selection.getRangeAt(0);
      const br = document.createElement('br');
      range.deleteContents();
      range.insertNode(br);
      
      // Cursor nach dem <br> positionieren
      range.setStartAfter(br);
      range.setEndAfter(br);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) {
      console.warn('Zeilenumbruch fehlgeschlagen:', error);
    }
  };

  // Undo/Redo Funktionalität
  const saveToUndoStack = (content) => {
    // Speichere nur wenn sich Inhalt wirklich geändert hat
    if (content !== lastSavedContent.current) {
      setUndoStack(prev => {
        const newStack = [...prev, lastSavedContent.current];
        // Begrenze Undo-Stack auf 30 Einträge
        return newStack.slice(-30);
      });
      setRedoStack([]); // Clear redo stack when new action is performed
      lastSavedContent.current = content;
    }
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const currentContent = editorRef.current?.innerHTML || '';
      const previousContent = undoStack[undoStack.length - 1];
      
      setRedoStack(prev => [...prev, currentContent]);
      setUndoStack(prev => prev.slice(0, -1));
      
      if (editorRef.current) {
        editorRef.current.innerHTML = previousContent;
        const textValue = editorRef.current.innerText;
        if (onChange) {
          onChange({ target: { value: textValue } });
        }
        lastSavedContent.current = previousContent;
      }
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const currentContent = editorRef.current?.innerHTML || '';
      const nextContent = redoStack[redoStack.length - 1];
      
      setUndoStack(prev => [...prev, currentContent]);
      setRedoStack(prev => prev.slice(0, -1));
      
      if (editorRef.current) {
        editorRef.current.innerHTML = nextContent;
        const textValue = editorRef.current.innerText;
        if (onChange) {
          onChange({ target: { value: textValue } });
        }
        lastSavedContent.current = nextContent;
      }
    }
  };

  // Handle Keyboard Events
  const handleKeyDown = (e) => {
    // Snippet-Detection für @ Zeichen
    if (e.key === '@') {
      e.preventDefault(); // Prevent the @ character from being typed
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const element = e.target;
        
        // Get cursor position for dialog placement
        const rect = element.getBoundingClientRect();
        const cursorPos = {
          x: rect.left + 20, // Offset from left edge
          y: rect.top + 30   // Offset from top edge
        };
        
        // Save insertion point for later use - save current position without @
        const insertionRange = range.cloneRange();
        if (onSnippetInsertionPointSet) {
          onSnippetInsertionPointSet({ element, range: insertionRange });
        }
        
        // Open snippet dialog
        if (onSnippetDialogOpen) {
          onSnippetDialogOpen(cursorPos);
        }
      }
      return;
    }
    
    // STRG + Enter Navigation für Freitext-Templates wird jetzt vom globalen Handler behandelt
    // (entfernt um Doppel-Navigation zu vermeiden)

    // Eigene Undo/Redo Steuerung
    if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleUndo();
      return;
    }
    
    if (e.ctrlKey && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      e.stopPropagation();
      handleRedo();
      return;
    }

    // Behandle Pfeil-Tasten für Navigation bei einzeiligen Feldern
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && multiline === false) {
      // Bei einzeiligen Feldern: Pfeiltasten für Finding-Navigation verwenden
      e.preventDefault();
      e.stopPropagation();
      // Lasse Navigation durch das übergeordnete System zu
      return;
    }

    // Behandle Enter-Taste für Zeilenumbrüche (Navigation wird vom übergeordneten System behandelt)
    if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
      // Numpad Enter: Lasse Navigation durch das übergeordnete System zu
      if (e.code === 'NumpadEnter') {
        // NICHT preventDefault() oder stopPropagation() - lasse die Navigation durch
        return;
      }
      
      // Normale Enter: Nur in mehrzeiligen Feldern Zeilenumbruch erlauben
      if (e.code === 'Enter') {
        if (multiline !== false) {
          // Mehrzeiliges Feld: Zeilenumbruch einfügen
          e.preventDefault();
          insertLineBreak();
          return;
        } else {
          // Einzeiliges Feld: Lasse Navigation durch das übergeordnete System zu
          // NICHT preventDefault() - lasse die Navigation durch
          return;
        }
      }
    }

    // Speichere Zustand vor großen Änderungen
    if (e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Delete') {
      const currentContent = editorRef.current?.innerHTML || '';
      saveToUndoStack(currentContent);
    }
  };

  // Handle Text Changes
  const handleInput = (e) => {
    // WICHTIG: Verwende innerHTML statt innerText um Formatierung zu erhalten
    const newHtmlValue = e.target.innerHTML;
    const newTextValue = e.target.innerText;
    
    if (onChange) {
      // Für die Parent-Komponente geben wir den Text ohne HTML zurück, 
      // aber speichern intern die HTML-Version
      onChange({ target: { value: newTextValue } });
    }
    
    // Prüfe, ob Inline-Kommentar-Icons gelöscht wurden
    checkForDeletedComments(e.target);

    // Verzögertes Speichern für sanfte Eingaben
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    saveTimer.current = setTimeout(() => {
      saveToUndoStack(newHtmlValue);
    }, 1000); // Speichere nach 1 Sekunde Pause
  };

  // Funktion zum Prüfen und Entfernen gelöschter Kommentare
  const checkForDeletedComments = (element) => {
    if (!element) return;
    
    // Hole alle aktuell im DOM vorhandenen Comment-Icons
    const remainingIcons = element.querySelectorAll('.inline-comment-icon');
    const remainingCommentIds = Array.from(remainingIcons).map(icon => 
      icon.getAttribute('data-comment-id')
    );
    
    // Informiere Parent-Komponente über verbleibende Icons
    if (onCommentIconsChanged) {
      onCommentIconsChanged(remainingCommentIds);
    }
  };

  // Set initial content when value changes
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Prüfe ob sich der Inhalt wirklich geändert hat (nicht nur durch Formatierung)
      const currentText = editorRef.current.innerText;
      const newText = value ? value.replace(/<[^>]*>/g, '') : '';
      
      if (currentText !== newText) {
        editorRef.current.innerHTML = value || '';
        // Initialisiere Undo-System mit aktuellem Inhalt
        lastSavedContent.current = value || '';
      }
    }
  }, [value]);

  // Event-Delegation für Inline Comment Icons - funktioniert auch für dynamisch eingefügte Icons
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInlineCommentClick = (e) => {
      // Prüfe ob geklicktes Element oder ein Parent-Element ein Inline Comment Icon ist
      let target = e.target;
      while (target && target !== editor) {
        if (target.classList && target.classList.contains('inline-comment-icon')) {
          e.preventDefault();
          e.stopPropagation();
          const commentId = target.getAttribute('data-comment-id');
          if (onInlineCommentClick) {
            onInlineCommentClick(commentId, e);
          }
          return;
        }
        target = target.parentNode;
      }
    };
    
    const handleInlineCommentHover = (e) => {
      let target = e.target;
      while (target && target !== editor) {
        if (target.classList && target.classList.contains('inline-comment-icon')) {
          const tooltipText = target.getAttribute('data-tooltip');
          if (tooltipText && onTooltipShow) {
            onTooltipShow({ open: true, anchorEl: target, text: tooltipText });
          }
          return;
        }
        target = target.parentNode;
      }
    };
    
    const handleInlineCommentLeave = (e) => {
      let target = e.target;
      while (target && target !== editor) {
        if (target.classList && target.classList.contains('inline-comment-icon')) {
          if (onTooltipHide) {
            onTooltipHide();
          }
          return;
        }
        target = target.parentNode;
      }
    };

    // Event-Listener für alle Clicks im Editor
    editor.addEventListener('click', handleInlineCommentClick, true);
    editor.addEventListener('mouseover', handleInlineCommentHover, true);
    editor.addEventListener('mouseout', handleInlineCommentLeave, true);

    return () => {
      editor.removeEventListener('click', handleInlineCommentClick, true);
      editor.removeEventListener('mouseover', handleInlineCommentHover, true);
      editor.removeEventListener('mouseout', handleInlineCommentLeave, true);
    };
  }, [onInlineCommentClick, onTooltipShow, onTooltipHide]);



  // Initialisiere Undo-System beim ersten Mount
  useEffect(() => {
    if (editorRef.current && lastSavedContent.current === '') {
      lastSavedContent.current = editorRef.current.innerHTML;
    }
  }, []);

  // Cleanup Timer beim Unmount
  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    };
  }, []);

    return (
    <div className="form-group" style={{ 
      width: fullWidth ? '100%' : 'auto',
      ...(sx || {})
    }}>
      {label && (
        <label 
          htmlFor={id}
          style={{ 
            display: 'block',
            marginBottom: '4px', 
            fontSize: size === 'small' ? '0.75rem' : '0.875rem',
            color: theme.palette.text.secondary 
          }}
        >
          {label}
        </label>
      )}
      <Box
        ref={editorRef}
        id={id}
        name={name}
        contentEditable={!disabled}
        suppressContentEditableWarning={true}
        onInput={disabled ? undefined : handleInput}
        onKeyDown={disabled ? undefined : handleKeyDown}
        onFocus={disabled ? undefined : () => setIsFocused(true)}
        onBlur={disabled ? undefined : () => setIsFocused(false)}
        // onClick handler entfernt - wird jetzt über Event-Delegation gehandhabt
        className={`rich-text-field report-editor-content ${databaseField ? (isDarkMode ? 'database-field-dark' : 'database-field-light') : ''}`}
        data-multiline={multiline === false ? 'false' : 'true'}
        sx={{
          border: `1px solid ${isFocused && !disabled ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: 'var(--rd-bg-field)',
          padding: size === 'small' ? '8px 12px' : '16.5px 14px',
          minHeight: multiline ? calculatedHeight : '40px',
          maxHeight: multiline ? 'none' : '40px',
          overflowY: multiline ? 'auto' : 'hidden',
          fontSize: size === 'small' ? '0.875rem' : '1rem',
          lineHeight: 1.4,
          outline: 'none',
          position: databaseField ? 'relative' : 'static',
          zIndex: databaseField ? 2 : 'auto',
          cursor: disabled ? 'not-allowed' : 'text',
          fontFamily: 'inherit',
          overflow: 'auto',
          '&:focus': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px'
          },
          '&:empty:before': {
            content: `"${placeholder || ''}"`,
            color: theme.palette.text.secondary,
            fontStyle: 'italic'
          }
        }}
        {...otherProps}
      />
    </div>
  );
};

export default ReportEditor; 